"use client";

import { useState, useEffect, useRef, useCallback, useMemo, type MutableRefObject, type ReactNode, type Ref, type RefObject } from "react";
import { createPortal } from "react-dom";
import {
  EcomButton,
  EcomDropdown,
  ECOM_LAYERS,
  EcomIconAction,
  EcomInput,
  EcomModal,
  EcomRadioSelect,
  EcomSelect,
  CropIcon,
  OrignUpload,
  EcomSwitch,
  useEcomFeedback,
  useEcomPollingFeedback,
} from "../../ui";
import {
  ImageUploadAddTrigger,
  ImageUploadField,
  type ImageUploadFieldItem,
} from "../ui/image-upload-field";
import {
  WorkflowUIConfig,
  ComponentConfig,
  ModelAsset,
  BackgroundAsset,
  ecomImageApi,
  ExpandConfigItem,
  UploadResponse,
  WorkflowCostItem,
  MyCostsResult,
  PromptTemplate,
  executeFunctional,
  getFunctionalResult as requestFunctionalResult,
} from "../../lib/ecom-image-api";
import ModelGalleryPopup from "./ModelGalleryPopup";
import BackgroundGalleryPopup from "./BackgroundGalleryPopup";
import ImageSourceMenu from "../shared/ImageSourceMenu";
import InspirationPromptButton from "./InspirationPromptButton";
import LogoPresetPanel, { type LogoPresetPayload } from "./LogoPresetPanel";
import CozeTemplateSetField from "./CozeTemplateSetField";
import { ReversePromptAiIcon } from "./ReversePromptAiIcon";
import { isImageFile, IMAGE_ACCEPT, compressImagesForBatchUpload } from "../../lib/file-utils";
import { getSafeErrorMessage } from "../../lib/error-codes";
import { createEcomApiFailure } from "../../lib/ecom-api-error";
import { applyPrefillParams, normalizeBatchMultiImageArrayValue } from "../../lib/ecom-prefill-utils";
import { applyReversePromptRoleToSelection, sortReversePromptImagesByRole, type ReversePromptImageRole } from "../../lib/ecom-reverse-prompt-roles";
import {
  getEcomHelpTipDeviceId,
  getEcomHelpTipDismissalStorageKey,
  getEcomHelpTipRedDotStorageKey,
} from "../../lib/ecom-help-tip-dismissal";
import { shouldRenderLocalUploadHoverPreview } from "../../lib/ecom-upload-hover-preview-position";
import { normalizePromptFieldValue } from "../../lib/ecom-prompt-field-value";
import { resolvePromptPresentation } from "../../lib/ecom-prompt-presentation";
import {
  insertPromptImageReferenceAtCaret,
  rewritePromptImageReferencesForMove,
  rewritePromptImageReferencesForRemoval,
  rewriteReversePromptImageReferences,
} from "../../lib/ecom-prompt-image-reference";
import { normalizeReversePromptToPlainText } from "../../lib/ecom-reverse-prompt-plain-text";
import {
  flattenCozeTemplateImageUrls,
  normalizeCozeTemplateSetValue,
  validateCozeTemplateSetValue,
} from "../../lib/ecom-coze-template-set";
import {
  ECOM_IMAGE_REVERSE_PROMPT_DRAFT_IMAGES_KEY,
  normalizeImageReversePromptDraftImages,
  type EcomImageReversePromptDraftImage,
} from "../../lib/ecom-draft-meta";
import {
  getCollapsedRatioSummary,
  getRatioSelectorDefault,
  normalizeRatioSelectorSelection,
  resolveRatioSelectorExternalSelection,
  resolveRatioSelectorOptions,
} from "../../lib/ecom-auto-ratio";
import { ChevronRight, Loader2, Pencil, RefreshCw, Trash2, X } from "lucide-react";
import ImageCropDialog from "./ImageCropDialog";
import EcomUploadEmptyState from "./EcomUploadEmptyState";
import { ConfiguredPromptTextarea } from "./ConfiguredPromptTextarea";
import { ModelAssetImage } from "./ModelAssetImage";
import { PerBatchRatioInlinePicker } from "./PerBatchRatioPanel";
import type { PerBatchRatio } from "../../lib/ecom-tab-history";
import { useShellState } from "@flowx/module-runtime/client";
import DetailPageReplicaConfigPanel from "./DetailPageReplicaConfigPanel";
import { isDetailPageReplicaWorkflow } from "../../lib/ecom-detail-page-replica";
import { mapWithConcurrency } from "../../lib/ecom-upload-batch";
import { resolveMainReplicaTutorialConfig } from "../../lib/ecom-main-replica-tutorial";
import { createClientUUID } from "../../lib/client-uuid";
import {
  claimReversePromptOperation,
  getLatestReversePromptOperation,
  isReversePromptOperationCurrent,
  subscribeReversePromptOperations,
  updateReversePromptOperation,
  type ReversePromptOperationSnapshot,
} from "../../lib/ecom-reverse-prompt-lifecycle";
import PromptImageReferenceOverlay from "./PromptImageReferenceOverlay";
import PromptImageMentionMenu from "./PromptImageMentionMenu";
import AdaptiveImagePreview from "./AdaptiveImagePreview";
import {
  HelpTipHoverArea,
  HelpTipRedDot,
  HelpTipRedDotArea,
  HelpTipRedDotContext,
  HelpTipTrigger,
  normalizeHelpTipConfig,
  type HelpTipConfig,
  useHelpTipRedDot,
} from "./help-tip/HelpTip";

type ReverseImageRole = ReversePromptImageRole;

const REVERSE_IMAGE_ROLE_OPTIONS: Array<{ label: string; value: ReverseImageRole }> = [
  { label: "未定义", value: "undefined" },
  { label: "产品图", value: "product" },
  { label: "参考图", value: "reference" },
];
const REVERSE_IMAGE_BULK_ROLE_OPTIONS: Array<{ label: string; value: ReverseImageRole }> = [
  { label: "产品图", value: "product" },
  { label: "参考图", value: "reference" },
  { label: "未定义", value: "undefined" },
];

const REVERSE_PROMPT_IMAGE_SOURCE_TYPES = new Set([
  "image_upload",
  "multi_image",
  "batch_multi_image",
  "reference_image",
]);

type ReversePromptImageItem = {
  id: string;
  componentKey: string;
  url: string;
  previewUrl?: string;
  editUrl?: string;
  label?: string;
};

type CozeTemplateSourceImageItem = {
  id: string;
  url: string;
  preview_url?: string;
  source_param_key?: string;
  source_label?: string;
};

type ReversePromptSubmitRequest = {
  mode: "single" | "multi";
  templateID?: number;
  sourceImages?: ReversePromptImageItem[];
  images: Array<{
    url: string;
    role: "product" | "reference";
    label: string;
  }>;
};

type ReversePromptFailureAction = {
  onRetry: () => void;
};

type ReversePromptButtonController = {
  sourceKeys: Set<string>;
  anchorKey: string;
  render: (sourceComp: ComponentConfig) => ReactNode;
};

type PromptTemplateDraft = {
  title: string;
  content: string;
};

const REVERSE_PROMPT_MODAL_OVERLAY_STYLE = {
  background: "#00000066",
};
const REVERSE_PROMPT_MODAL_SCALE = 1.2;
const REVERSE_PROMPT_MODAL_WIDTH = Math.round(522 * REVERSE_PROMPT_MODAL_SCALE);
const REVERSE_PROMPT_MODAL_IMAGE_SIZE = Math.round(104 * REVERSE_PROMPT_MODAL_SCALE);
const REVERSE_PROMPT_MODAL_ROLE_WIDTH = REVERSE_PROMPT_MODAL_IMAGE_SIZE;
const PROMPT_TEMPLATE_PREVIEW_WIDTH = 300;
const PROMPT_TEMPLATE_PREVIEW_GAP = 8;
const PROMPT_TEMPLATE_PREVIEW_MIN_WIDTH = 240;
const PROMPT_TEMPLATE_TITLE_MAX_LENGTH = 10;
function getComponentHelpTip(comp: ComponentConfig): HelpTipConfig | null {
  return normalizeHelpTipConfig(comp.config?.help_tip);
}

function getHelpTipStorageKey(scopeKey: string | undefined, workflowKey: string, comp: ComponentConfig): string {
  const helpTip = getComponentHelpTip(comp);
  return getEcomHelpTipDismissalStorageKey({
    scopeKey,
    workflowKey,
    componentParamKey: comp.coze_param_key || `component-${comp.id}`,
    helpTip: helpTip ? {
      ...helpTip,
      revision: comp.revision,
      updated_at: comp.updated_at,
    } : helpTip,
  });
}

function getHelpTipRedDotStorageKey(scopeKey: string | undefined, workflowKey: string, comp: ComponentConfig): string {
  const helpTip = getComponentHelpTip(comp);
  return getEcomHelpTipRedDotStorageKey({
    scopeKey,
    workflowKey,
    componentParamKey: comp.coze_param_key || `component-${comp.id}`,
    helpTip: helpTip ? {
      ...helpTip,
      revision: comp.revision,
      updated_at: comp.updated_at,
    } : helpTip,
  });
}

const promptTemplateCache = new Map<string, PromptTemplate[]>();
const promptTemplateRequests = new Map<string, Promise<PromptTemplate[]>>();

function promptTemplateBindingCacheKey(scopeKey: string, workflowKey: string, boundParamKey: string) {
  return `${scopeKey}::${workflowKey}::${boundParamKey}`;
}

async function loadPromptTemplatesOnce(scopeKey: string, workflowKey: string, boundParamKey: string, force = false): Promise<PromptTemplate[]> {
  const cacheKey = promptTemplateBindingCacheKey(scopeKey, workflowKey, boundParamKey);
  if (!force && promptTemplateCache.has(cacheKey)) return promptTemplateCache.get(cacheKey) || [];
  const existingRequest = promptTemplateRequests.get(cacheKey);
  if (!force && existingRequest) return existingRequest;

  const request = ecomImageApi
    .getPromptTemplates({ workflow_key: workflowKey, bound_param_key: boundParamKey, page_size: 200 })
    .then((res) => {
      const items = (res.data?.items || []).filter(
        (item) => item.workflow_key === workflowKey && item.bound_param_key === boundParamKey,
      );
      const sortedItems = sortPromptTemplatesForPicker(items);
      promptTemplateCache.set(cacheKey, sortedItems);
      return sortedItems;
    })
    .finally(() => {
      promptTemplateRequests.delete(cacheKey);
    });

  promptTemplateRequests.set(cacheKey, request);
  return request;
}

function sortPromptTemplatesForPicker(items: PromptTemplate[]): PromptTemplate[] {
  return [...items].sort((a, b) => {
    const aCustom = !!a.owner_user_id;
    const bCustom = !!b.owner_user_id;
    if (aCustom !== bCustom) return aCustom ? -1 : 1;
    const aPreset = a.team_id === 0 && !a.owner_user_id;
    const bPreset = b.team_id === 0 && !b.owner_user_id;
    if (aPreset !== bPreset) return aPreset ? -1 : 1;
    if ((a.category_id || 0) !== (b.category_id || 0)) return (a.category_id || 0) - (b.category_id || 0);
    if ((a.sort_order || 0) !== (b.sort_order || 0)) return (a.sort_order || 0) - (b.sort_order || 0);
    return b.id - a.id;
  });
}

function limitPromptTemplateTitle(value: string) {
  return Array.from(value).slice(0, PROMPT_TEMPLATE_TITLE_MAX_LENGTH).join("");
}

function isUserPromptTemplate(template: PromptTemplate, userID?: number) {
  return !!userID && !!template.owner_user_id && template.owner_user_id === userID;
}

export type ImageReferenceActions = {
  onInsert: (index: number) => void;
  onRemove: (index: number, imageCount: number) => void;
  /** Keep prompt @图片N references on the same image after a drag reorder. */
  onMove: (fromIndex: number, toIndex: number, imageCount: number) => void;
};

interface DynamicComponentPanelProps {
  /** Host-specific asset authority; undefined delegates to the existing workflow renderer. */
  renderComponentOverride?: (component: ComponentConfig, getImageReferenceActions: (sourceKey: string) => ImageReferenceActions | undefined) => ReactNode | undefined;
  /** Components for which this returns true render inside a disabled, inert fieldset (e.g. while an Agent task runs). */
  lockComponent?: (component: ComponentConfig) => boolean;
  imageReferenceUrlsByKey?: Record<string, string[]>;
  disabled?: boolean;
  /** Optional consumer layout while preserving the shared field renderers. */
  layoutClassName?: string;
  workflowKey: string;
  workflowName?: string;
  uiConfig: WorkflowUIConfig | null;
  /** Current parameter values (controlled) */
  params: Record<string, unknown>;
  /** Parameter change callback */
  onParamChange: (cozeParamKey: string, value: unknown) => void;
  /** Open reference setting modal */
  onOpenRefSetting?: (target: "product" | "model") => void;
  /** 参考图上传后的图片URL，用于传给参考设置弹窗 */
  refImageUrl?: string;
  /** 参考图URL变更回调 */
  onRefImageChange?: (url: string) => void;
  /** 当前参考图预处理结果 */
  referenceResult?: { referenceKey: string; targetParamKey: string; resultUrl: string; weight: number } | null;
  /** 清除参考图预处理结果 */
  onClearReferenceResult?: () => void;
  /** 预填充数据（来自推荐下一步） */
  prefillData?: Record<string, unknown> | null;
  /** 预填充数据消费完毕回调 */
  onPrefillConsumed?: () => void;
  /** 扩写配置列表 */
  expandConfigs?: ExpandConfigItem[];
  /** 当前选择的批次数（用于批次提示词组件） */
  batchCount?: number;
  /** 批次单位文案（后台 batch_unit 配置，如"批"、"屏"，默认"批"） */
  batchUnit?: string;
  /** Render prop for batch_selector component (rendered at its configured position) */
  renderBatchSelector?: (headerAction?: ReactNode) => ReactNode;
  /** Param keys to hide when batch processing mode is active */
  hiddenParamKeys?: string[];
  /** Callback notifying parent whether any image is still uploading */
  onUploadingChange?: (uploading: boolean) => void;
  /** Callback notifying parent whether any prompt is still being generated */
  onPromptGeneratingChange?: (generating: boolean) => void;
  /** Uploaded thumbnail hover preview routed to the main preview area */
  onImageHoverPreviewChange?: (preview: ImageHoverPreview | null) => void;
  onTemplateOnboardingChange?: (visible: boolean) => void;
  leftNavCollapsed?: boolean;
  /** Prompt text inherited from the paired FlowX Pro image-to-image workflow. */
  promptReferenceText?: string;
  /** Per-batch ratio controls anchored to a batch_multi_image component */
  perBatchRatioBinding?: {
    boundParamKey: string;
    ratioComp: ComponentConfig;
    values: PerBatchRatio[];
    globalRatio: string | null;
    globalCropSize: string | null;
    onChange: (index: number, next: PerBatchRatio) => void;
    onResetAll: () => void;
    count: number;
    batchUnit: string;
    executionMode?: string;
  } | null;
  callableKey?: string;
  billingCosts?: WorkflowCostItem[];
  myCosts?: MyCostsResult | null;
  onReversePromptCompleted?: () => void;
  /** Workspace-tab-scoped storage key for unapplied Coze reverse prompt drafts. */
  reversePromptHistoryScope?: string;
  detailReplicaActiveIndex?: number;
  onDetailReplicaActiveIndexChange?: (index: number) => void;
  onDetailReplicaGenerate?: (batchCount?: number) => void;
}

type PerBatchRatioBinding = NonNullable<DynamicComponentPanelProps["perBatchRatioBinding"]>;
export type UploadImageHoverPreview = { url: string; alt?: string };
type ImageHoverPreview = UploadImageHoverPreview;

// Ratio options matching existing page style
const RATIO_OPTIONS = ["auto", "1:1", "4:5", "2:3", "5:4", "3:2", "9:16", "3:4", "16:9", "4:3", "21:9"];

function cleanString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function parseAcceptFromKeys(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(cleanString).filter(Boolean);
  }
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(cleanString).filter(Boolean) : [];
  } catch {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
}

function isReversePromptImageSourceComponent(comp: ComponentConfig): boolean {
  return REVERSE_PROMPT_IMAGE_SOURCE_TYPES.has(comp.component_type);
}

function resolveRatioBindingForComponent(
  comp: ComponentConfig,
  binding?: PerBatchRatioBinding | null,
): PerBatchRatioBinding | undefined {
  if (!binding || comp.component_type !== "batch_multi_image") return undefined;
  if (binding.boundParamKey === comp.coze_param_key) return binding;

  const reverseSourceKey = cleanString(comp.config?.batch_ratio_source_param_key);
  if (reverseSourceKey && reverseSourceKey === binding.ratioComp.coze_param_key) {
    return binding;
  }

  const directTargetKey = cleanString(binding.ratioComp.config?.batch_ratio_bind_param_key);
  if (directTargetKey && directTargetKey === comp.coze_param_key) {
    return binding;
  }

  return undefined;
}


function normalizeImageUrlValue(value: unknown): string {
  const direct = cleanString(value);
  if (direct) return direct;

  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const record = value as Record<string, unknown>;
  return (
    cleanString(record.file_url) ||
    cleanString(record.public_url) ||
    cleanString(record.url) ||
    cleanString(record.image_url) ||
    cleanString(record.thumbnail_url)
  );
}

function normalizeImageUrlArrayValue(value: unknown): string[] {
  return flattenCozeTemplateImageUrls(value);
}

function collectCozeTemplateSourceImages(
  params: Record<string, unknown>,
  components: ComponentConfig[],
): CozeTemplateSourceImageItem[] {
  const imageTypes = new Set(["image_upload", "multi_image", "batch_multi_image", "reference_image"]);
  const seen = new Set<string>();
  const items: CozeTemplateSourceImageItem[] = [];

  components
    .filter((comp) => comp.is_visible && imageTypes.has(comp.component_type))
    .forEach((comp) => {
      const value = params[comp.coze_param_key];
      const urls = comp.component_type === "image_upload" || comp.component_type === "reference_image"
        ? normalizeImageUrlArrayValue(value).slice(0, 1)
        : normalizeImageUrlArrayValue(value);
      urls.forEach((url, index) => {
        if (!url || seen.has(url) || url.startsWith("blob:")) return;
        seen.add(url);
        items.push({
          id: `${comp.coze_param_key}-${index}-${url}`,
          url,
          preview_url: url,
          source_param_key: comp.coze_param_key,
          source_label: `${comp.label || comp.coze_param_key}${urls.length > 1 ? ` ${index + 1}` : ""}`,
        });
      });
    });

  return items;
}

function getUploadedImageUrl(upload?: UploadResponse | null): string {
  return normalizeImageUrlValue(upload);
}

function getUploadedPreviewUrl(upload?: UploadResponse | null): string {
  return (
    cleanString(upload?.thumbnail_url) ||
    cleanString(upload?.file_url) ||
    cleanString(upload?.public_url)
  );
}

function getStorageProxyUrl(storageUUID?: string): string {
  const uuid = cleanString(storageUUID);
  if (!uuid) return "";
  return `/api/v1/public/storage/file/${encodeURIComponent(uuid)}`;
}

function getUploadedEditUrl(upload?: UploadResponse | null): string {
  return getStorageProxyUrl(upload?.storage_uuid) || getUploadedPreviewUrl(upload);
}

function withMappedUrl(map: Record<string, string>, key: string, url?: string): Record<string, string> {
  const cleanKey = cleanString(key);
  const cleanUrl = cleanString(url);
  if (!cleanKey || !cleanUrl || cleanKey === cleanUrl) return map;
  return { ...map, [cleanKey]: cleanUrl };
}

function resolveHoverPreviewUrl(input: {
  url?: unknown;
  previewUrl?: unknown;
  editUrl?: unknown;
  localFallbackUrl?: unknown;
}): string {
  return (
    normalizeImageUrlValue(input.previewUrl) ||
    normalizeImageUrlValue(input.localFallbackUrl) ||
    normalizeImageUrlValue(input.url) ||
    normalizeImageUrlValue(input.editUrl)
  );
}

function revokeBlobUrl(url?: string) {
  if (url?.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}

const INTERNAL_IMAGE_DRAG_MIME = "application/x-flowx-ecom-image";
const REORDER_INDEX_MIME = "text/x-reorder-index";

type InternalImageDragPayload = {
  url: string;
  previewUrl?: string;
  editUrl?: string;
  localFallbackUrl?: string;
};

function setInternalImageDragData(e: React.DragEvent, payload: InternalImageDragPayload) {
  const url = normalizeImageUrlValue(payload.url);
  if (!url) return;
  const normalizedPayload: InternalImageDragPayload = {
    url,
    previewUrl: normalizeImageUrlValue(payload.previewUrl),
    editUrl: normalizeImageUrlValue(payload.editUrl),
    localFallbackUrl: normalizeImageUrlValue(payload.localFallbackUrl),
  };
  e.dataTransfer.setData(INTERNAL_IMAGE_DRAG_MIME, JSON.stringify(normalizedPayload));
  e.dataTransfer.setData("text/plain", url);
}

function getInternalImageDragData(dataTransfer: DataTransfer): InternalImageDragPayload | null {
  const rawPayload = dataTransfer.getData(INTERNAL_IMAGE_DRAG_MIME);
  if (rawPayload) {
    try {
      const parsed = JSON.parse(rawPayload) as Record<string, unknown>;
      const url = normalizeImageUrlValue(parsed.url);
      if (url) {
        return {
          url,
          previewUrl: normalizeImageUrlValue(parsed.previewUrl),
          editUrl: normalizeImageUrlValue(parsed.editUrl),
          localFallbackUrl: normalizeImageUrlValue(parsed.localFallbackUrl),
        };
      }
    } catch {
      // Ignore malformed drag payloads and fall back to text/uri-list.
    }
  }

  const uriList = cleanString(dataTransfer.getData("text/uri-list"));
  const plainText = cleanString(dataTransfer.getData("text/plain"));
  const fallbackUrl = normalizeImageUrlValue(uriList || plainText);
  return fallbackUrl ? { url: fallbackUrl } : null;
}

function hasReorderDragData(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes(REORDER_INDEX_MIME);
}

function UploadedPreviewImage({
  src,
  previewSrc,
  localFallbackSrc,
  alt,
  className,
}: {
  src: unknown;
  previewSrc?: unknown;
  localFallbackSrc?: unknown;
  alt: string;
  className: string;
}) {
  const candidates = useMemo(
    () => Array.from(new Set([previewSrc, src, localFallbackSrc].map(normalizeImageUrlValue).filter(Boolean))),
    [src, previewSrc, localFallbackSrc],
  );
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [src, previewSrc, localFallbackSrc]);

  const imageSrc = candidates[candidateIndex] || "";

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      draggable={false}
      decoding="async"
      onError={() => {
        setCandidateIndex((current) => (current < candidates.length - 1 ? current + 1 : current));
      }}
    />
  );
}

function resolveWorkflowSourceImageUrl(
  uiConfig: WorkflowUIConfig,
  params: Record<string, unknown>,
): string | null {
  const imageTypes = new Set(["image_upload", "reference_image", "multi_image", "batch_multi_image"]);
  const candidates = uiConfig.components
    .filter((c) => c.is_visible && imageTypes.has(c.component_type))
    .sort((a, b) => a.display_order - b.display_order);

  for (const comp of candidates) {
    const val = params[comp.coze_param_key];
    if (typeof val === "string" && val.trim()) {
      return val;
    }
    if (Array.isArray(val)) {
      if (val.length > 0 && Array.isArray(val[0])) {
        const firstBatch = val[0] as unknown[];
        const nested = firstBatch.find((v) => typeof v === "string" && (v as string).trim());
        if (typeof nested === "string") return nested;
      }
      const first = val.find((v) => typeof v === "string" && (v as string).trim());
      if (typeof first === "string") return first;
    }
  }
  return null;
}

function resolveWorkflowOutputCropSize(params: Record<string, unknown>): string | null {
  const raw = params.__output_crop_size;
  return typeof raw === "string" && raw.trim() ? raw : null;
}

function resolveWorkflowAspectRatioHint(
  uiConfig: WorkflowUIConfig,
  params: Record<string, unknown>,
): string | null {
  const ratioComp = uiConfig.components.find((c) => c.component_type === "ratio_selector");
  if (!ratioComp) return null;
  const val = params[ratioComp.coze_param_key];
  if (typeof val !== "string" || !val.trim()) return null;
  if (val === "auto" || /^\d+:\d+$/.test(val)) return val;

  const ratioResolutions = ratioComp.config?.ratio_resolutions;
  if (ratioResolutions && typeof ratioResolutions === "object" && !Array.isArray(ratioResolutions)) {
    const normalizedValue = normalizeDimensionValue(val);
    for (const [ratio, resolutions] of Object.entries(ratioResolutions as Record<string, unknown>)) {
      if (!Array.isArray(resolutions)) continue;
      if (resolutions.some((resolution) => typeof resolution === "string" && normalizeDimensionValue(resolution) === normalizedValue)) {
        return ratio;
      }
    }
  }
  return null;
}

function normalizeDimensionValue(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "").replace("*", "x");
}

function normalizeStringArrayValue(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item ?? ""));
  if (typeof value === "string") return [value];
  return [];
}

function reorderImageUrlsByPreferredOrder<T>(items: T[], preferredUrls: string[]): T[] {
  const preferred = preferredUrls.map(normalizeImageUrlValue).filter(Boolean);
  if (preferred.length === 0 || items.length < 2) return items;

  const rank = new Map(preferred.map((url, index) => [url, index]));
  return [...items].sort((a, b) => {
    const aRank = rank.get(normalizeImageUrlValue(a));
    const bRank = rank.get(normalizeImageUrlValue(b));
    const aKnown = typeof aRank === "number";
    const bKnown = typeof bRank === "number";
    if (aKnown && bKnown) return aRank - bRank;
    if (aKnown) return -1;
    if (bKnown) return 1;
    return 0;
  });
}

function normalizeAssetCompareValue(value: string | undefined): string {
  if (!value) return "";
  try {
    const url = new URL(value, "http://flowx.local");
    return url.pathname.replace(/\/+$/, "").toLowerCase();
  } catch {
    return value.trim().split("?")[0].replace(/\/+$/, "").toLowerCase();
  }
}

function isSameGalleryAsset(
  selected: Pick<ModelAsset, "id" | "coze_value" | "image_url" | "thumbnail_url"> | null,
  asset: Pick<ModelAsset, "id" | "coze_value" | "image_url" | "thumbnail_url">,
): boolean {
  if (!selected) return false;
  if (selected.id > 0 && selected.id === asset.id) return true;
  const selectedValues = new Set([
    selected.coze_value,
    selected.image_url,
    selected.thumbnail_url,
  ].map(normalizeAssetCompareValue).filter(Boolean));
  return [asset.coze_value, asset.image_url, asset.thumbnail_url]
    .map(normalizeAssetCompareValue)
    .some((value) => value && selectedValues.has(value));
}

export default function DynamicComponentPanel({
  renderComponentOverride,
  lockComponent,
  imageReferenceUrlsByKey,
  disabled = false,
  layoutClassName,
  workflowKey,
  workflowName,
  uiConfig,
  params,
  onParamChange,
  onOpenRefSetting,
  onRefImageChange,
  referenceResult,
  onClearReferenceResult,
  prefillData,
  onPrefillConsumed,
  expandConfigs,
  batchCount = 1,
  batchUnit = "批",
  renderBatchSelector,
  hiddenParamKeys,
  onUploadingChange,
  onPromptGeneratingChange,
  onImageHoverPreviewChange,
  onTemplateOnboardingChange,
  leftNavCollapsed,
  perBatchRatioBinding,
  callableKey,
  billingCosts,
  myCosts,
  onReversePromptCompleted,
  promptReferenceText,
  reversePromptHistoryScope,
  detailReplicaActiveIndex = 0,
  onDetailReplicaActiveIndexChange,
  onDetailReplicaGenerate,
}: DynamicComponentPanelProps) {
  const { user } = useShellState();
  const [showModelGallery, setShowModelGallery] = useState(false);
  const [showBackgroundGallery, setShowBackgroundGallery] = useState(false);
  // Track which componentConfig to pass to popups
  const [modelGalleryConfig, setModelGalleryConfig] = useState<ComponentConfig | undefined>();
  const [bgGalleryConfig, setBgGalleryConfig] = useState<ComponentConfig | undefined>();
  const [imageReversePrefills, setImageReversePrefills] = useState<Record<string, string>>({});
  const [reversePromptGeneratingKeys, setReversePromptGeneratingKeys] = useState<Record<string, boolean>>({});
  const [cozePromptGeneratingKeys, setCozePromptGeneratingKeys] = useState<Record<string, boolean>>({});
  const [reversePromptFailures, setReversePromptFailures] = useState<Record<string, ReversePromptFailureAction>>({});
  const [reversePromptRegenerateActions, setReversePromptRegenerateActions] = useState<Record<string, (() => void) | undefined>>({});
  const [helpTipDeviceId, setHelpTipDeviceId] = useState("server");
  useEffect(() => {
    setHelpTipDeviceId(getEcomHelpTipDeviceId());
  }, []);
  const helpTipScopeKey = `u${user?.id || 0}:t${user?.current_team_id || 0}:d${helpTipDeviceId}`;
  const imageReverseDraftImages = useMemo(
    () => normalizeImageReversePromptDraftImages(params[ECOM_IMAGE_REVERSE_PROMPT_DRAFT_IMAGES_KEY]),
    [params],
  );
  const mainReplicaTutorialConfig = useMemo(
    () => resolveMainReplicaTutorialConfig(uiConfig),
    [uiConfig],
  );

  // Aggregate upload-in-progress count from all multi-image child components
  const uploadingCountRef = useRef(0);
  const notifyUploading = useCallback((delta: number) => {
    uploadingCountRef.current += delta;
    onUploadingChange?.(uploadingCountRef.current > 0);
  }, [onUploadingChange]);

  useEffect(() => {
    return () => {
      onImageHoverPreviewChange?.(null);
    };
  }, [onImageHoverPreviewChange]);

  // Inject default values for hidden (is_visible=false) components on mount / config change.
  // This ensures that params hidden in admin still get their default values sent to Coze.
  useEffect(() => {
    if (!uiConfig) return;
    const hiddenComponents = uiConfig.components.filter((c) => !c.is_visible);
    for (const comp of hiddenComponents) {
      const defaultValue = comp.config?.default_value;
      if (defaultValue !== undefined && defaultValue !== null && defaultValue !== "") {
        onParamChange(comp.coze_param_key, defaultValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiConfig]);

  // 预填充数据处理（来自推荐下一步 / 历史再次生成）
  useEffect(() => {
    if (!prefillData || !uiConfig) return;
    applyPrefillParams(prefillData, uiConfig.components, onParamChange, (key, url) => {
      setImageReversePrefills((prev) => prev[key] === url ? prev : { ...prev, [key]: url });
    });
    onPrefillConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData, uiConfig]);

  // 派生数组必须 memo：reverseButtonControllers 的 useMemo 以 imageReverseComponents 为依赖，
  // 每次渲染新建数组会让反推订阅 effect 反复重跑并触发 setState 死循环（输入被旧值回滚）。
  const visibleComponents = useMemo(
    () => (uiConfig?.components ?? [])
      .filter((c) => c.is_visible && !(hiddenParamKeys?.includes(c.coze_param_key)))
      .sort((a, b) => a.display_order - b.display_order),
    [uiConfig, hiddenParamKeys],
  );

  const configuredPromptComponents = useMemo(
    () => (uiConfig?.components ?? []).filter(
      (component) => component.component_type === "prompt",
    ),
    [uiConfig],
  );
  const promptComponents = useMemo(
    () => visibleComponents.filter((c) => c.component_type === "prompt"),
    [visibleComponents],
  );
  const promptComp = promptComponents[0];
  const promptTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const promptTextareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const lastFocusedPromptKeyRef = useRef("");
  const hasExplicitPromptImageBindings = configuredPromptComponents.some(
    (component) => !!cleanString(component.config?.image_reference_source_param_key),
  );
  const imageReverseComponents = useMemo(
    () => visibleComponents.filter((c) => c.component_type === "image_reverse_prompt"),
    [visibleComponents],
  );
  const renderableComponents = visibleComponents.filter((c) => (
    c.component_type !== "image_reverse_prompt" && c.component_type !== "main_image_replica_tutorial"
  ));

  // Merged ratio+resolution: when a ratio_selector has embed_resolution=true and a resolution_selector exists
  const mergedRatioComp = renderableComponents.find(
    (c) => c.component_type === "ratio_selector" && c.config?.embed_resolution === true,
  ) ?? null;
  const standaloneResolutionComp = renderableComponents.find(
    (c) => c.component_type === "resolution_selector",
  ) ?? null;
  const isMerged = mergedRatioComp !== null && standaloneResolutionComp !== null;
  const displayComponents = isMerged
    ? renderableComponents.filter((c) => c.id !== standaloneResolutionComp!.id)
    : renderableComponents;

  const handleImageReverseDraftChange = useCallback((paramKey: string, image: EcomImageReversePromptDraftImage | null) => {
    const current = normalizeImageReversePromptDraftImages(params[ECOM_IMAGE_REVERSE_PROMPT_DRAFT_IMAGES_KEY]);
    const next = { ...current };
    if (image?.url) {
      next[paramKey] = image;
    } else {
      delete next[paramKey];
    }
    onParamChange(ECOM_IMAGE_REVERSE_PROMPT_DRAFT_IMAGES_KEY, next);
  }, [params, onParamChange]);

  const handleReversePromptGeneratingChange = useCallback((targetKey: string, generating: boolean) => {
    setReversePromptGeneratingKeys((prev) => {
      if (prev[targetKey] === generating) return prev;
      return { ...prev, [targetKey]: generating };
    });
  }, []);

  const handleCozePromptGeneratingChange = useCallback((componentKey: string, generating: boolean) => {
    setCozePromptGeneratingKeys((prev) => {
      if (prev[componentKey] === generating) return prev;
      return { ...prev, [componentKey]: generating };
    });
  }, []);

  const anyPromptGenerating = useMemo(
    () => Object.values(reversePromptGeneratingKeys).some(Boolean) || Object.values(cozePromptGeneratingKeys).some(Boolean),
    [cozePromptGeneratingKeys, reversePromptGeneratingKeys],
  );

  useEffect(() => {
    onPromptGeneratingChange?.(anyPromptGenerating);
  }, [anyPromptGenerating, onPromptGeneratingChange]);

  useEffect(() => () => {
    onPromptGeneratingChange?.(false);
  }, [onPromptGeneratingChange]);

  const handleReversePromptFailureChange = useCallback((targetKey: string, failure: ReversePromptFailureAction | null) => {
    setReversePromptFailures((prev) => {
      const current = prev[targetKey];
      if (!failure && !current) return prev;
      if (failure && current?.onRetry === failure.onRetry) return prev;

      const next = { ...prev };
      if (failure) {
        next[targetKey] = failure;
      } else {
        delete next[targetKey];
      }
      return next;
    });
  }, []);

  const handleReversePromptRegenerateActionChange = useCallback((targetKey: string, action?: () => void) => {
    setReversePromptRegenerateActions((prev) => {
      if (prev[targetKey] === action) return prev;
      const next = { ...prev };
      if (action) next[targetKey] = action;
      else delete next[targetKey];
      return next;
    });
  }, []);

  const handlePromptParamChange = useCallback((key: string, value: unknown) => {
    handleReversePromptFailureChange(key, null);
    onParamChange(key, value);
  }, [handleReversePromptFailureChange, onParamChange]);

  const getPromptDisplayReference = useCallback((comp: ComponentConfig) => {
    if (comp.coze_param_key !== "prompt") return "";
    const referenceText = cleanString(promptReferenceText);
    if (!referenceText) return "";
    const currentText = cleanString(params[comp.coze_param_key]);
    if (currentText && currentText !== referenceText) return "";
    return referenceText;
  }, [params, promptReferenceText]);

  const handleReversePromptSourceOrderChange = useCallback((sourceKeys: string[], orderedImages: ReversePromptImageItem[]) => {
    if (orderedImages.length === 0) return;

    sourceKeys.forEach((sourceKey) => {
      const preferredUrls = orderedImages
        .filter((image) => image.componentKey === sourceKey)
        .map((image) => image.url);
      if (preferredUrls.length === 0) return;

      const currentValue = params[sourceKey];
      if (Array.isArray(currentValue)) {
        const nextValue = Array.isArray(currentValue[0])
          ? currentValue.map((batchImages) => (
              Array.isArray(batchImages)
                ? reorderImageUrlsByPreferredOrder(batchImages, preferredUrls)
                : batchImages
            ))
          : reorderImageUrlsByPreferredOrder(currentValue, preferredUrls);

        if (JSON.stringify(nextValue) !== JSON.stringify(currentValue)) {
          onParamChange(sourceKey, nextValue);
        }
      }
    });
  }, [params, onParamChange]);

  const reverseButtonControllers = useMemo<ReversePromptButtonController[]>(() => {
    if (!uiConfig || imageReverseComponents.length === 0) return [];
    const imageSourceComponents = uiConfig.components
      .filter((c) => c.is_visible && isReversePromptImageSourceComponent(c))
      .sort((a, b) => a.display_order - b.display_order);

    return imageReverseComponents.map((reverseComp) => {
      const configuredKeys = parseAcceptFromKeys(reverseComp.accept_from);
      const fallbackKey = imageSourceComponents[0]?.coze_param_key;
      const resolvedSourceKeys = (configuredKeys.length > 0 ? configuredKeys : fallbackKey ? [fallbackKey] : []).filter(Boolean);
      const sourceKeys = new Set(resolvedSourceKeys);
      const anchorKey = imageSourceComponents.find((sourceComp) => sourceKeys.has(sourceComp.coze_param_key))?.coze_param_key || "";
      return {
        sourceKeys,
        anchorKey,
        render: (sourceComp: ComponentConfig) => {
          const irEc = expandConfigs?.find(e => e.component_key === reverseComp.coze_param_key && e.is_enabled);
          const targetKey = cleanString(reverseComp.config?.target_prompt_key) || "prompt";
          return (
            <ImageReversePromptButton
              key={`${reverseComp.id}-${sourceComp.coze_param_key}`}
              comp={reverseComp}
              onChange={(v) => handlePromptParamChange(targetKey, v)}
              workflowKey={workflowKey}
              callableKey={callableKey || workflowKey}
              expandConfig={irEc}
              sourceKeys={Array.from(sourceKeys)}
              params={params}
              uiComponents={uiConfig.components}
              prefillImageUrl={imageReversePrefills[reverseComp.coze_param_key]}
              draftImage={imageReverseDraftImages[reverseComp.coze_param_key]}
              onDraftImageChange={(image) => handleImageReverseDraftChange(reverseComp.coze_param_key, image)}
              billingCosts={billingCosts}
              myCosts={myCosts}
              isGenerating={!!reversePromptGeneratingKeys[targetKey]}
              onGeneratingChange={(generating) => handleReversePromptGeneratingChange(targetKey, generating)}
              onFailure={(failure) => handleReversePromptFailureChange(targetKey, failure)}
              onRegenerateActionChange={(action) => handleReversePromptRegenerateActionChange(targetKey, action)}
              onCompleted={onReversePromptCompleted}
              onSourceOrderChange={(orderedImages) => handleReversePromptSourceOrderChange(Array.from(sourceKeys), orderedImages)}
              helpTipStorageKey={getHelpTipStorageKey(helpTipScopeKey, workflowKey, reverseComp)}
              helpTipRedDotStorageKey={getHelpTipRedDotStorageKey(helpTipScopeKey, workflowKey, reverseComp)}
              reversePromptOwnerScope={reversePromptHistoryScope}
            />
          );
        },
      };
    });
  }, [
    billingCosts,
    callableKey,
    expandConfigs,
    handleImageReverseDraftChange,
    handlePromptParamChange,
    handleReversePromptSourceOrderChange,
    handleReversePromptFailureChange,
    handleReversePromptRegenerateActionChange,
    handleReversePromptGeneratingChange,
    helpTipScopeKey,
    imageReverseComponents,
    imageReverseDraftImages,
    imageReversePrefills,
    myCosts,
    onReversePromptCompleted,
    params,
    reversePromptGeneratingKeys,
    uiConfig,
    workflowKey,
  ]);

  const getReversePromptHeaderAction = useCallback((sourceComp: ComponentConfig): ReactNode => {
    const actionNodes = reverseButtonControllers
      .filter((controller) => controller.anchorKey === sourceComp.coze_param_key)
      .map((controller) => controller.render(sourceComp));
    return actionNodes.length > 0 ? <div className="flex items-center gap-1.5">{actionNodes}</div> : null;
  }, [reverseButtonControllers]);

  const getPromptTargetsForImageSource = useCallback((
    sourceParamKey: string,
    allowLegacyFallback: boolean,
  ) => {
    const explicitlyBound = promptComponents.filter((component) => (
      cleanString(component.config?.image_reference_source_param_key) === sourceParamKey
    ));
    if (explicitlyBound.length > 0) return explicitlyBound;
    if (hasExplicitPromptImageBindings || !allowLegacyFallback || !promptComp) return [];
    return [promptComp];
  }, [hasExplicitPromptImageBindings, promptComp, promptComponents]);

  const handleImageRefInsert = useCallback((
    sourceParamKey: string,
    index: number,
    allowLegacyFallback: boolean,
  ) => {
    const targets = getPromptTargetsForImageSource(sourceParamKey, allowLegacyFallback);
    if (targets.length === 0) return;
    const preferredTarget = targets.find(
      (target) => target.coze_param_key === lastFocusedPromptKeyRef.current,
    ) || targets[0];
    const current = normalizePromptFieldValue(params[preferredTarget.coze_param_key]);
    const textarea = promptTextareaRefs.current[preferredTarget.coze_param_key] || promptTextareaRef.current;
    const selectionStart = textarea?.selectionStart ?? current.length;
    const selectionEnd = textarea?.selectionEnd ?? selectionStart;
    const result = insertPromptImageReferenceAtCaret(current, selectionStart, index + 1, selectionEnd);

    onParamChange(
      preferredTarget.coze_param_key,
      result.value,
    );
    window.requestAnimationFrame(() => {
      const currentTextarea = promptTextareaRefs.current[preferredTarget.coze_param_key] || promptTextareaRef.current;
      if (!currentTextarea) return;
      currentTextarea.focus();
      currentTextarea.setSelectionRange(result.caret, result.caret);
    });
  }, [getPromptTargetsForImageSource, onParamChange, params]);

  const handlePromptImageSourceMove = useCallback((
    sourceParamKey: string,
    fromIndex: number,
    toIndex: number,
    imageCount: number,
    allowLegacyFallback = true,
  ) => {
    getPromptTargetsForImageSource(sourceParamKey, allowLegacyFallback).forEach((target) => {
      const current = normalizePromptFieldValue(params[target.coze_param_key]);
      const next = rewritePromptImageReferencesForMove(current, fromIndex, toIndex, imageCount);
      if (next !== current) onParamChange(target.coze_param_key, next);
    });
  }, [getPromptTargetsForImageSource, onParamChange, params]);

  const handlePromptImageSourceRemoval = useCallback((
    sourceParamKey: string,
    removedIndex: number,
    imageCount: number,
    allowLegacyFallback: boolean,
  ) => {
    getPromptTargetsForImageSource(sourceParamKey, allowLegacyFallback).forEach((target) => {
      const current = normalizePromptFieldValue(params[target.coze_param_key]);
      const next = rewritePromptImageReferencesForRemoval(current, removedIndex, imageCount);
      if (next !== current) onParamChange(target.coze_param_key, next);
    });
  }, [getPromptTargetsForImageSource, onParamChange, params]);

  const getPromptReferenceImageUrls = useCallback((promptComponent: ComponentConfig): string[] => {
    const sourceKey = cleanString(promptComponent.config?.image_reference_source_param_key);
    if (!sourceKey) return [];
    if (imageReferenceUrlsByKey) return imageReferenceUrlsByKey[sourceKey] ?? [];
    const source = visibleComponents.find((component) => component.coze_param_key === sourceKey);
    if (!source) return [];
    if (source.component_type === "image_upload" || source.component_type === "reference_image") {
      const url = normalizeImageUrlValue(params[sourceKey]);
      return url ? [url] : [];
    }
    if (source.component_type === "multi_image") {
      return normalizeImageUrlArrayValue(params[sourceKey]);
    }
    return [];
  }, [imageReferenceUrlsByKey, params, visibleComponents]);

  if (!uiConfig) return null;

  if (isDetailPageReplicaWorkflow(workflowKey, workflowName)) {
    const detailReplicaConfigComponent = uiConfig.components.find(
      (component) => component.component_type === "detail_page_replica_config",
    );
    const detailReplicaHelpTip = detailReplicaConfigComponent
      ? getComponentHelpTip(detailReplicaConfigComponent)
      : null;
    const detailReplicaConfigTitle = cleanString(
      detailReplicaConfigComponent?.config?.detail_replica_config_title,
    ) || cleanString(
      uiConfig.components.find((component) => component.component_type === "inspire_prompt")
        ?.config?.detail_replica_config_title,
    ) || "配置";
    const detailReplicaHelpTipStorageKey = detailReplicaConfigComponent
      ? getHelpTipStorageKey(helpTipScopeKey, workflowKey, detailReplicaConfigComponent)
      : undefined;
    const detailReplicaRedDotStorageKey = detailReplicaConfigComponent
      ? getHelpTipRedDotStorageKey(helpTipScopeKey, workflowKey, detailReplicaConfigComponent)
      : undefined;

    return (
      <DetailPageReplicaConfigPanel
        workflowKey={workflowKey}
        callableKey={callableKey}
        uiConfig={uiConfig}
        params={params}
        onParamChange={onParamChange}
        activeIndex={detailReplicaActiveIndex}
        onActiveIndexChange={onDetailReplicaActiveIndexChange || (() => undefined)}
        onUploadingChange={(uploading) => onUploadingChange?.(uploading)}
        onPromptGeneratingChange={onPromptGeneratingChange}
        onImageHoverPreviewChange={onImageHoverPreviewChange}
        onReversePromptCompleted={onReversePromptCompleted}
        onGenerate={onDetailReplicaGenerate}
        billingCosts={billingCosts}
        myCosts={myCosts}
        reversePromptHistoryScope={reversePromptHistoryScope}
        configTitleAction={
          <HelpTipTrigger
            helpTip={detailReplicaHelpTip?.display_mode === "icon" ? detailReplicaHelpTip : null}
            fallbackTitle={detailReplicaConfigTitle}
            redDotStorageKey={detailReplicaRedDotStorageKey}
          />
        }
        renderConfigTitle={detailReplicaHelpTip ? (title) => (
          <HelpTipRedDotContext.Provider
            value={{ helpTip: detailReplicaHelpTip, storageKey: detailReplicaRedDotStorageKey }}
          >
            <HelpTipRedDotArea>
              <HelpTipHoverArea
                helpTip={detailReplicaHelpTip}
                fallbackTitle={detailReplicaConfigTitle}
                storageKey={detailReplicaHelpTipStorageKey}
              >
                {title}
              </HelpTipHoverArea>
            </HelpTipRedDotArea>
          </HelpTipRedDotContext.Provider>
        ) : undefined}
      />
    );
  }

  const renderComponent = (comp: ComponentConfig) => {
    const override = renderComponentOverride?.(comp, (sourceKey) => {
      if (getPromptTargetsForImageSource(sourceKey, false).length === 0) return undefined;
      return {
        onInsert: (index) => { if (!disabled) handleImageRefInsert(sourceKey, index, false); },
        onRemove: (index, count) => { if (!disabled) handlePromptImageSourceRemoval(sourceKey, index, count, false); },
        onMove: (from, to, count) => { if (!disabled) handlePromptImageSourceMove(sourceKey, from, to, count, false); },
      };
    });
    if (override !== undefined) return override;
    const ratioBindingForComponent = resolveRatioBindingForComponent(comp, perBatchRatioBinding);
    switch (comp.component_type) {
      case "prompt": {
        return (
          <PromptField
            workflowKey={workflowKey}
            disabled={disabled}
            comp={comp}
            value={params[comp.coze_param_key]}
            onChange={(v) => handlePromptParamChange(comp.coze_param_key, v)}
            textareaRef={(node) => {
              promptTextareaRefs.current[comp.coze_param_key] = node;
              if (comp.id === promptComp?.id) promptTextareaRef.current = node;
            }}
            onFocus={() => {
              lastFocusedPromptKeyRef.current = comp.coze_param_key;
            }}
            imageReferenceUrls={getPromptReferenceImageUrls(comp)}
            reverseGenerating={!!reversePromptGeneratingKeys[comp.coze_param_key]}
            reverseDisplayReference={getPromptDisplayReference(comp)}
            reverseFailure={reversePromptFailures[comp.coze_param_key]}
            onReverseFailureDismiss={() => handleReversePromptFailureChange(comp.coze_param_key, null)}
            onRegenerate={reversePromptRegenerateActions[comp.coze_param_key]}
          />
        );
      }
      case "model_gallery":
        return <ModelGalleryField comp={comp} value={params[comp.coze_param_key] as ModelAsset | null} onOpen={() => { setModelGalleryConfig(comp); setShowModelGallery(true); }} onSelect={(asset) => onParamChange(comp.coze_param_key, asset)} onClear={() => onParamChange(comp.coze_param_key, null)} />;
      case "background_gallery":
        return <BackgroundGalleryField comp={comp} value={params[comp.coze_param_key] as BackgroundAsset | null} onOpen={() => { setBgGalleryConfig(comp); setShowBackgroundGallery(true); }} onClear={() => onParamChange(comp.coze_param_key, null)} />;
      case "reference_image":
        return <ReferenceImageField
          comp={comp}
          value={normalizeImageUrlValue(params[comp.coze_param_key])}
          onChange={(v) => {
            onParamChange(comp.coze_param_key, v);
            onRefImageChange?.(v);
          }}
          onImageRefClick={
            getPromptTargetsForImageSource(comp.coze_param_key, false).length > 0
              ? () => handleImageRefInsert(comp.coze_param_key, 0, false)
              : undefined
          }
          onImageRemove={() => handlePromptImageSourceRemoval(
            comp.coze_param_key,
            0,
            1,
            false,
          )}
          onOpen={() => onOpenRefSetting?.("product")}
          referenceResult={referenceResult}
          onClearResult={onClearReferenceResult}
          headerAction={getReversePromptHeaderAction(comp)}
          onHoverPreviewChange={onImageHoverPreviewChange}
        />;
      case "ratio_selector": {
        const embedProps = isMerged && comp.id === mergedRatioComp!.id && standaloneResolutionComp
          ? (() => {
              const resComp = standaloneResolutionComp;
              const configOptions = resComp.config?.options as Array<{ label: string; value: string }> | undefined;
              const opts = configOptions?.length ? configOptions.map((o) => o.value) : DEFAULT_RESOLUTION_OPTIONS;
              return {
                label: resComp.label || "分辨率",
                options: opts,
                value: (params[resComp.coze_param_key] as string) || "",
                onChange: (v: string) => onParamChange(resComp.coze_param_key, v),
              };
            })()
          : null;
        return <RatioField
          comp={comp}
          value={params[comp.coze_param_key]}
          cropSize={params.__output_crop_size}
          onChange={({ ratio, cropSize }) => {
            onParamChange(comp.coze_param_key, ratio);
            onParamChange("__output_crop_size", cropSize);
          }}
          resolutionEmbed={embedProps}
        />;
      }
      case "resolution_selector":
        return <ResolutionField comp={comp} value={params[comp.coze_param_key] as string || ""} onChange={(v) => onParamChange(comp.coze_param_key, v)} />;
      case "image_upload":
        if (ratioBindingForComponent) {
          return <BatchMultiImageField comp={comp} value={normalizeImageUrlArrayValue(params[comp.coze_param_key])} batchCount={batchCount} batchUnit={batchUnit} onChange={(v) => onParamChange(comp.coze_param_key, v)} notifyUploading={notifyUploading} ratioBinding={ratioBindingForComponent} headerAction={getReversePromptHeaderAction(comp)} onHoverPreviewChange={onImageHoverPreviewChange} />;
        }
        return <SingleImageField
          comp={comp}
          value={normalizeImageUrlValue(params[comp.coze_param_key])}
          onChange={(v) => onParamChange(comp.coze_param_key, v)}
          onImageRefClick={
            getPromptTargetsForImageSource(comp.coze_param_key, false).length > 0
              ? () => handleImageRefInsert(comp.coze_param_key, 0, false)
              : undefined
          }
          onImageRemove={() => handlePromptImageSourceRemoval(
            comp.coze_param_key,
            0,
            1,
            false,
          )}
          headerAction={getReversePromptHeaderAction(comp)}
          onHoverPreviewChange={onImageHoverPreviewChange}
        />;
      case "multi_image":
        if (ratioBindingForComponent) {
          return <BatchMultiImageField comp={comp} value={normalizeImageUrlArrayValue(params[comp.coze_param_key])} batchCount={batchCount} batchUnit={batchUnit} onChange={(v) => onParamChange(comp.coze_param_key, v)} notifyUploading={notifyUploading} ratioBinding={ratioBindingForComponent} headerAction={getReversePromptHeaderAction(comp)} onHoverPreviewChange={onImageHoverPreviewChange} />;
        }
        return <MultiImageField
          comp={comp}
          value={normalizeImageUrlArrayValue(params[comp.coze_param_key])}
          onChange={(v) => onParamChange(comp.coze_param_key, v)}
          onImageRefClick={(index) => handleImageRefInsert(comp.coze_param_key, index, true)}
          onImageReorder={(fromIndex, toIndex, imageCount) => handlePromptImageSourceMove(
            comp.coze_param_key,
            fromIndex,
            toIndex,
            imageCount,
          )}
          onImageRemove={(index, imageCount) => handlePromptImageSourceRemoval(
            comp.coze_param_key,
            index,
            imageCount,
            true,
          )}
          notifyUploading={notifyUploading}
          headerAction={getReversePromptHeaderAction(comp)}
          onHoverPreviewChange={onImageHoverPreviewChange}
        />;
      case "preset_prompt":
        return <PresetPromptField
          comp={comp}
          value={params[comp.coze_param_key] as string || ""}
          onChange={(v) => onParamChange(comp.coze_param_key, v)}
          onPromptChange={(promptText) => {
            const pComp = visibleComponents.find((c) => c.component_type === "prompt");
            if (pComp) {
              onParamChange(pComp.coze_param_key, promptText);
            }
          }}
          onMetaChange={(meta) => {
            for (const [k, v] of Object.entries(meta)) {
              onParamChange(k, v);
            }
          }}
        />;
      case "select":
        return <SelectField comp={comp} value={params[comp.coze_param_key] as string || ""} onChange={(v) => onParamChange(comp.coze_param_key, v)} />;
      case "tag_select":
        return <TagSelectField comp={comp} value={params[comp.coze_param_key] as string || ""} onChange={(v) => onParamChange(comp.coze_param_key, v)} />;
      case "style_preset_selector":
        return <TagSelectField comp={comp} value={params[comp.coze_param_key] as string || ""} onChange={(v) => onParamChange(comp.coze_param_key, v)} />;
      case "batch_prompt": {
        return (
          <BatchPromptField
            workflowKey={workflowKey}
            comp={comp}
            value={normalizeStringArrayValue(params[comp.coze_param_key])}
            batchCount={batchCount}
            batchUnit={batchUnit}
            onChange={(v) => handlePromptParamChange(comp.coze_param_key, v)}
            reverseGenerating={!!reversePromptGeneratingKeys[comp.coze_param_key]}
            reverseFailure={reversePromptFailures[comp.coze_param_key]}
            onReverseFailureDismiss={() => handleReversePromptFailureChange(comp.coze_param_key, null)}
          />
        );
      }
      case "batch_multi_image":
        if (comp.config?.param_type === "array") {
          return <BatchMultiImageArrayField comp={comp} value={normalizeBatchMultiImageArrayValue(params[comp.coze_param_key])} batchCount={batchCount} batchUnit={batchUnit} onChange={(v) => onParamChange(comp.coze_param_key, v)} notifyUploading={notifyUploading} ratioBinding={ratioBindingForComponent} headerAction={getReversePromptHeaderAction(comp)} onHoverPreviewChange={onImageHoverPreviewChange} />;
        }
        return <BatchMultiImageField comp={comp} value={normalizeImageUrlArrayValue(params[comp.coze_param_key])} batchCount={batchCount} batchUnit={batchUnit} onChange={(v) => onParamChange(comp.coze_param_key, v)} notifyUploading={notifyUploading} ratioBinding={ratioBindingForComponent} headerAction={getReversePromptHeaderAction(comp)} onHoverPreviewChange={onImageHoverPreviewChange} />;
      case "hidden":
        return <HiddenField comp={comp} onChange={(v) => onParamChange(comp.coze_param_key, v)} />;
      case "batch_selector": {
        const helpTip = getComponentHelpTip(comp);
        return renderBatchSelector ? <>{renderBatchSelector(<HelpTipTrigger helpTip={helpTip?.display_mode === "icon" ? helpTip : null} fallbackTitle={`生成${batchUnit}次`} />)}</> : null;
      }
      case "coze_template_set": {
        const imageReferenceSourceKeys = new Set([
          comp.coze_param_key,
          cleanString(comp.config?.upload_images_param),
        ].filter(Boolean));
        return (
          <CozeTemplateSetField
            comp={comp}
            workflowKey={workflowKey}
            value={normalizeCozeTemplateSetValue(params[comp.coze_param_key])}
            sourceImages={collectCozeTemplateSourceImages(params, uiConfig.components)}
            onChange={(v) => onParamChange(comp.coze_param_key, v)}
            myCosts={myCosts}
            notifyUploading={notifyUploading}
            onPromptGeneratingChange={handleCozePromptGeneratingChange}
            onHoverPreviewChange={onImageHoverPreviewChange}
            onOnboardingChange={onTemplateOnboardingChange}
            leftNavCollapsed={leftNavCollapsed}
            reversePromptHistoryScope={reversePromptHistoryScope}
            mainReplicaTutorialConfig={mainReplicaTutorialConfig}
            imageReferenceBindingEnabled={
              configuredPromptComponents.some((promptComponent) => (
                imageReferenceSourceKeys.has(
                  cleanString(promptComponent.config?.image_reference_source_param_key),
                )
              ))
            }
          />
        );
      }
      case "main_image_replica_tutorial":
        return null;
      case "inspire_prompt": {
        const acceptFromRaw: string[] = (() => {
          if (!comp.accept_from) return [];
          try { return JSON.parse(comp.accept_from); } catch { return []; }
        })();
        const acceptFromParsed = [...acceptFromRaw].sort((a, b) => {
          const compA = uiConfig.components.find((c) => c.coze_param_key === a);
          const compB = uiConfig.components.find((c) => c.coze_param_key === b);
          return (compA?.display_order ?? 999) - (compB?.display_order ?? 999);
        });
        const targetKey = (comp.config as Record<string, unknown>)?.target_prompt_key as string || "prompt";
        const configuredTemplateID = Number((comp.config as Record<string, unknown>)?.inspire_template_id);
        const defaultTemplateID = Number.isFinite(configuredTemplateID) && configuredTemplateID > 0
          ? configuredTemplateID
          : undefined;
        const componentLabels: Record<string, string> = {};
        for (const key of acceptFromParsed) {
          const found = uiConfig.components.find((c) => c.coze_param_key === key);
          if (found?.label) componentLabels[key] = found.label;
        }
        return (
          <div>
            <FieldHeader label={comp.label || "灵感反推"} required={comp.is_required} helpTip={getComponentHelpTip(comp)} />
            <InspirationPromptButton
              acceptFrom={acceptFromParsed}
              targetPromptKey={targetKey}
              batchCount={batchCount}
              params={params}
              workflowKey={workflowKey}
              defaultTemplateId={defaultTemplateID}
              componentLabels={componentLabels}
              reversePromptOwnerScope={reversePromptHistoryScope}
              onApplyPrompts={(prompts) => {
                onParamChange(targetKey, prompts);
              }}
            />
          </div>
        );
      }
      case "logo_preset":
        return (
          <div>
            <FieldHeader label={comp.label || "Logo 预设"} required={comp.is_required} helpTip={getComponentHelpTip(comp)} />
            <LogoPresetField
              value={params[comp.coze_param_key] as LogoPresetFieldValue || { enabled: true, items: [] }}
              onChange={(v) => onParamChange(comp.coze_param_key, v)}
              sourceImageUrl={resolveWorkflowSourceImageUrl(uiConfig, params)}
              outputCropSize={resolveWorkflowOutputCropSize(params)}
              aspectRatioHint={resolveWorkflowAspectRatioHint(uiConfig, params)}
            />
          </div>
        );
      case "template_multi_select":
        return (
          <TemplateMultiSelectField
            comp={comp}
            value={(params[comp.coze_param_key] as number[]) || []}
            onChange={(v) => onParamChange(comp.coze_param_key, v)}
          />
        );
      default:
        return null;
    }
  };

  function resolveColumns(comp: ComponentConfig): 1 | 2 | 3 {
    if (comp.component_type === "model_gallery") return 1;
    if (comp.columns === 1 || comp.columns === 2 || comp.columns === 3) return comp.columns;
    if (comp.component_type === "background_gallery" || comp.component_type === "reference_image") return 2;
    return 1;
  }

  return (
    <>
      <div className={layoutClassName || "flex flex-wrap gap-2"}>
        {displayComponents.map((comp) => {
          const cols = resolveColumns(comp);
          const colClass = cols === 2
            ? "w-[calc(50%-4px)]"
            : cols === 3
              ? "w-[calc(33.33%-4px)]"
              : "w-full";
          const helpTip = getComponentHelpTip(comp);
          const helpTipStorageKey = getHelpTipStorageKey(helpTipScopeKey, workflowKey, comp);
          const redDotStorageKey = getHelpTipRedDotStorageKey(helpTipScopeKey, workflowKey, comp);
          return (
            // id=0（无组件行的 param 派生工作流）时回落到 coze_param_key，
            // 避免多个组件共享 key 0 触发 React 重复 key 报错。
            <div key={comp.id || comp.coze_param_key} className={colClass}>
              <HelpTipRedDotContext.Provider value={{ helpTip, storageKey: redDotStorageKey }}>
                <HelpTipRedDotArea>
                  <HelpTipHoverArea helpTip={helpTip} fallbackTitle={comp.label} storageKey={helpTipStorageKey}>
                    {lockComponent?.(comp)
                      ? <fieldset disabled inert className="m-0 min-w-0 border-0 p-0">{renderComponent(comp)}</fieldset>
                      : renderComponent(comp)}
                  </HelpTipHoverArea>
                </HelpTipRedDotArea>
              </HelpTipRedDotContext.Provider>
            </div>
          );
        })}
      </div>

      {/* Popups */}
      <ModelGalleryPopup
        isOpen={showModelGallery}
        onClose={() => setShowModelGallery(false)}
        workflowKey={workflowKey}
        componentConfig={modelGalleryConfig}
        onSelectAsset={(asset) => {
          // Find the model_gallery component to get its coze_param_key
          const modelComp = visibleComponents.find((c) => c.component_type === "model_gallery");
          if (modelComp) {
            onParamChange(modelComp.coze_param_key, asset);
          }
          setShowModelGallery(false);
        }}
      />

      <BackgroundGalleryPopup
        isOpen={showBackgroundGallery}
        onClose={() => setShowBackgroundGallery(false)}
        componentConfig={bgGalleryConfig}
        onSelectAsset={(asset) => {
          const bgComp = visibleComponents.find((c) => c.component_type === "background_gallery");
          if (bgComp) {
            onParamChange(bgComp.coze_param_key, asset);
          }
          setShowBackgroundGallery(false);
        }}
      />
    </>
  );
}

// ====== Sub-components ======

function FieldHeader({
  label,
  required,
  meta,
  action,
  helpTip,
}: {
  label: string;
  required?: boolean;
  meta?: ReactNode;
  action?: ReactNode;
  helpTip?: HelpTipConfig | null;
}) {
  return (
    <div className="mb-1.5 flex min-h-7 items-center justify-between gap-2">
      <div className="min-w-0 text-[15px] font-medium text-white">
        <span className="inline-flex min-w-0 items-center gap-1.5 align-middle">
          <span className="truncate">{label}</span>
          {required && <span className="shrink-0 text-red-400">*</span>}
          <HelpTipTrigger helpTip={helpTip?.display_mode === "icon" ? helpTip : null} fallbackTitle={label} />
        </span>
        {meta}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

function PromptTemplateIcon({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1024 1024"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M869.973333 632.917333l10.112 6.058667a42.666667 42.666667 0 0 1 0 73.173333l-343.722666 206.250667a42.666667 42.666667 0 0 1-43.904 0l-343.722667-206.250667a42.666667 42.666667 0 0 1 0-73.173333l10.154667-6.058667 355.541333 213.333334 355.541333-213.333334z m0-170.666666l10.112 6.058666a42.666667 42.666667 0 0 1 0 73.173334l-343.722666 206.250666a42.666667 42.666667 0 0 1-43.904 0l-343.722667-206.250666a42.666667 42.666667 0 0 1 0-73.173334l10.154667-6.058666 355.541333 213.333333 355.541333-213.333333zM536.32 91.434667l343.722667 206.208a42.666667 42.666667 0 0 1 0 73.173333l-343.722667 206.250667a42.666667 42.666667 0 0 1-43.904 0L148.736 370.816a42.666667 42.666667 0 0 1 0-73.173333l343.722667-206.208a42.666667 42.666667 0 0 1 43.904 0z"
        fill="#ffffff"
      />
    </svg>
  );
}

function CustomPromptTemplateModal({
  open,
  mode = "create",
  draft,
  saving,
  error,
  onDraftChange,
  onClose,
  onSave,
}: {
  open: boolean;
  mode?: "create" | "edit";
  draft: PromptTemplateDraft;
  saving: boolean;
  error: string;
  onDraftChange: (draft: PromptTemplateDraft) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, saving, onClose]);

  if (!open || typeof document === "undefined") return null;

  const canSave = draft.title.trim() !== "" && draft.content.trim() !== "" && !saving;

  return createPortal(
    <div
      className="flowx-ecom-overlay fixed inset-0 z-[150] flex items-center justify-center bg-black/20 px-4 py-6 backdrop-blur-[7.5px]"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <div className="flex h-[min(515px,calc(100vh-48px))] w-[min(515px,calc(100vw-32px))] flex-col overflow-hidden rounded-[15px] bg-[#181818]">
        <div className="flex h-[71px] shrink-0 items-center border-b border-[#353537] px-[35px]">
          <h3 className="text-base font-medium text-[#F6F6F6]">{mode === "edit" ? "编辑自定义模版" : "新建自定义模版"}</h3>
        </div>
        <div className="flex min-h-0 flex-1 flex-col px-[35px] pb-[30px] pt-6">
          <label className="text-sm font-normal leading-5 text-[#F6F6F6]" htmlFor="custom-prompt-template-title">
            模版名称
          </label>
          <EcomInput
            id="custom-prompt-template-title"
            value={draft.title}
            onChange={(event) => onDraftChange({ ...draft, title: limitPromptTemplateTitle(event.target.value) })}
            disabled={saving}
            placeholder="编辑模版名称"
            className="mt-[10px] h-[46px] w-full rounded-[12px] border border-[#3F3F3F] bg-transparent px-5 text-sm leading-5 text-[#F6F6F6] outline-none placeholder:text-[#8C8C8C] focus:border-[#555] disabled:cursor-not-allowed disabled:opacity-60"
            style={{ width: '100%' }}
            maxLength={PROMPT_TEMPLATE_TITLE_MAX_LENGTH}
          />

          <label className="mt-[18px] text-sm font-normal leading-5 text-[#F6F6F6]" htmlFor="custom-prompt-template-content">
            模版提示词
          </label>
          <EcomInput
            multiline
            id="custom-prompt-template-content"
            data-ecom-prompt-input
            value={draft.content}
            onChange={(event) => onDraftChange({ ...draft, content: event.target.value })}
            disabled={saving}
            placeholder="编辑提示词内容"
            className="mt-[10px] min-h-[150px] flex-1 resize-none rounded-[12px] px-4 py-3 text-sm leading-5 disabled:cursor-not-allowed disabled:opacity-60"
            style={{ width: '100%' }}
          />

          {error && <div className="mt-2 text-xs text-red-300">{error}</div>}

          <div className="mt-[30px] flex shrink-0 items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-10 w-[74px] rounded-[12px] border border-[#3F3F3F] text-sm font-normal text-[#9F9F9F] transition-colors hover:border-[#555] hover:text-[#F6F6F6] disabled:cursor-not-allowed disabled:opacity-60"
            >
              取消
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={!canSave}
              className={`inline-flex h-10 w-[165px] items-center justify-center rounded-[8px] text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                canSave
                  ? "bg-[#FAFBFF] text-[#222222] hover:bg-[#F1F3FA]"
                  : "bg-[#2C2C2C] text-[#F6F6F6]"
              }`}
            >
              {saving && <Loader2 size={14} className="mr-2 animate-spin" />}
              {mode === "edit" ? "保存修改" : "保存提示词"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DeletePromptTemplateConfirmModal({
  template,
  deleting,
  onClose,
  onConfirm,
}: {
  template: PromptTemplate | null;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    if (!template) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !deleting) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [template, deleting, onClose]);

  if (!template || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[160] flex items-center justify-center bg-black/20 px-4 py-6 backdrop-blur-[7.5px]"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget && !deleting) {
          onClose();
        }
      }}
    >
      <div className="w-[min(420px,calc(100vw-32px))] overflow-hidden rounded-[15px] border border-[#353537] bg-[#181818] shadow-2xl">
        <div className="flex h-[70px] items-center justify-between border-b border-[#353537] px-8">
          <h3 className="text-base font-medium text-[#F6F6F6]">删除自定义模版</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[#8C8C8C] transition-colors hover:bg-white/8 hover:text-[#F6F6F6] disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-8 pb-7 pt-6">
          <p className="text-sm leading-6 text-[#F6F6F6]">
            确认删除「<span className="font-medium text-white">{template.title}</span>」？
          </p>
          <p className="mt-2 text-xs leading-5 text-[#8C8C8C]">删除后该提示词模版将从当前列表移除。</p>
          <div className="mt-7 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="h-10 w-[74px] rounded-[12px] border border-[#3F3F3F] text-sm font-normal text-[#9F9F9F] transition-colors hover:border-[#555] hover:text-[#F6F6F6] disabled:cursor-not-allowed disabled:opacity-60"
            >
              取消
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={deleting}
              className="inline-flex h-10 w-[112px] items-center justify-center rounded-[8px] bg-[#FAFBFF] text-sm font-medium text-[#222222] transition-colors hover:bg-[#F1F3FA] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleting && <Loader2 size={14} className="mr-2 animate-spin" />}
              确认删除
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PromptTemplatePicker({ workflowKey, boundParamKey, disabled, onApply }: {
  workflowKey: string;
  boundParamKey: string;
  disabled?: boolean;
  onApply: (content: string) => void;
}) {
  const feedback = useEcomFeedback();
  const { user } = useShellState();
  const scopeKey = `u${user?.id || 0}:t${user?.current_team_id || 0}`;
  const cacheKey = promptTemplateBindingCacheKey(scopeKey, workflowKey, boundParamKey);
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<PromptTemplate[]>(() => promptTemplateCache.get(cacheKey) || []);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(() => promptTemplateCache.has(cacheKey));
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<PromptTemplateDraft>({ title: "", content: "" });
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = useState<PromptTemplate | null>(null);
  const [deletingTemplateID, setDeletingTemplateID] = useState<number | null>(null);
  const [selectedTemplateID, setSelectedTemplateID] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previewAnchorRef = useRef<HTMLElement | null>(null);
  const previewTemplateRef = useRef<PromptTemplate | null>(null);
  const [templatePreview, setTemplatePreview] = useState<{
    template: PromptTemplate;
    left: number;
    top: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const loadTemplates = useCallback(async (force = false) => {
    setLoading(true);
    setError("");
    try {
      const items = await loadPromptTemplatesOnce(scopeKey, workflowKey, boundParamKey, force);
      setTemplates(items);
      setLoaded(true);
    } catch (err: unknown) {
      setError(getSafeErrorMessage(err, "模板加载失败"));
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [scopeKey, workflowKey, boundParamKey]);

  useEffect(() => {
    const nextCacheKey = promptTemplateBindingCacheKey(scopeKey, workflowKey, boundParamKey);
    setTemplates(promptTemplateCache.get(nextCacheKey) || []);
    setLoaded(promptTemplateCache.has(nextCacheKey));
    setSelectedTemplateID(null);
    setError("");
  }, [scopeKey, workflowKey, boundParamKey]);

  useEffect(() => {
    if (!open || loaded || loading) return;
    loadTemplates();
  }, [open, loaded, loading, loadTemplates]);

  useEffect(() => {
    if (open) return;
    previewTemplateRef.current = null;
    previewAnchorRef.current = null;
    setTemplatePreview(null);
  }, [open]);

  const handleApply = (template: PromptTemplate) => {
    onApply(template.content);
    setSelectedTemplateID(template.id);
    setOpen(false);
    feedback.success("已应用提示词模板");
  };

  const updateTemplatePreviewPosition = useCallback((template: PromptTemplate, anchor: HTMLElement) => {
    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    const margin = 12;
    const maxHeight = Math.min(340, window.innerHeight - margin * 2);
    const baseRect = panelRect || anchorRect;
    const spaceRight = window.innerWidth - baseRect.right - PROMPT_TEMPLATE_PREVIEW_GAP - margin;
    const spaceLeft = baseRect.left - PROMPT_TEMPLATE_PREVIEW_GAP - margin;
    const width = Math.min(
      PROMPT_TEMPLATE_PREVIEW_WIDTH,
      Math.max(PROMPT_TEMPLATE_PREVIEW_MIN_WIDTH, window.innerWidth - margin * 2),
    );
    let left = margin;
    let top = anchorRect.top;

    if (spaceRight >= PROMPT_TEMPLATE_PREVIEW_MIN_WIDTH) {
      left = baseRect.right + PROMPT_TEMPLATE_PREVIEW_GAP;
    } else if (spaceLeft >= PROMPT_TEMPLATE_PREVIEW_MIN_WIDTH) {
      left = baseRect.left - Math.min(width, spaceLeft) - PROMPT_TEMPLATE_PREVIEW_GAP;
    } else {
      left = Math.max(margin, Math.min(anchorRect.left, window.innerWidth - margin - width));
      const bottomTop = baseRect.bottom + PROMPT_TEMPLATE_PREVIEW_GAP;
      const topTop = baseRect.top - maxHeight - PROMPT_TEMPLATE_PREVIEW_GAP;
      top = bottomTop + Math.min(maxHeight, 220) <= window.innerHeight - margin ? bottomTop : topTop;
    }

    left = Math.max(margin, Math.min(left, window.innerWidth - margin - width));
    top = Math.max(margin, Math.min(top, window.innerHeight - margin - maxHeight));
    setTemplatePreview((prev) => {
      if (
        prev?.template.id === template.id &&
        Math.abs(prev.left - left) < 0.5 &&
        Math.abs(prev.top - top) < 0.5 &&
        Math.abs(prev.width - width) < 0.5 &&
        Math.abs(prev.maxHeight - maxHeight) < 0.5
      ) {
        return prev;
      }
      return {
        template,
        left,
        top,
        width,
        maxHeight,
      };
    });
  }, []);

  const handleShowTemplatePreview = useCallback((template: PromptTemplate, anchor: HTMLElement) => {
    previewTemplateRef.current = template;
    previewAnchorRef.current = anchor;
    updateTemplatePreviewPosition(template, anchor);
  }, [updateTemplatePreviewPosition]);

  const hideTemplatePreview = useCallback(() => {
    previewTemplateRef.current = null;
    previewAnchorRef.current = null;
    setTemplatePreview(null);
  }, []);

  useEffect(() => {
    if (!open) return;

    let animationFrameID = 0;
    const trackPosition = () => {
      const template = previewTemplateRef.current;
      const anchor = previewAnchorRef.current;
      if (template && anchor && document.body.contains(anchor)) {
        updateTemplatePreviewPosition(template, anchor);
      }

      animationFrameID = window.requestAnimationFrame(trackPosition);
    };

    animationFrameID = window.requestAnimationFrame(trackPosition);
    return () => window.cancelAnimationFrame(animationFrameID);
  }, [open, updateTemplatePreviewPosition]);

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setCreateDraft({ title: "", content: "" });
    setCreateError("");
    setOpen(false);
    setCreateOpen(true);
  };

  const handleOpenEdit = (template: PromptTemplate) => {
    setEditingTemplate(template);
    setCreateDraft({ title: template.title, content: template.content });
    setCreateError("");
    setOpen(false);
    setCreateOpen(true);
  };

  const handleCloseCreate = useCallback(() => {
    if (createSaving) return;
    setCreateOpen(false);
    setCreateError("");
    setEditingTemplate(null);
  }, [createSaving]);

  const handleSaveCreate = useCallback(async () => {
    const title = limitPromptTemplateTitle(createDraft.title.trim());
    const content = createDraft.content.trim();
    if (!title) {
      setCreateError("模版名称不能为空");
      return;
    }
    if (!content) {
      setCreateError("提示词内容不能为空");
      return;
    }
    setCreateSaving(true);
    setCreateError("");
    try {
      const res = editingTemplate
        ? await ecomImageApi.updatePromptTemplate(editingTemplate.id, {
            title,
            content,
            workflow_key: workflowKey,
            bound_param_key: boundParamKey,
          })
        : await ecomImageApi.createPromptTemplate({
            title,
            content,
            workflow_key: workflowKey,
            bound_param_key: boundParamKey,
            category_id: 0,
          });
      const saved = res.data;
      const currentItems = promptTemplateCache.get(cacheKey) || templates;
      const nextItems = saved
        ? sortPromptTemplatesForPicker([saved, ...currentItems.filter((item) => item.id !== saved.id)])
        : await loadPromptTemplatesOnce(scopeKey, workflowKey, boundParamKey, true);
      promptTemplateCache.set(cacheKey, nextItems);
      setTemplates(nextItems);
      setLoaded(true);
      setError("");
      setCreateOpen(false);
      setEditingTemplate(null);
      setOpen(true);
      feedback.success(editingTemplate ? "已修改提示词模板" : "已保存提示词模板");
    } catch (err: unknown) {
      setCreateError(getSafeErrorMessage(err, editingTemplate ? "模板修改失败" : "模板保存失败"));
    } finally {
      setCreateSaving(false);
    }
  }, [boundParamKey, cacheKey, createDraft, editingTemplate, scopeKey, templates, workflowKey]);

  const handleRequestDeleteTemplate = useCallback((template: PromptTemplate) => {
    setPendingDeleteTemplate(template);
    setOpen(false);
  }, []);

  const handleCloseDeleteTemplate = useCallback(() => {
    if (deletingTemplateID != null) return;
    setPendingDeleteTemplate(null);
  }, [deletingTemplateID]);

  const handleConfirmDeleteTemplate = useCallback(async () => {
    const template = pendingDeleteTemplate;
    if (!template) return;
    setDeletingTemplateID(template.id);
    try {
      await ecomImageApi.deletePromptTemplate(template.id);
      const nextItems = (promptTemplateCache.get(cacheKey) || templates).filter((item) => item.id !== template.id);
      promptTemplateCache.set(cacheKey, nextItems);
      setTemplates(nextItems);
      setSelectedTemplateID((selectedID) => selectedID === template.id ? null : selectedID);
      setPendingDeleteTemplate(null);
      feedback.success("已删除提示词模板");
    } catch {
      feedback.error({ code: "E5007", fallback: "operation" });
    } finally {
      setDeletingTemplateID(null);
    }
  }, [cacheKey, pendingDeleteTemplate, templates]);

  const panel = (
    <div
      ref={panelRef}
      className="ecom-prompt-template-popup"
    >
      <div
        className="ecom-prompt-template-list"
        onScroll={hideTemplatePreview}
      >
        {loading ? (
          <div className="flex h-20 items-center justify-center text-xs text-white/45">
            <Loader2 size={14} className="mr-2 animate-spin" />
            模板加载中...
          </div>
        ) : error ? (
          <div className="m-2 rounded-[4px] border border-red-500/20 bg-red-500/10 p-2.5 text-xs text-red-200">
            <div>{error}</div>
            <button type="button" onClick={() => loadTemplates(true)} className="mt-2 text-[#00FAE5] hover:text-[#5ffef1]">重试</button>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex h-20 items-center justify-center text-xs text-white/35">暂无可用模板</div>
        ) : (
          <div>
            {templates.map((template) => {
              const canEdit = isUserPromptTemplate(template, user?.id);
              const deleting = deletingTemplateID === template.id;
              return (
                <div
                  key={template.id}
                  className="ecom-prompt-template-item group"
                  data-selected={selectedTemplateID === template.id ? "true" : undefined}
                  onMouseEnter={(event) => handleShowTemplatePreview(template, event.currentTarget)}
                  onMouseLeave={hideTemplatePreview}
                >
                  <div className="mb-1.5 flex min-w-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleApply(template)}
                      aria-pressed={selectedTemplateID === template.id}
                      onFocus={(event) => handleShowTemplatePreview(template, event.currentTarget)}
                      onBlur={hideTemplatePreview}
                      className="min-w-0 flex-1 text-left"
                    >
                      <span className="block min-w-0 truncate text-sm font-medium leading-5">{template.title}</span>
                    </button>
                    {canEdit && (
                      <div className="flex shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(template)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-[6px]"
                          aria-label="修改自定义模版"
                          title="修改"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRequestDeleteTemplate(template)}
                          disabled={deleting}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="删除自定义模版"
                          title="删除"
                        >
                          {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleApply(template)}
                    aria-pressed={selectedTemplateID === template.id}
                    onFocus={(event) => handleShowTemplatePreview(template, event.currentTarget)}
                    onBlur={hideTemplatePreview}
                    className="min-w-0 text-left"
                  >
                    <p className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-light leading-[18px]">{template.content}</p>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="shrink-0 bg-transparent pt-1">
        <button
          type="button"
          onClick={handleOpenCreate}
          className="w-full rounded-[8px] bg-transparent p-[10px] text-left text-sm font-medium leading-5 text-[#F6F6F6] hover:bg-[#3F3F3F]"
        >
          +新建自定义模版
        </button>
      </div>
    </div>
  );

  const previewPanel = open && templatePreview ? (
    <div
      className="flowx-ecom-overlay flowx-ecom-layer--dropdown fixed overflow-hidden rounded-[8px] border border-white/14 bg-[#080808] p-4 shadow-[0_18px_48px_rgba(0,0,0,0.6)]"
      style={{
        left: templatePreview.left,
        top: templatePreview.top,
        width: templatePreview.width,
        maxHeight: templatePreview.maxHeight,
        zIndex: ECOM_LAYERS.dropdown,
      }}
    >
      <div className="mb-2 break-words text-sm font-medium leading-5 text-white">
        {templatePreview.template.title}
      </div>
      <div className="max-h-[280px] overflow-y-auto whitespace-pre-wrap break-words text-[12px] font-light leading-[20px] text-white [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-track]:bg-transparent">
        {templatePreview.template.content}
      </div>
    </div>
  ) : null;

  return (
    <div className="relative">
      <EcomDropdown
        open={open}
        onOpenChange={setOpen}
        popup={panel}
        disabled={disabled}
        placement="rightBottom"
      >
        <button
          type="button"
          disabled={disabled}
          className="inline-flex h-6 cursor-pointer items-center gap-1 rounded-[4px] border border-white/12 bg-[#2D2D2D] px-2 text-[10px] font-medium text-white/85 transition-colors hover:border-white/20 hover:bg-[#333] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PromptTemplateIcon className="h-3 w-3 shrink-0" />
          <span>提示词模版</span>
        </button>
      </EcomDropdown>
      {previewPanel && typeof document !== "undefined" ? createPortal(previewPanel, document.body) : null}
      <CustomPromptTemplateModal
        open={createOpen}
        mode={editingTemplate ? "edit" : "create"}
        draft={createDraft}
        saving={createSaving}
        error={createError}
        onDraftChange={setCreateDraft}
        onClose={handleCloseCreate}
        onSave={handleSaveCreate}
      />
      <DeletePromptTemplateConfirmModal
        template={pendingDeleteTemplate}
        deleting={deletingTemplateID === pendingDeleteTemplate?.id}
        onClose={handleCloseDeleteTemplate}
        onConfirm={handleConfirmDeleteTemplate}
      />
    </div>
  );
}

function ReversePromptFailureBanner({
  failure,
  onDismiss,
  anchorRef,
}: {
  failure?: ReversePromptFailureAction;
  onDismiss?: () => void;
  anchorRef: RefObject<HTMLElement | null>;
}) {
  const [position, setPosition] = useState<{ left: number; top: number; width: number } | null>(null);

  useEffect(() => {
    if (!failure || typeof window === "undefined") return;

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(364, Math.max(280, window.innerWidth - 16));
      const left = Math.min(rect.left + 58, window.innerWidth - width - 8);
      setPosition({
        left: Math.max(8, left),
        top: Math.max(8, rect.top),
        width,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef, failure]);

  if (!failure) return null;

  const popup = (
    <div
      className="fixed z-[10000] flex min-h-16 items-start gap-1.5 rounded-[4px] bg-[#CA4242] px-5 py-2.5 text-[15px] leading-5 text-white shadow-[0_12px_28px_rgba(0,0,0,0.32)]"
      style={position ? { left: position.left, top: position.top, width: position.width } : { left: -9999, top: -9999, width: 364 }}
    >
      <div className="min-w-0 flex-1">
        <span>提示词生成失败，您可选择 </span>
        <button
          type="button"
          onClick={failure.onRetry}
          className="mx-1 inline-flex h-[22px] items-center rounded-[4px] bg-[#F6F6F6] px-2 text-sm leading-[22px] text-[#525252] hover:bg-white"
        >
          再次反推
        </button>
        <span> 或自行编辑 提示词。</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="关闭提示词生成失败提示"
        className="-mr-1 mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-white/90 hover:bg-white/12 hover:text-white"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(popup, document.body) : null;
}

function PromptField({
  workflowKey,
  comp,
  value,
  onChange,
  textareaRef,
  onFocus,
  imageReferenceUrls = [],
  disabled = false,
  reverseGenerating,
  reverseDisplayReference,
  reverseFailure,
  onReverseFailureDismiss,
  onRegenerate,
}: {
  workflowKey: string;
  comp: ComponentConfig; value: unknown; onChange: (v: string) => void;
  textareaRef?: Ref<HTMLTextAreaElement>;
  onFocus?: () => void;
  imageReferenceUrls?: string[];
  disabled?: boolean;
  reverseGenerating?: boolean;
  reverseDisplayReference?: string;
  reverseFailure?: ReversePromptFailureAction;
  onReverseFailureDismiss?: () => void;
  onRegenerate?: () => void;
}) {
  const normalizedValue = normalizePromptFieldValue(value);
  const placeholder = typeof comp.config?.placeholder === "string" ? comp.config.placeholder : "";
  const textareaWrapRef = useRef<HTMLDivElement>(null);
  const innerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const { showTemplateToolbar } = resolvePromptPresentation(
    comp.config?.prompt_template_enabled === true,
    comp.config?.prompt_large_frame === true,
  );
  const hasReverseDisplayReference = !!cleanString(reverseDisplayReference);
  const shouldShowReverseGeneratingOverlay = !!reverseGenerating && !hasReverseDisplayReference;
  const setTextareaRef = useCallback((node: HTMLTextAreaElement | null) => {
    innerTextareaRef.current = node;
    if (typeof textareaRef === "function") {
      textareaRef(node);
    } else if (textareaRef) {
      (textareaRef as MutableRefObject<HTMLTextAreaElement | null>).current = node;
    }
  }, [textareaRef]);

  return (
    <div ref={fieldRef}>
      <FieldHeader label={comp.label} required={comp.is_required} helpTip={getComponentHelpTip(comp)} />
      <ReversePromptFailureBanner failure={reverseFailure} onDismiss={onReverseFailureDismiss} anchorRef={fieldRef} />
      <div
        className="overflow-hidden rounded-[10px] border border-white/12 bg-[#191919]"
        ref={textareaWrapRef}
      >
        <div className="relative">
          <ConfiguredPromptTextarea
            ref={setTextareaRef}
            data-ecom-prompt-input
            value={normalizedValue}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            placeholder={placeholder}
            minHeight={285}
            maxHeight={320}
            disabled={disabled || shouldShowReverseGeneratingOverlay}
            className={`block w-full bg-transparent px-4 py-3 text-sm leading-6 text-white placeholder:text-white/36
                       outline-hidden disabled:cursor-wait disabled:text-white/25
                       [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-track]:bg-transparent`}
          />
          {imageReferenceUrls.length > 0 && (
            <PromptImageReferenceOverlay
              value={normalizedValue}
              imageUrls={imageReferenceUrls}
              textareaRef={innerTextareaRef}
              onChange={onChange}
              disabled={disabled || shouldShowReverseGeneratingOverlay}
              mirrorClassName="px-4 py-3 text-sm leading-6"
            />
          )}
          <PromptImageMentionMenu
            value={normalizedValue}
            imageUrls={imageReferenceUrls}
            textareaRef={innerTextareaRef}
            mirrorClassName="px-4 py-3 text-sm leading-6"
            onChange={onChange}
            disabled={disabled || shouldShowReverseGeneratingOverlay}
          />
          {shouldShowReverseGeneratingOverlay && (
            <div className="pointer-events-none absolute inset-0 bg-[#191919] px-4 py-4 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white/80" />
                <span>提示词生成中...</span>
              </div>
            </div>
          )}
        </div>
        {showTemplateToolbar && (
          <div className="flex h-11 items-center justify-end gap-1 border-t border-white/10 bg-[#191919] px-3">
            {!!normalizedValue.trim() && !reverseGenerating && onRegenerate && (
              <button
                type="button"
                onClick={onRegenerate}
                className="inline-flex h-6 w-6 items-center justify-center rounded-[4px] text-white/45 transition-colors hover:bg-white/10 hover:text-white"
                title="重新生成提示词"
                aria-label="重新生成提示词"
              >
                <RefreshCw size={14} />
              </button>
            )}
            <PromptTemplatePicker workflowKey={workflowKey} boundParamKey={comp.coze_param_key} disabled={!!reverseGenerating} onApply={onChange} />
          </div>
        )}
      </div>
    </div>
  );
}

function ModelGalleryField({
  comp,
  value,
  onOpen,
  onSelect,
  onClear,
}: {
  comp: ComponentConfig;
  value: ModelAsset | null;
  onOpen: () => void;
  onSelect: (asset: ModelAsset) => void;
  onClear: () => void;
}) {
  const [hoverPreview, setHoverPreview] = useState<{ asset: ModelAsset; x: number; y: number } | null>(null);
  const inlineAssets = useMemo(() => {
    const assets = comp.data?.categories?.flatMap((category) => (category.assets as ModelAsset[]) || []) || [];
    const unique = new Map<number, ModelAsset>();
    assets.forEach((asset) => {
      if (asset?.id && !unique.has(asset.id)) unique.set(asset.id, asset);
    });
    return Array.from(unique.values()).slice(0, 10);
  }, [comp.data?.categories]);

  const handleMouseEnter = useCallback((e: React.MouseEvent, asset = value) => {
    if (!asset) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const previewH = 320;
    const halfH = previewH / 2;
    let y = rect.top + rect.height / 2;
    y = Math.max(halfH + 8, Math.min(y, window.innerHeight - halfH - 8));
    setHoverPreview({
      asset,
      x: rect.right + 8,
      y,
    });
  }, [value]);

  const handleMouseLeave = useCallback(() => {
    setHoverPreview(null);
  }, []);

  return (
    <div>
      <FieldHeader
        label={comp.label}
        required={comp.is_required}
        helpTip={getComponentHelpTip(comp)}
        action={(
          <button
            type="button"
            onClick={onOpen}
            className="flex h-7 items-center gap-0.5 rounded-[6px] border border-[#a8792a]/60 bg-[#42331E] px-2.5
                       text-xs font-medium text-[#d6a95a] transition-all hover:border-[#d6a95a]/80 hover:bg-[#3a2a18] hover:text-[#f0c978]"
            aria-label="打开模特库"
          >
            <span>模特库</span>
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          </button>
        )}
      />
      <div className="flex flex-col gap-2">
        <div className="group relative h-[108px] w-[108px]">
          <button
            type="button"
            onClick={onOpen}
            className={`h-full w-full overflow-hidden rounded-[10px] border
                     flex items-center justify-center transition-all hover:border-white/45 hover:bg-white/8
            ${value ? "border-white/18 bg-black/30" : "border-dashed border-white/25 bg-white/5"}`}
            aria-label="打开模特库"
          >
            {value ? (
              <ModelAssetImage asset={value} alt={value.name} className="h-full w-full object-cover" />
            ) : (
              <svg className="w-9 h-9 text-white/80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            )}
          </button>
          {value && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className="absolute right-1 top-1 h-5 w-5 rounded-full bg-black/70 border border-white/20
                         flex items-center justify-center opacity-0 transition-all hover:bg-red-500/80 group-hover:opacity-100 group-focus-within:opacity-100"
              aria-label="清除模特"
            >
              <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
        <div className="grid grid-cols-5 gap-2">
          {inlineAssets.map((asset) => {
            const selected = isSameGalleryAsset(value, asset);
            return (
              <button
                key={asset.id}
                type="button"
                onClick={() => onSelect(asset)}
                onMouseEnter={(e) => handleMouseEnter(e, asset)}
                onMouseLeave={handleMouseLeave}
                className={`relative h-[60px] w-full overflow-hidden rounded-[6px] border transition-all
                  ${selected ? "border-transparent" : "border-transparent hover:border-white/35"}`}
                aria-pressed={selected}
                title={asset.name}
              >
                <ModelAssetImage asset={asset} alt={asset.name} className="h-full w-full object-cover" />
                {!selected && (
                  <span className="pointer-events-none absolute inset-0 bg-black/45" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      </div>
      {value && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="truncate text-xs text-white/60">{value.name}</span>
          <button type="button" onClick={onClear} className="text-xs text-white/35 transition-colors hover:text-red-300">
            清除
          </button>
        </div>
      )}

      {hoverPreview && typeof document !== "undefined" && createPortal(
        <div
          className="fixed z-9999 pointer-events-none"
          style={{
            left: hoverPreview.x,
            top: hoverPreview.y,
            transform: "translateY(-50%)",
          }}
        >
          <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/70 bg-black/90"
            style={{ maxWidth: "240px", maxHeight: "320px" }}>
            <ModelAssetImage
              asset={hoverPreview.asset}
              alt={hoverPreview.asset.name || "模特预览"}
              className="block max-w-[240px] max-h-80 object-contain"
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function BackgroundGalleryField({
  comp,
  value,
  onOpen,
  onClear,
}: {
  comp: ComponentConfig;
  value: BackgroundAsset | null;
  onOpen: () => void;
  onClear: () => void;
}) {
  return (
    <div>
      <FieldHeader label={comp.label} required={comp.is_required} helpTip={getComponentHelpTip(comp)} />
      <div className="flex items-center gap-1.5">
        <div className="relative w-[42px] h-[42px] shrink-0">
          <button
            onClick={onOpen}
            className="w-full h-full rounded-md overflow-hidden border border-white/30 transition-all hover:border-white/60 flex items-center justify-center"
            style={{ background: value ? "none" : "linear-gradient(180deg, rgba(129,129,129,0.48) 0%, rgba(255,255,255,0.432) 100%)" }}
          >
            {value ? (
              <img src={value.thumbnail_url || value.image_url} alt={value.name} className="w-full h-full object-cover" decoding="async" />
            ) : (
              <svg className="w-5 h-5 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M3 15l5-5 4 4 4-4 5 5" />
              </svg>
            )}
          </button>
          {value && (
            <button
              onClick={onClear}
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-black/70 border border-white/20 flex items-center justify-center hover:bg-red-500/80 transition-colors z-10"
            >
              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
        {!value && (
          <span className="text-[10px] text-white/50 whitespace-nowrap">选择背景</span>
        )}
        {value && (
          <span className="text-[10px] text-white/70 truncate">{value.name}</span>
        )}
      </div>
    </div>
  );
}

function ReferenceImageField({
  comp,
  value,
  onChange,
  onImageRefClick,
  onImageRemove,
  onOpen,
  referenceResult,
  onClearResult,
  headerAction,
  onHoverPreviewChange,
}: {
  comp: ComponentConfig;
  value: string;
  onChange: (v: string) => void;
  onImageRefClick?: () => void;
  onImageRemove?: () => void;
  onOpen: () => void;
  referenceResult?: { referenceKey: string; targetParamKey: string; resultUrl: string; weight: number } | null;
  onClearResult?: () => void;
  headerAction?: ReactNode;
  onHoverPreviewChange?: (preview: ImageHoverPreview | null) => void;
}) {
  const feedback = useEcomFeedback();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [serverPreviewUrl, setServerPreviewUrl] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [isDraggingRef, setIsDraggingRef] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Use local blob URL as fallback when server URL fails to load
  const displayUrl = serverPreviewUrl || value || localPreviewUrl || "";
  const cropSourceUrl = editImageUrl || localPreviewUrl || value || displayUrl;

  const handleFile = useCallback(async (file: File) => {
    if (!isImageFile(file)) return;
    setUploading(true);
    setUploadError(null);
    const localUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(localUrl);
    onChange(localUrl);
    try {
      const res = await ecomImageApi.uploadProductImage(file);
      const uploadedUrl = getUploadedImageUrl(res.data);
      if (uploadedUrl) {
        onChange(uploadedUrl);
        setServerPreviewUrl(getUploadedPreviewUrl(res.data));
        setEditImageUrl(getUploadedEditUrl(res.data));
      }
    } catch {
      setUploadError("上传失败，请重试");
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingRef(false);
    const draggedImage = getInternalImageDragData(e.dataTransfer);
    if (draggedImage) {
      onChange(draggedImage.url);
      setServerPreviewUrl(draggedImage.previewUrl || "");
      setEditImageUrl(draggedImage.editUrl || "");
      setLocalPreviewUrl(draggedImage.localFallbackUrl || null);
      setUploadError(null);
      onClearResult?.();
      return;
    }
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile, onChange, onClearResult]);

  const handleClear = () => {
    onHoverPreviewChange?.(null);
    onImageRemove?.();
    onChange("");
    setLocalPreviewUrl(null);
    setServerPreviewUrl("");
    setEditImageUrl("");
    setUploadError(null);
    onClearResult?.();
  };

  // When server URL fails to load, fall back to local blob preview
  const handleImageError = () => {
    if (localPreviewUrl && value !== localPreviewUrl) {
      onChange(localPreviewUrl);
    }
  };

  const handleHoverPreviewEnter = useCallback(() => {
    const previewUrl = resolveHoverPreviewUrl({
      url: value || displayUrl,
      previewUrl: displayUrl || serverPreviewUrl,
      editUrl: editImageUrl,
      localFallbackUrl: localPreviewUrl,
    });
    if (previewUrl) {
      onHoverPreviewChange?.({ url: previewUrl, alt: comp.label || "上传图片" });
    }
  }, [comp.label, displayUrl, editImageUrl, localPreviewUrl, onHoverPreviewChange, serverPreviewUrl, value]);

  const handleHoverPreviewLeave = useCallback(() => {
    onHoverPreviewChange?.(null);
  }, [onHoverPreviewChange]);

  return (
    <div>
      <FieldHeader label={comp.label} required={comp.is_required} action={headerAction} helpTip={getComponentHelpTip(comp)} />
      {displayUrl ? (
        <OrignUpload
          status={uploadError ? "error" : uploading ? "uploading" : isDraggingRef ? "drag-over" : "ready"}
          onPasteFiles={files => { if (files[0]) void handleFile(files[0]); }}
          pasteDisabled={uploading}
          draggable
          className={`relative w-full aspect-4/3 group transition-all ${isDraggingRef ? "ring-2 ring-white/50" : ""}`}
          onDrop={handleDrop}
          onDragStart={(e) => {
            setInternalImageDragData(e, {
              url: value || displayUrl,
              previewUrl: serverPreviewUrl || displayUrl,
              editUrl: editImageUrl,
              localFallbackUrl: localPreviewUrl || undefined,
            });
          }}
          onDragOver={(e) => { e.preventDefault(); setIsDraggingRef(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsDraggingRef(false); }}
          onMouseEnter={handleHoverPreviewEnter}
          onMouseLeave={handleHoverPreviewLeave}
          onPointerEnter={handleHoverPreviewEnter}
          onPointerLeave={handleHoverPreviewLeave}
        >
          <img src={displayUrl} alt={comp.label} className="w-full h-full rounded-[2px] object-contain bg-[#141414]" decoding="async" onError={handleImageError} />
          {onImageRefClick && !uploading && (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                onImageRefClick();
              }}
              className="absolute bottom-2 left-2 z-20 rounded bg-black/70 px-2 py-1 text-[10px] font-medium text-white/90 hover:bg-black/85"
              title="点击插入 @图片1 到提示词"
            >
              @图片1
            </button>
          )}
          {isDraggingRef && (
            <div className="absolute inset-0 bg-white/10 flex items-center justify-center pointer-events-none z-10">
              <span className="text-sm text-white font-medium bg-black/50 px-3 py-1.5 rounded-lg">释放以替换图片</span>
            </div>
          )}
          <div
            data-orign-upload-actions
            className="pointer-events-none absolute inset-0 z-20"
          >
            <EcomIconAction
              data-orign-upload-remove
              onClick={handleClear}
              className="pointer-events-auto absolute top-2 right-2 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
              aria-label={`删除${comp.label || "上传图片"}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </EcomIconAction>
            {!uploading && (
              <EcomIconAction
                data-orign-upload-edit
                onClick={(e) => { e.stopPropagation(); setShowCropDialog(true); }}
                className="pointer-events-auto absolute right-2 bottom-2 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                aria-label={`裁剪${comp.label || "上传图片"}`}
                title="裁剪图片"
              >
                <CropIcon />
              </EcomIconAction>
            )}
          </div>
          {/* 参考设置按钮 - 底部居中 */}
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-linear-to-t from-black/60 to-transparent
                          flex items-center justify-center">
            <button
              onClick={onOpen}
              className="h-7 px-3 flex items-center gap-1.5 rounded-full backdrop-blur-glass border border-white/25
                         hover:border-white/40 transition-all text-[11px] font-medium text-white"
              style={{ background: "linear-gradient(180deg, rgba(129, 129, 129, 0.48) 0%, rgba(255, 255, 255, 0.25) 100%)" }}
            >
              <svg className="w-3 h-3 text-white/90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              参考设置
            </button>
          </div>
          {/* 上传中遮罩 */}
          {uploading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
              <div className="w-6 h-6 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
            </div>
          )}
          {/* 预处理结果标记 */}
          {referenceResult && (
            <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-[#D24DDA]/80 backdrop-blur-xs">
              <span className="text-[10px] text-white font-medium">已配置参考</span>
            </div>
          )}
          {/* 上传失败提示 */}
          {uploadError && (
            <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-red-500/80 backdrop-blur-xs">
              <span className="text-[10px] text-white font-medium">{uploadError}</span>
            </div>
          )}
        </OrignUpload>
      ) : (
        <ImageSourceMenu
          onLocalFile={handleFile}
          onInspirationSelect={(url) => { onChange(url); setServerPreviewUrl(""); setEditImageUrl(""); }}
          disabled={uploading}
        >
          <EcomUploadEmptyState
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDraggingRef(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDraggingRef(false); }}
            uploading={uploading}
            dragOver={isDraggingRef}
            className="w-full aspect-4/3"
          />
        </ImageSourceMenu>
      )}
      {showCropDialog && cropSourceUrl && (
        <ImageCropDialog
          imageUrl={cropSourceUrl}
          onCancel={() => setShowCropDialog(false)}
          onSave={async (croppedDataUrl) => {
            setShowCropDialog(false);
            try {
              const blob = await fetch(croppedDataUrl).then(r => r.blob());
              const file = new File([blob], `cropped-${Date.now()}.png`, { type: "image/png" });
              const res = await ecomImageApi.uploadProductImage(file);
              const uploadedUrl = getUploadedImageUrl(res.data);
              if (uploadedUrl) {
                onChange(uploadedUrl);
                setServerPreviewUrl(getUploadedPreviewUrl(res.data));
                setEditImageUrl(getUploadedEditUrl(res.data));
              } else {
                feedback.error({ code: "E5003", fallback: "upload" });
              }
            } catch { feedback.error({ code: "E5003", fallback: "upload" }); }
          }}
        />
      )}
      <input ref={inputRef} type="file" accept={IMAGE_ACCEPT} className="absolute w-0 h-0 opacity-0 overflow-hidden pointer-events-none" tabIndex={-1} aria-hidden="true" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }} />
    </div>
  );
}

function normalizeRatioResolutions(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const normalized: Record<string, string[]> = {};
  Object.entries(value as Record<string, unknown>).forEach(([ratio, resolutions]) => {
    if (!RATIO_OPTIONS.includes(ratio)) return;
    normalized[ratio] = Array.isArray(resolutions)
      ? Array.from(new Set(resolutions.map(String).map(v => v.trim()).filter(Boolean)))
      : [];
  });
  return normalized;
}

function RatioField({
  comp,
  value,
  cropSize,
  onChange,
  resolutionEmbed,
}: {
  comp: ComponentConfig;
  value: unknown;
  cropSize: unknown;
  onChange: (next: { ratio: string | null; cropSize: string | null }) => void;
  resolutionEmbed?: { label: string; options: string[]; value: string; onChange: (v: string) => void } | null;
}) {
  const ratioResolutions = useMemo(
    () => normalizeRatioResolutions(comp.config?.ratio_resolutions),
    [comp.config],
  );
  const ratioOptions = useMemo(
    () => resolveRatioSelectorOptions(comp.config),
    [comp.config],
  );
  const allowAuto = comp.config?.allow_auto !== false;
  const configuredDefault = !allowAuto && typeof comp.config?.default_value === "string" && ratioOptions.includes(comp.config.default_value)
    ? comp.config.default_value : getRatioSelectorDefault(comp.config?.default_value);
  const defaultRatio = !allowAuto && !ratioOptions.includes(configuredDefault)
    ? ratioOptions[0] ?? '' : configuredDefault;
  const externalSelection = useMemo(() => {
    const selection = resolveRatioSelectorExternalSelection({ value, cropSize, defaultRatio, ratioResolutions });
    if (!allowAuto && defaultRatio && !ratioOptions.includes(selection.selectedRatio)) {
      return { selectedRatio: defaultRatio, selectedCropSize: '', submitMode: 'ratio' as const, shouldSyncDefault: true };
    }
    return selection;
  }, [allowAuto, cropSize, defaultRatio, ratioOptions, ratioResolutions, value]);

  const [selectedRatio, setSelectedRatio] = useState(externalSelection.selectedRatio);
  const [submitMode, setSubmitMode] = useState<"ratio" | "resolution">(externalSelection.submitMode);
  const [selectedCropSize, setSelectedCropSize] = useState(externalSelection.selectedCropSize);
  const defaultSyncKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setSelectedRatio(externalSelection.selectedRatio);
    setSubmitMode(externalSelection.submitMode);
    setSelectedCropSize(externalSelection.selectedCropSize);
  }, [externalSelection]);

  useEffect(() => {
    if (externalSelection.shouldSyncDefault) {
      const syncKey = `${comp.coze_param_key}:${defaultRatio}`;
      if (defaultSyncKeyRef.current !== syncKey) {
        defaultSyncKeyRef.current = syncKey;
        onChange({ ratio: defaultRatio === "auto" ? null : defaultRatio, cropSize: null });
      }
      return;
    }
    defaultSyncKeyRef.current = null;
  }, [comp.coze_param_key, defaultRatio, externalSelection.shouldSyncDefault, onChange]);

  // Initialize resolutionEmbed value when embed mode is active and no value yet
  useEffect(() => {
    if (/^\d+:\d+$/.test(typeof value === "string" ? value : "")) {
      const normalized = normalizeRatioSelectorSelection(ratioOptions, value, cropSize);
      if (
        normalized.ratio !== value ||
        normalized.cropSize !== (cropSize || null)
      ) {
        onChange(normalized);
      }
    }
  }, [cropSize, onChange, ratioOptions, value]);

  useEffect(() => {
    if (resolutionEmbed && !resolutionEmbed.value && resolutionEmbed.options.length > 0) {
      resolutionEmbed.onChange(resolutionEmbed.options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolutionEmbed?.options, resolutionEmbed?.value]);

  const submitRatio = (ratio: string) => {
    setSelectedRatio(ratio);
    setSelectedCropSize("");
    setSubmitMode("ratio");
    onChange({ ratio: ratio === "auto" ? null : ratio, cropSize: null });
  };

  const submitResolution = (ratio: string, resolution: string) => {
    if (!resolution) return;
    setSelectedRatio(ratio);
    setSelectedCropSize(resolution);
    setSubmitMode("resolution");
    onChange({ ratio, cropSize: resolution });
  };

  const collapsedSummary = getCollapsedRatioSummary({
    selectedRatio,
    cropSize: submitMode === "resolution" ? selectedCropSize : null,
    resolutionTier: resolutionEmbed
      ? (resolutionEmbed.value || resolutionEmbed.options[0] || "")
      : null,
  });

  return (
    <div>
      <FieldHeader label={comp.label} required={comp.is_required} helpTip={getComponentHelpTip(comp)} />
      <EcomRadioSelect
        label={comp.label}
        summary={collapsedSummary}
        ratios={ratioOptions}
        selectedRatio={selectedRatio}
        selectedCropSize={selectedCropSize}
        submitMode={submitMode}
        ratioResolutions={ratioResolutions}
        embeddedResolution={resolutionEmbed ? {
          label: resolutionEmbed.label || "分辨率",
          options: resolutionEmbed.options,
          value: resolutionEmbed.value || resolutionEmbed.options[0] || "",
          onValueChange: resolutionEmbed.onChange,
        } : undefined}
        autoTitle={
          typeof comp.config?.auto_anchor_param_key === "string" && comp.config.auto_anchor_param_key
            ? "auto（根据上传图片智能识别比例）"
            : undefined
        }
        onRatioChange={submitRatio}
        onResolutionChange={submitResolution}
      />
    </div>
  );
}

const DEFAULT_RESOLUTION_OPTIONS = ["1k", "2k", "3k", "4k"];

function ResolutionField({ comp, value, onChange }: { comp: ComponentConfig; value: string; onChange: (v: string) => void }) {
  const configOptions = comp.config?.options as Array<{ label: string; value: string }> | undefined;
  const options = configOptions?.length
    ? configOptions.map(o => o.value)
    : DEFAULT_RESOLUTION_OPTIONS;

  const configDefault = (comp.config?.default_value as string) || "";
  const initialValue = configDefault && options.includes(configDefault) ? configDefault : options[0];
  const activeValue = value || initialValue;

  useEffect(() => {
    if (!value && initialValue) {
      onChange(initialValue);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <FieldHeader label={comp.label} required={comp.is_required} helpTip={getComponentHelpTip(comp)} />
      <div className="flex h-11 rounded-[12px] bg-[#242424] p-1">
        {options.map((r) => (
          <button
            key={r}
            onClick={() => onChange(r)}
            className={`
              h-full flex-1 cursor-pointer rounded-[8px] text-sm font-medium transition-all disabled:cursor-not-allowed
              ${activeValue === r
                ? "bg-[#3A3A3A] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                : "text-white/80 hover:bg-white/6 hover:text-white"
              }
            `}
          >
            {r}
          </button>
        ))}
      </div>
    </div>
  );
}

function SingleImageField({
  comp,
  value,
  onChange,
  onImageRefClick,
  onImageRemove,
  headerAction,
  onHoverPreviewChange,
}: {
  comp: ComponentConfig;
  value: string;
  onChange: (v: string) => void;
  onImageRefClick?: () => void;
  onImageRemove?: () => void;
  headerAction?: ReactNode;
  onHoverPreviewChange?: (preview: ImageHoverPreview | null) => void;
}) {
  const feedback = useEcomFeedback();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [editUrls, setEditUrls] = useState<Record<string, string>>({});

  const handleFile = useCallback(async (file: File) => {
    if (!isImageFile(file)) return;
    setUploading(true);
    try {
      const res = await ecomImageApi.uploadProductImage(file);
      const uploadedUrl = getUploadedImageUrl(res.data);
      if (uploadedUrl) {
        onChange(uploadedUrl);
        setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedPreviewUrl(res.data)));
        setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
      }
    } catch {
      // upload failed silently
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const draggedImage = getInternalImageDragData(e.dataTransfer);
    if (draggedImage) {
      onChange(draggedImage.url);
      setPreviewUrls(prev => withMappedUrl(prev, draggedImage.url, draggedImage.previewUrl));
      setEditUrls(prev => withMappedUrl(prev, draggedImage.url, draggedImage.editUrl));
      return;
    }
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile, onChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleInspirationSelect = useCallback((url: string) => {
    onChange(url);
  }, [onChange]);

  const handleClear = useCallback(() => {
    const current = value;
    onHoverPreviewChange?.(null);
    onImageRemove?.();
    onChange("");
    setPreviewUrls(prev => {
      const next = { ...prev };
      delete next[current];
      return next;
    });
    setEditUrls(prev => {
      const next = { ...prev };
      delete next[current];
      return next;
    });
  }, [onChange, onHoverPreviewChange, onImageRemove, value]);

  const handleHoverPreviewEnter = useCallback(() => {
    const previewUrl = resolveHoverPreviewUrl({
      url: value,
      previewUrl: previewUrls[value],
      editUrl: editUrls[value],
    });
    if (previewUrl) {
      onHoverPreviewChange?.({ url: previewUrl, alt: comp.label || "上传图片" });
    }
  }, [comp.label, editUrls, onHoverPreviewChange, previewUrls, value]);

  const handleHoverPreviewLeave = useCallback(() => {
    onHoverPreviewChange?.(null);
  }, [onHoverPreviewChange]);

  return (
    <div>
      <FieldHeader label={comp.label} required={comp.is_required} action={headerAction} helpTip={getComponentHelpTip(comp)} />
      {value ? (
        <OrignUpload
          status={uploading ? "uploading" : dragOver ? "drag-over" : "ready"}
          onPasteFiles={files => { if (files[0]) void handleFile(files[0]); }}
          pasteDisabled={uploading}
          draggable
          className={`relative w-full aspect-4/3 group transition-all ${dragOver ? "ring-2 ring-white/50" : ""}`}
          onDrop={handleDrop}
          onDragStart={(e) => {
            setInternalImageDragData(e, {
              url: value,
              previewUrl: previewUrls[value],
              editUrl: editUrls[value],
            });
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onMouseEnter={handleHoverPreviewEnter}
          onMouseLeave={handleHoverPreviewLeave}
          onPointerEnter={handleHoverPreviewEnter}
          onPointerLeave={handleHoverPreviewLeave}
        >
          <UploadedPreviewImage
            src={value}
            previewSrc={previewUrls[value]}
            alt={comp.label}
            className="w-full h-full rounded-[2px] object-contain bg-[#141414]"
          />
          {onImageRefClick && !uploading && (
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                onImageRefClick();
              }}
              className="absolute bottom-2 left-2 z-20 rounded bg-black/70 px-2 py-1 text-[10px] font-medium text-white/90 hover:bg-black/85"
              title="点击插入 @图片1 到提示词"
            >
              @图片1
            </button>
          )}
          {dragOver && (
            <div className="absolute inset-0 bg-white/10 flex items-center justify-center pointer-events-none z-10">
              <span className="text-sm text-white font-medium bg-black/50 px-3 py-1.5 rounded-lg">释放以替换图片</span>
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
              <div className="w-6 h-6 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
            </div>
          )}
          <div
            data-orign-upload-actions
            className="pointer-events-none absolute inset-0 z-20"
          >
            <EcomIconAction
              data-orign-upload-remove
              onClick={handleClear}
              className="pointer-events-auto absolute top-2 right-2 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
              aria-label={`删除${comp.label || "上传图片"}`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </EcomIconAction>
            {!uploading && (
              <EcomIconAction
                data-orign-upload-edit
                onClick={(e) => { e.stopPropagation(); setShowCropDialog(true); }}
                className="pointer-events-auto absolute right-2 bottom-2 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                aria-label={`裁剪${comp.label || "上传图片"}`}
                title="裁剪图片"
              >
                <CropIcon />
              </EcomIconAction>
            )}
          </div>
        </OrignUpload>
      ) : (
        <ImageSourceMenu
          onLocalFile={handleFile}
          onInspirationSelect={handleInspirationSelect}
          disabled={uploading}
        >
          <EcomUploadEmptyState
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            uploading={uploading}
            dragOver={dragOver}
            className="w-full aspect-4/3"
          />
        </ImageSourceMenu>
      )}
      {showCropDialog && value && (
        <ImageCropDialog
          imageUrl={editUrls[value] || previewUrls[value] || value}
          onCancel={() => setShowCropDialog(false)}
          onSave={async (croppedDataUrl) => {
            setShowCropDialog(false);
            try {
              const blob = await fetch(croppedDataUrl).then(r => r.blob());
              const file = new File([blob], `cropped-${Date.now()}.png`, { type: "image/png" });
              const res = await ecomImageApi.uploadProductImage(file);
              const uploadedUrl = getUploadedImageUrl(res.data);
              if (uploadedUrl) {
                onChange(uploadedUrl);
                setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedPreviewUrl(res.data)));
                setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
              } else {
                feedback.error({ code: "E5003", fallback: "upload" });
              }
            } catch { feedback.error({ code: "E5003", fallback: "upload" }); }
          }}
        />
      )}
    </div>
  );
}

function MultiImageField({
  comp,
  value,
  onChange,
  onImageRefClick,
  onImageReorder,
  onImageRemove,
  notifyUploading,
  headerAction,
  onHoverPreviewChange,
}: {
  comp: ComponentConfig;
  value: string[];
  onChange: (v: string[]) => void;
  onImageRefClick?: (index: number) => void;
  onImageReorder?: (fromIndex: number, toIndex: number, imageCount: number) => void;
  onImageRemove?: (index: number, imageCount: number) => void;
  notifyUploading?: (delta: number) => void;
  headerAction?: ReactNode;
  onHoverPreviewChange?: (preview: ImageHoverPreview | null) => void;
}) {
  const feedback = useEcomFeedback();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [hoverPreview, setHoverPreview] = useState<{ url: string; x: number; y: number } | null>(null);
  const [reorderDragIdx, setReorderDragIdx] = useState<number | null>(null);
  const [reorderOverIdx, setReorderOverIdx] = useState<number | null>(null);
  const [uploadStates, setUploadStates] = useState<Record<string, "uploading" | "error">>({});
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [editUrls, setEditUrls] = useState<Record<string, string>>({});
  const [localFallbackUrls, setLocalFallbackUrls] = useState<Record<string, string>>({});
  const [cropImageIndex, setCropImageIndex] = useState<number | null>(null);

  const valueRef = useRef(value);
  valueRef.current = value;

  const handleThumbMouseEnter = useCallback((e: React.MouseEvent, url: string) => {
    if (reorderDragIdx !== null) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const previewUrl = resolveHoverPreviewUrl({
      url,
      previewUrl: previewUrls[url],
      editUrl: editUrls[url],
      localFallbackUrl: localFallbackUrls[url],
    });
    setHoverPreview({ url: previewUrl || url, x: rect.left + rect.width / 2, y: rect.top });
    if (previewUrl) {
      onHoverPreviewChange?.({ url: previewUrl, alt: `${comp.label} ${valueRef.current.indexOf(url) + 1}` });
    }
  }, [comp.label, editUrls, localFallbackUrls, onHoverPreviewChange, previewUrls, reorderDragIdx]);

  const handleThumbMouseLeave = useCallback(() => {
    setHoverPreview(null);
    onHoverPreviewChange?.(null);
  }, [onHoverPreviewChange]);
  const MAX_IMAGES = (typeof comp.config?.max_images === "number" && comp.config.max_images > 0)
    ? comp.config.max_images
    : 5;

  const maxImagesRef = useRef(MAX_IMAGES);
  maxImagesRef.current = MAX_IMAGES;

  const handleFiles = useCallback(async (files: FileList) => {
    const imageFiles = Array.from(files).filter((f) => isImageFile(f));
    const remaining = maxImagesRef.current - valueRef.current.length;
    const toUpload = imageFiles.slice(0, remaining);
    if (toUpload.length === 0) return;

    const blobUrls = toUpload.map(f => URL.createObjectURL(f));
    const newValue = [...valueRef.current, ...blobUrls];
    onChange(newValue);

    const newStates: Record<string, "uploading" | "error"> = {};
    blobUrls.forEach(url => { newStates[url] = "uploading"; });
    setUploadStates(prev => ({ ...prev, ...newStates }));

    setUploading(true);
    notifyUploading?.(1);
    try {
      const compressed = await compressImagesForBatchUpload(toUpload, valueRef.current.length - blobUrls.length);
      await mapWithConcurrency(compressed, async (file, i) => {
        try {
          const res = await ecomImageApi.uploadProductImage(file, { skipCompression: true });
          const uploadedUrl = getUploadedImageUrl(res.data);
          if (uploadedUrl) {
            const previewUrl = getUploadedPreviewUrl(res.data);
            const updated = valueRef.current.map(url => url === blobUrls[i] ? uploadedUrl : url);
            onChange(updated);
            setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, previewUrl));
            setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
            setLocalFallbackUrls(prev => ({ ...prev, [uploadedUrl]: blobUrls[i] }));
            setUploadStates(prev => {
              const next = { ...prev };
              delete next[blobUrls[i]];
              return next;
            });
          } else {
            setUploadStates(prev => ({ ...prev, [blobUrls[i]]: "error" }));
          }
        } catch {
          setUploadStates(prev => ({ ...prev, [blobUrls[i]]: "error" }));
        }
      });
    } catch {
      blobUrls.forEach(url => {
        setUploadStates(prev => ({ ...prev, [url]: "error" }));
      });
    } finally {
      setUploading(false);
      notifyUploading?.(-1);
    }
  }, [onChange, notifyUploading]);

  const handleFile = useCallback(async (file: File) => {
    if (!isImageFile(file)) return;
    const fakeFileList = { 0: file, length: 1, item: (i: number) => i === 0 ? file : null } as unknown as FileList;
    await handleFiles(fakeFileList);
  }, [handleFiles]);

  const handleInspirationSelect = useCallback((url: string) => {
    if (valueRef.current.length < maxImagesRef.current) {
      onChange([...valueRef.current, url]);
    }
  }, [onChange]);

  const removeImage = useCallback((index: number) => {
    setHoverPreview(null);
    onHoverPreviewChange?.(null);
    const url = valueRef.current[index];
    revokeBlobUrl(url);
    revokeBlobUrl(localFallbackUrls[url]);
    setUploadStates(prev => {
      const next = { ...prev };
      delete next[url];
      return next;
    });
    setPreviewUrls(prev => {
      const next = { ...prev };
      delete next[url];
      return next;
    });
    setEditUrls(prev => {
      const next = { ...prev };
      delete next[url];
      return next;
    });
    setLocalFallbackUrls(prev => {
      const next = { ...prev };
      delete next[url];
      return next;
    });
    onImageRemove?.(index, valueRef.current.length);
    onChange(valueRef.current.filter((_, i) => i !== index));
  }, [localFallbackUrls, onChange, onHoverPreviewChange, onImageRemove]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const draggedImage = getInternalImageDragData(e.dataTransfer);
    if (draggedImage && valueRef.current.length < maxImagesRef.current) {
      onChange([...valueRef.current, draggedImage.url]);
      setPreviewUrls(prev => withMappedUrl(prev, draggedImage.url, draggedImage.previewUrl));
      setEditUrls(prev => withMappedUrl(prev, draggedImage.url, draggedImage.editUrl));
      if (draggedImage.localFallbackUrl) {
        setLocalFallbackUrls(prev => ({ ...prev, [draggedImage.url]: draggedImage.localFallbackUrl || "" }));
      }
      return;
    }
    if (e.dataTransfer.getData(REORDER_INDEX_MIME)) return;
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  }, [handleFiles, onChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!hasReorderDragData(e.dataTransfer)) {
      setDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleReorderDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(REORDER_INDEX_MIME, String(index));
    const url = valueRef.current[index];
    setInternalImageDragData(e, {
      url,
      previewUrl: previewUrls[url],
      editUrl: editUrls[url],
      localFallbackUrl: localFallbackUrls[url],
    });
    setReorderDragIdx(index);
    setHoverPreview(null);
  }, [editUrls, localFallbackUrls, previewUrls]);

  const handleReorderDragOver = useCallback((e: React.DragEvent, index: number) => {
    if (reorderDragIdx === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setReorderOverIdx(index);
  }, [reorderDragIdx]);

  const handleReorderDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const srcStr = e.dataTransfer.getData(REORDER_INDEX_MIME);
    if (!srcStr) return;
    const srcIndex = parseInt(srcStr, 10);
    if (isNaN(srcIndex) || srcIndex === targetIndex) {
      setReorderDragIdx(null);
      setReorderOverIdx(null);
      return;
    }
    const next = [...valueRef.current];
    const [moved] = next.splice(srcIndex, 1);
    next.splice(targetIndex, 0, moved);
    onImageReorder?.(srcIndex, targetIndex, valueRef.current.length);
    onChange(next);
    setReorderDragIdx(null);
    setReorderOverIdx(null);
  }, [onChange, onImageReorder]);

  const handleReorderDragEnd = useCallback(() => {
    setReorderDragIdx(null);
    setReorderOverIdx(null);
  }, []);

  const uploadedCount = value.filter(url => !uploadStates[url]).length;
  const uploadItems: Array<ImageUploadFieldItem & {
    url: string;
    previewUrl?: string;
    localFallbackUrl?: string;
  }> = value.map((url, index) => ({
    id: `img-${index}-${url.slice(-12)}`,
    src: url,
    alt: `${comp.label} ${index + 1}`,
    status: uploadStates[url] ?? "ready",
    url,
    previewUrl: previewUrls[url],
    localFallbackUrl: localFallbackUrls[url],
  }));

  return (
    <>
    <ImageUploadField
      items={uploadItems}
      onPasteFiles={handleFiles}
      pasteDisabled={uploading}
      maxItems={MAX_IMAGES}
      dragOver={dragOver}
      header={(
        <FieldHeader
          label={comp.label}
          required={comp.is_required}
          meta={(
            <span className="ml-1 text-white/30">
              ({uploading ? `${uploadedCount}+${value.length - uploadedCount}` : value.length}/{MAX_IMAGES})
            </span>
          )}
          action={headerAction}
        />
      )}
      emptyState={(
        <ImageSourceMenu
          onLocalFile={handleFile}
          onLocalFiles={handleFiles}
          onInspirationSelect={handleInspirationSelect}
          multiple
          disabled={uploading}
        >
          <EcomUploadEmptyState
            uploading={uploading}
            dragOver={dragOver}
            multiple
            formats={["JPG", "PNG"]}
            bgClassName="bg-[#141414]"
            className="w-full aspect-3/1"
          />
        </ImageSourceMenu>
      )}
      addTrigger={(
        <ImageSourceMenu
          onLocalFile={handleFile}
          onLocalFiles={handleFiles}
          onInspirationSelect={handleInspirationSelect}
          multiple
          disabled={uploading}
        >
          <ImageUploadAddTrigger uploading={uploading} />
        </ImageSourceMenu>
      )}
      capabilities={{
        activate: true,
        remove: true,
        edit: true,
        reorder: true,
        showNumber: true,
        showReference: true,
      }}
      labels={{
        edit: () => "裁剪图片",
      }}
      reorder={{
        activeIndex: reorderDragIdx,
        overIndex: reorderOverIdx,
        onDragStart: (event, _item, index) => handleReorderDragStart(event, index),
        onDragOver: (event, _item, index) => handleReorderDragOver(event, index),
        onDrop: (event, _item, index) => handleReorderDrop(event, index),
        onDragEnd: () => handleReorderDragEnd(),
      }}
      renderImage={(item) => (
        <UploadedPreviewImage
          src={item.url}
          previewSrc={item.previewUrl}
          localFallbackSrc={item.localFallbackUrl}
          alt={item.alt}
          className="w-full h-full object-contain bg-[#141414] pointer-events-none select-none"
        />
      )}
      loadingIndicator={<div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />}
      editIcon={<CropIcon data-dynamic-multi-image-crop-icon className="size-2.5 text-white" />}
      onItemActivate={(_item, index) => onImageRefClick?.(index)}
      onItemRemove={(_item, index) => removeImage(index)}
      onItemEdit={(_item, index) => setCropImageIndex(index)}
      onItemHoverChange={(item, _index, event) => {
        if (!item) {
          handleThumbMouseLeave();
          return;
        }
        if (event) handleThumbMouseEnter(event, item.url);
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    />

      {shouldRenderLocalUploadHoverPreview(!!hoverPreview, !!onHoverPreviewChange) && hoverPreview && (
        <AdaptiveImagePreview
          url={hoverPreview.url}
          alt="预览"
          anchorX={hoverPreview.x}
          anchorTop={hoverPreview.y}
        />
      )}

      {cropImageIndex !== null && value[cropImageIndex] && (
        <ImageCropDialog
          imageUrl={editUrls[value[cropImageIndex]] || localFallbackUrls[value[cropImageIndex]] || previewUrls[value[cropImageIndex]] || value[cropImageIndex]}
          onCancel={() => setCropImageIndex(null)}
          onSave={async (croppedDataUrl) => {
            setCropImageIndex(null);
            const idx = cropImageIndex;
            try {
              const blob = await fetch(croppedDataUrl).then(r => r.blob());
              const file = new File([blob], `cropped-${Date.now()}.png`, { type: "image/png" });
              const res = await ecomImageApi.uploadProductImage(file);
              const uploadedUrl = getUploadedImageUrl(res.data);
              if (uploadedUrl) {
                const updated = [...valueRef.current];
                updated[idx] = uploadedUrl;
                onChange(updated);
                const previewUrl = getUploadedPreviewUrl(res.data);
                setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, previewUrl));
                setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
              } else {
                feedback.error({ code: "E5003", fallback: "upload" });
              }
            } catch {
              feedback.error({ code: "E5003", fallback: "upload" });
            }
          }}
        />
      )}
    </>
  );
}

function SelectField({ comp, value, onChange }: { comp: ComponentConfig; value: string; onChange: (v: string) => void }) {
  const options = (comp.config?.options as Array<{ label: string; value: string }>) || [];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label;

  // Set default value on mount if not already set
  useEffect(() => {
    if (!value && options.length > 0) {
      onChange(options[0].value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <FieldHeader label={comp.label} required={comp.is_required} helpTip={getComponentHelpTip(comp)} />
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full h-9 bg-white/10 rounded-md px-3 text-sm text-left
                   outline-hidden focus:bg-white/15 transition-all
                   border border-white/15 cursor-pointer flex items-center justify-between"
      >
        <span className={selectedLabel ? "text-[#f6f6f6]" : "text-white/40"}>{selectedLabel || "请选择"}</span>
        <svg className={`w-3 h-3 transition-transform ${selectedLabel ? "text-[#f6f6f6]" : "text-white/50"} ${open ? "rotate-180" : ""}`} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#1A1A1A] border border-white/15 rounded-md py-1 shadow-xl max-h-48 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-white/30">暂无选项</div>
          ) : (
            options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`px-3 py-2 text-sm cursor-pointer transition-colors
                  ${opt.value === value ? "bg-[#343434] text-[#F6F6F6]" : "text-white/80 hover:bg-white/10"}`}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TagSelectField({ comp, value, onChange }: { comp: ComponentConfig; value: string; onChange: (v: string) => void }) {
  const options = (comp.config?.options as Array<{ label: string; value: string }>) || [];

  useEffect(() => {
    if (!value && options.length > 0) {
      onChange(options[0].value);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <FieldHeader
        label={comp.label}
        required={comp.is_required}
        helpTip={getComponentHelpTip(comp)}
        meta={<span className="ml-1.5 text-[10px] text-white/30">可选</span>}
      />
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isActive = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`
                px-4 py-1.5 rounded-md text-xs font-medium transition-all
                ${isActive
                  ? "border border-transparent bg-[url('/button_bg2.png')] bg-[length:100%_100%] bg-center bg-no-repeat text-[#00FAE5] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                  : "bg-white/8 text-white/70 border border-white/15 hover:bg-white/12 hover:text-white/90 hover:border-white/25"
                }
              `}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ====== Template Multi-Select Field ======

interface TemplateMultiSelectOption {
  id: number;
  label: string;
  description?: string;
  inspire_template_id?: number;
  default_target_workflow?: string;
  default_target_workflows?: string[];
  cost?: number;
}

function TemplateMultiSelectField({
  comp,
  value,
  onChange,
}: {
  comp: ComponentConfig;
  value: number[];
  onChange: (v: number[]) => void;
}) {
  const feedback = useEcomFeedback();
  const templates: TemplateMultiSelectOption[] = (comp.config?.templates as TemplateMultiSelectOption[]) || [];
  const maxSelect = (comp.config?.max_select as number) || 5;
  const minSelect = (comp.config?.min_select as number) || 1;

  // Initialize with first template selected if empty
  useEffect(() => {
    if (value.length === 0 && templates.length > 0) {
      onChange([templates[0].id]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTemplate = (id: number) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      if (value.length >= maxSelect) {
        feedback.warning(`最多选择 ${maxSelect} 个模板`);
        return;
      }
      onChange([...value, id]);
    }
  };

  return (
    <div>
      <FieldHeader
        label={comp.label}
        required={comp.is_required}
        helpTip={getComponentHelpTip(comp)}
        meta={(
          <span className="ml-1.5 text-[10px] text-white/30">
            {minSelect === maxSelect ? `选择 ${minSelect} 个` : `选 ${minSelect}~${maxSelect} 个`}
          </span>
        )}
      />
      <div className="grid grid-cols-2 gap-2">
        {templates.map((tpl) => {
          const isActive = value.includes(tpl.id);
          return (
            <button
              key={tpl.id}
              type="button"
              onClick={() => toggleTemplate(tpl.id)}
              className={`
                relative h-[46px] overflow-hidden rounded-lg border border-[#27282A] bg-[#0D0E11] px-3.5 text-left
                flex flex-col justify-center outline-none
              `}
              title={tpl.description}
            >
              <span
                className={`pointer-events-none absolute inset-0 bg-[url('/button_bg5.png')] bg-[length:100%_100%] bg-center bg-no-repeat ${
                  isActive ? "opacity-100" : "opacity-0"
                }`}
                aria-hidden="true"
              />
              <span className={`relative z-10 block text-xs font-medium ${isActive ? "text-[#00FAE5]" : "text-white/80"}`}>
                {tpl.label}
              </span>
              {typeof tpl.cost === "number" && (
                <span className={`relative z-10 block mt-0.5 text-[10px] ${isActive ? "text-white/60" : "text-white/40"}`}>
                  {tpl.cost} 豌豆/次
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

type PresetPromptOption = {
  label: string;
  value: string;
  prompt: string;
  // 新增：当该预设属于灵感反推工作流的「分析预设」按钮时，绑定一个 LLM 提示词模板 ID。
  // 后端 functional/execute 优先用 params.template_id 覆盖工作流默认模板。
  inspire_template_id?: number | string;
};

function PresetPromptField({
  comp,
  value,
  onChange,
  onPromptChange,
  onMetaChange,
}: {
  comp: ComponentConfig;
  value: string;
  onChange: (v: string) => void;
  onPromptChange: (prompt: string) => void;
  onMetaChange?: (meta: Record<string, unknown>) => void;
}) {
  const options = (comp.config?.preset_prompt_options as PresetPromptOption[]) || [];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((o) => o.value === value);
  const selectedLabel = selectedOption?.label;
  const optionsKey = options.map((o) => o.value).join("|");

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const emitMeta = (opt: PresetPromptOption) => {
    if (!onMetaChange) return;
    const meta: Record<string, unknown> = {};
    if (opt.inspire_template_id != null && opt.inspire_template_id !== "") {
      const idNum = typeof opt.inspire_template_id === "string"
        ? parseInt(opt.inspire_template_id, 10)
        : opt.inspire_template_id;
      if (!Number.isNaN(idNum) && idNum > 0) {
        meta.template_id = idNum;
      }
    }
    meta.preset_label = opt.label;
    onMetaChange(meta);
  };

  useEffect(() => {
    if (options.length > 0 && !value) {
      const first = options[0];
      onChange(first.value);
      if (first.prompt) {
        onPromptChange(first.prompt);
      }
      emitMeta(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey]);

  const handleSelect = (opt: PresetPromptOption) => {
    onChange(opt.value);
    if (opt.prompt) {
      onPromptChange(opt.prompt);
    }
    emitMeta(opt);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <FieldHeader label={comp.label} required={comp.is_required} helpTip={getComponentHelpTip(comp)} />
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full h-9 bg-white/10 rounded-md px-3 text-sm text-left
                   outline-hidden focus:bg-white/15 transition-all
                   border border-white/15 cursor-pointer flex items-center justify-between"
      >
        <span className={selectedLabel ? "text-[#F6F6F6]" : "text-white/40"}>
          {selectedLabel || "请选择"}
        </span>
        <svg className={`w-3 h-3 transition-transform ${selectedLabel ? "text-[#F6F6F6]" : "text-white/50"} ${open ? "rotate-180" : ""}`} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#1A1A1A] border border-white/15 rounded-md py-1 shadow-xl max-h-48 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-white/30">暂无选项</div>
          ) : (
            options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt)}
                className={`px-3 py-2 text-sm cursor-pointer transition-colors
                  ${opt.value === value ? "bg-[#343434] text-[#F6F6F6]" : "text-white/80 hover:bg-white/10"}`}
              >
                <div>{opt.label}</div>
                {opt.prompt && (
                  <div className="text-[10px] text-white/30 mt-0.5 truncate">{opt.prompt}</div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function BatchPromptField({
  workflowKey,
  comp,
  value,
  batchCount,
  batchUnit = "批",
  onChange,
  reverseGenerating,
  reverseFailure,
  onReverseFailureDismiss,
}: {
  workflowKey: string;
  comp: ComponentConfig;
  value: string[];
  batchCount: number;
  batchUnit?: string;
  onChange: (v: string[]) => void;
  reverseGenerating?: boolean;
  reverseFailure?: ReversePromptFailureAction;
  onReverseFailureDismiss?: () => void;
}) {
  const placeholder = (comp.config?.placeholder as string) || "";
  const { showTemplateToolbar } = resolvePromptPresentation(
    comp.config?.prompt_template_enabled === true,
    comp.config?.prompt_large_frame === true,
  );
  const [activeBatchIdx, setActiveBatchIdx] = useState(0);
  const fieldRef = useRef<HTMLDivElement>(null);

  const ensuredValue = Array.from({ length: batchCount }, (_, i) => value[i] || "");
  const safeActiveBatchIdx = Math.min(activeBatchIdx, Math.max(ensuredValue.length - 1, 0));
  const activePrompt = ensuredValue[safeActiveBatchIdx] || "";
  const isSingleBatch = batchCount <= 1;

  const handleChange = (index: number, text: string) => {
    const next = [...ensuredValue];
    next[index] = text;
    onChange(next);
  };

  useEffect(() => {
    if (ensuredValue.length !== value.length || ensuredValue.some((v, i) => v !== (value[i] || ""))) {
      onChange(ensuredValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchCount]);

  useEffect(() => {
    if (activeBatchIdx !== safeActiveBatchIdx) {
      setActiveBatchIdx(safeActiveBatchIdx);
    }
  }, [activeBatchIdx, safeActiveBatchIdx]);

  return (
    <div ref={fieldRef}>
      <FieldHeader
        label={comp.label}
        required={comp.is_required}
        helpTip={getComponentHelpTip(comp)}
        meta={!isSingleBatch ? <span className="ml-1 text-white/30">({batchCount}{batchUnit})</span> : undefined}
      />
      <ReversePromptFailureBanner failure={reverseFailure} onDismiss={onReverseFailureDismiss} anchorRef={fieldRef} />
      <div>
        {!isSingleBatch && <div className="mb-3 flex flex-wrap gap-1.5">
          {ensuredValue.map((prompt, i) => {
            const isActive = i === safeActiveBatchIdx;
            const hasPrompt = prompt.trim().length > 0;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActiveBatchIdx(i)}
                className={`h-6 rounded-[8px] border text-[12px] px-2 transition-colors cursor-pointer
                  ${isActive
                    ? "border-transparent bg-[url('/button_bg2.png')] bg-[length:100%_100%] bg-center bg-no-repeat text-[#00FAE5] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                    : hasPrompt
                      ? "border-white/18 bg-transparent text-white/70 hover:border-white/28 hover:text-white"
                      : "border-white/12 bg-transparent text-white/42 hover:border-white/22 hover:text-white/70"
                  }`}
              >
                第{i + 1}{batchUnit}
              </button>
            );
          })}
        </div>}
        <div className="relative overflow-hidden rounded-[10px] border border-white/12 bg-[#191919]">
          <ConfiguredPromptTextarea
            data-ecom-prompt-input
            value={activePrompt}
            onChange={(e) => handleChange(safeActiveBatchIdx, e.target.value)}
            placeholder={placeholder}
            minHeight={285}
            maxHeight={320}
            disabled={reverseGenerating}
            className="block w-full bg-transparent px-4 py-3 text-sm leading-6 text-white placeholder:text-white/36 outline-hidden disabled:cursor-wait disabled:text-white/25 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-track]:bg-transparent"
          />
          {reverseGenerating && (
            <div className="pointer-events-none absolute inset-0 bg-[#191919] px-4 py-4 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white/80" />
                <span>提示词生成中...</span>
              </div>
            </div>
          )}
          {showTemplateToolbar && (
            <div className="flex h-11 items-center justify-end border-t border-white/10 bg-[#191919] px-3">
              <PromptTemplatePicker workflowKey={workflowKey} boundParamKey={comp.coze_param_key} disabled={!!reverseGenerating} onApply={(text) => handleChange(safeActiveBatchIdx, text)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BatchMultiImageArrayField({
  comp,
  value,
  batchCount,
  batchUnit = "批",
  onChange,
  notifyUploading,
  ratioBinding,
  headerAction,
  onHoverPreviewChange,
}: {
  comp: ComponentConfig;
  value: string[][];
  batchCount: number;
  batchUnit?: string;
  onChange: (v: string[][]) => void;
  notifyUploading?: (delta: number) => void;
  ratioBinding?: PerBatchRatioBinding;
  headerAction?: ReactNode;
  onHoverPreviewChange?: (preview: ImageHoverPreview | null) => void;
}) {
  const feedback = useEcomFeedback();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [hoverPreview, setHoverPreview] = useState<{ url: string; x: number; y: number } | null>(null);
  const [uploadStates, setUploadStates] = useState<Record<string, "uploading" | "error">>({});
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [editUrls, setEditUrls] = useState<Record<string, string>>({});
  const [localFallbackUrls, setLocalFallbackUrls] = useState<Record<string, string>>({});
  const [activeBatchIdx, setActiveBatchIdx] = useState(0);
  const [reorderDragIdx, setReorderDragIdx] = useState<number | null>(null);
  const [reorderOverIdx, setReorderOverIdx] = useState<number | null>(null);
  const [cropImageIndex, setCropImageIndex] = useState<number | null>(null);

  const MAX_PER_BATCH = (typeof comp.config?.max_images === "number" && comp.config.max_images > 0)
    ? comp.config.max_images : 5;

  const ensuredValue: string[][] = Array.from({ length: batchCount }, (_, i) => value[i] || []);
  const ensuredRef = useRef(ensuredValue);
  ensuredRef.current = ensuredValue;

  const currentBatchImages = ensuredValue[activeBatchIdx] || [];
  const currentBatchFull = currentBatchImages.filter(Boolean).length >= MAX_PER_BATCH;

  const updateBatchImages = useCallback((batchIdx: number, images: string[]) => {
    const next = [...ensuredRef.current];
    next[batchIdx] = images;
    onChange(next);
  }, [onChange]);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter((f) => isImageFile(f));
    if (imageFiles.length === 0) return;

    const batchIdx = activeBatchIdx;
    const currentImages = ensuredRef.current[batchIdx] || [];
    const remaining = MAX_PER_BATCH - currentImages.filter(Boolean).length;
    const toUpload = imageFiles.slice(0, remaining);
    if (toUpload.length === 0) return;

    const blobUrls = toUpload.map(f => URL.createObjectURL(f));
    updateBatchImages(batchIdx, [...currentImages, ...blobUrls]);

    const newStates: Record<string, "uploading" | "error"> = {};
    blobUrls.forEach(url => { newStates[url] = "uploading"; });
    setUploadStates(prev => ({ ...prev, ...newStates }));

    setUploading(true);
    notifyUploading?.(1);
    try {
      const compressed = await compressImagesForBatchUpload(toUpload, currentImages.length);
      await mapWithConcurrency(compressed, async (file, i) => {
        try {
          const res = await ecomImageApi.uploadProductImage(file, { skipCompression: true });
          const uploadedUrl = getUploadedImageUrl(res.data);
          if (uploadedUrl) {
            const previewUrl = getUploadedPreviewUrl(res.data);
            const curBatch = ensuredRef.current[batchIdx] || [];
            const updated = curBatch.map(url => url === blobUrls[i] ? uploadedUrl : url);
            updateBatchImages(batchIdx, updated);
            setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, previewUrl));
            setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
            setLocalFallbackUrls(prev => ({ ...prev, [uploadedUrl]: blobUrls[i] }));
            setUploadStates(prev => { const n = { ...prev }; delete n[blobUrls[i]]; return n; });
          } else {
            setUploadStates(prev => ({ ...prev, [blobUrls[i]]: "error" }));
          }
        } catch {
          setUploadStates(prev => ({ ...prev, [blobUrls[i]]: "error" }));
        }
      });
    } catch {
      blobUrls.forEach(url => { setUploadStates(prev => ({ ...prev, [url]: "error" })); });
    } finally {
      setUploading(false);
      notifyUploading?.(-1);
    }
  }, [activeBatchIdx, MAX_PER_BATCH, updateBatchImages, notifyUploading]);

  const handleLocalFile = useCallback((file: File) => {
    if (!isImageFile(file)) return;
    handleFiles([file]);
  }, [handleFiles]);

  const handleLocalFiles = useCallback((files: FileList) => {
    handleFiles(files);
  }, [handleFiles]);

  const handleInspirationSelect = useCallback((url: string) => {
    const batchIdx = activeBatchIdx;
    const curBatch = ensuredRef.current[batchIdx] || [];
    if (curBatch.filter(Boolean).length >= MAX_PER_BATCH) return;
    updateBatchImages(batchIdx, [...curBatch, url]);
  }, [activeBatchIdx, MAX_PER_BATCH, updateBatchImages]);

  const removeImage = useCallback((batchIdx: number, imgIdx: number) => {
    setHoverPreview(null);
    onHoverPreviewChange?.(null);
    const curBatch = ensuredRef.current[batchIdx] || [];
    const url = curBatch[imgIdx];
    revokeBlobUrl(url);
    revokeBlobUrl(localFallbackUrls[url]);
    setUploadStates(prev => { const n = { ...prev }; delete n[url]; return n; });
    setPreviewUrls(prev => { const n = { ...prev }; delete n[url]; return n; });
    setEditUrls(prev => { const n = { ...prev }; delete n[url]; return n; });
    setLocalFallbackUrls(prev => { const n = { ...prev }; delete n[url]; return n; });
    updateBatchImages(batchIdx, curBatch.filter((_, i) => i !== imgIdx));
  }, [localFallbackUrls, onHoverPreviewChange, updateBatchImages]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const draggedImage = getInternalImageDragData(e.dataTransfer);
    if (draggedImage) {
      const batchIdx = activeBatchIdx;
      const curBatch = ensuredRef.current[batchIdx] || [];
      if (curBatch.filter(Boolean).length < MAX_PER_BATCH) {
        updateBatchImages(batchIdx, [...curBatch, draggedImage.url]);
        setPreviewUrls(prev => withMappedUrl(prev, draggedImage.url, draggedImage.previewUrl));
        setEditUrls(prev => withMappedUrl(prev, draggedImage.url, draggedImage.editUrl));
        if (draggedImage.localFallbackUrl) {
          setLocalFallbackUrls(prev => ({ ...prev, [draggedImage.url]: draggedImage.localFallbackUrl || "" }));
        }
      }
      return;
    }
    if (e.dataTransfer.getData(REORDER_INDEX_MIME)) return;
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  }, [MAX_PER_BATCH, activeBatchIdx, handleFiles, updateBatchImages]);

  const handleReorderDragStart = useCallback((e: React.DragEvent, imgIdx: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(REORDER_INDEX_MIME, String(imgIdx));
    const url = ensuredRef.current[activeBatchIdx]?.[imgIdx] || "";
    setInternalImageDragData(e, {
      url,
      previewUrl: previewUrls[url],
      editUrl: editUrls[url],
      localFallbackUrl: localFallbackUrls[url],
    });
    setReorderDragIdx(imgIdx);
    setHoverPreview(null);
    onHoverPreviewChange?.(null);
  }, [activeBatchIdx, editUrls, localFallbackUrls, onHoverPreviewChange, previewUrls]);

  const handleReorderDragOver = useCallback((e: React.DragEvent, imgIdx: number) => {
    if (reorderDragIdx === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setReorderOverIdx(imgIdx);
  }, [reorderDragIdx]);

  const handleReorderDrop = useCallback((e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    const srcStr = e.dataTransfer.getData(REORDER_INDEX_MIME);
    if (!srcStr) return;
    const srcIdx = parseInt(srcStr, 10);
    if (isNaN(srcIdx) || srcIdx === targetIdx) {
      setReorderDragIdx(null);
      setReorderOverIdx(null);
      return;
    }
    const batchIdx = activeBatchIdx;
    const curBatch = [...(ensuredRef.current[batchIdx] || [])];
    const [moved] = curBatch.splice(srcIdx, 1);
    curBatch.splice(targetIdx, 0, moved);
    updateBatchImages(batchIdx, curBatch);
    setReorderDragIdx(null);
    setReorderOverIdx(null);
  }, [activeBatchIdx, updateBatchImages]);

  const handleReorderDragEnd = useCallback(() => {
    setReorderDragIdx(null);
    setReorderOverIdx(null);
  }, []);

  const handleThumbMouseEnter = useCallback((e: React.MouseEvent, url: string) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const previewUrl = resolveHoverPreviewUrl({
      url,
      previewUrl: previewUrls[url],
      editUrl: editUrls[url],
      localFallbackUrl: localFallbackUrls[url],
    });
    setHoverPreview({ url: previewUrl || url, x: rect.left + rect.width / 2, y: rect.top });
    if (previewUrl) {
      onHoverPreviewChange?.({ url: previewUrl, alt: `${comp.label} 第${activeBatchIdx + 1}${batchUnit}` });
    }
  }, [activeBatchIdx, batchUnit, comp.label, editUrls, localFallbackUrls, onHoverPreviewChange, previewUrls]);

  const handleThumbMouseLeave = useCallback(() => {
    setHoverPreview(null);
    onHoverPreviewChange?.(null);
  }, [onHoverPreviewChange]);

  useEffect(() => {
    if (ensuredValue.length !== value.length) {
      onChange(ensuredValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchCount]);

  // Clamp activeBatchIdx
  useEffect(() => {
    if (activeBatchIdx >= batchCount) setActiveBatchIdx(Math.max(0, batchCount - 1));
  }, [batchCount, activeBatchIdx]);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => {
        e.preventDefault();
        if (!hasReorderDragData(e.dataTransfer)) setDragOver(true);
      }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
    >
      <FieldHeader
        label={comp.label}
        required={comp.is_required}
        helpTip={getComponentHelpTip(comp)}
        meta={<span className="ml-1 text-white/30">({batchCount}{batchUnit})</span>}
        action={headerAction}
      />
      <div className="text-[10px] text-white/30 mb-2">
        上传图片后按{batchUnit}次绑定，{batchUnit}次数超过图片数时自动从第一张循环
      </div>

      {/* Batch tabs */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {ensuredValue.map((batchImgs, i) => {
          const count = batchImgs.filter(Boolean).length;
          const isActive = i === activeBatchIdx;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setActiveBatchIdx(i)}
              className={` h-6 rounded-[8px] border text-[12px] px-2 transition-colors cursor-pointer
                ${isActive
                  ? "border-transparent bg-[url('/button_bg2.png')] bg-[length:100%_100%] bg-center bg-no-repeat text-[#00FAE5] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                  : count > 0
                    ? "border-white/18 bg-transparent text-white/70 hover:border-white/28 hover:text-white"
                    : "border-white/12 bg-transparent text-white/42 hover:border-white/22 hover:text-white/70"
                }`}
            >
              第{i + 1}{batchUnit}
            </button>
          );
        })}
      </div>

      {/* Current batch images */}
      {currentBatchImages.filter(Boolean).length === 0 ? (
        <ImageSourceMenu
          onLocalFile={handleLocalFile}
          onLocalFiles={handleLocalFiles}
          onInspirationSelect={handleInspirationSelect}
          multiple
          disabled={uploading}
        >
          <EcomUploadEmptyState
            uploading={uploading}
            dragOver={dragOver}
            multiple
            className="w-full aspect-3/1"
          >
            <span className="text-[10px] text-white/25">每{batchUnit}最多 {MAX_PER_BATCH} 张</span>
          </EcomUploadEmptyState>
        </ImageSourceMenu>
      ) : (
        <div className={`grid grid-cols-3 gap-2 p-2 rounded-[10px] border border-dashed transition-all
                        ${dragOver ? "border-white/70 bg-white/10" : "border-transparent"}`}>
          {currentBatchImages.map((url, imgIdx) => {
            if (!url) return null;
            const state = uploadStates[url];
            const isItemUploading = state === "uploading";
            const isItemError = state === "error";
            return (
              <div
                key={`batch-${activeBatchIdx}-img-${imgIdx}-${url.slice(-12)}`}
                className="min-w-0 space-y-1.5"
              >
                <OrignUpload
                  status={isItemError ? "error" : isItemUploading ? "uploading" : "ready"}
                  draggable={!isItemUploading}
                  onDragStart={(e) => handleReorderDragStart(e, imgIdx)}
                  onPasteFiles={handleFiles}
                  pasteDisabled={uploading || currentBatchFull}
                  onDragOver={(e) => handleReorderDragOver(e, imgIdx)}
                  onDrop={(e) => handleReorderDrop(e, imgIdx)}
                  onDragEnd={handleReorderDragEnd}
                  className={`relative aspect-square group cursor-grab active:cursor-grabbing
                             transition-all duration-150
                             ${reorderDragIdx === imgIdx ? "opacity-30 scale-95" : ""}
                             ${isItemError ? "border border-red-500 rounded-[4px]" : ""}
                             ${reorderOverIdx === imgIdx && reorderDragIdx !== null && reorderDragIdx !== imgIdx ? "ring-2 ring-white/80 scale-105" : ""}`}
                  onMouseEnter={(e) => handleThumbMouseEnter(e, url)}
                  onMouseLeave={handleThumbMouseLeave}
                >
                  <div data-orign-upload-media className="relative w-full h-full overflow-hidden bg-[#141414]" style={{ borderRadius: 2 }}>
                    <UploadedPreviewImage
                      src={url}
                      previewSrc={previewUrls[url]}
                      localFallbackSrc={localFallbackUrls[url]}
                      alt={`${comp.label} 第${activeBatchIdx + 1}${batchUnit} ${imgIdx + 1}`}
                      className="w-full h-full object-contain bg-[#141414] pointer-events-none select-none"
                    />
                    {isItemUploading && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
                      </div>
                    )}
                    {isItemError && (
                      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-10 gap-1">
                        <svg className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 8v4m0 4h.01" strokeLinecap="round" />
                        </svg>
                        <span className="text-[9px] text-red-300">上传失败</span>
                      </div>
                    )}
                    <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-md bg-transparent backdrop-blur-xs
                                   flex items-center justify-center pointer-events-none">
                      <span className="text-[10px] font-bold text-white">{imgIdx + 1}</span>
                    </div>
                    <div className="absolute bottom-0 inset-x-0 bg-black/70 backdrop-blur-xs py-0.5 text-center pointer-events-none">
                      <span className="text-[9px] font-medium text-white/90">第{activeBatchIdx + 1}{batchUnit}</span>
                    </div>
                  </div>
                  <div
                    data-orign-upload-actions
                    className="pointer-events-none absolute inset-0 z-20"
                  >
                    <EcomIconAction
                      data-orign-upload-remove
                      onClick={() => removeImage(activeBatchIdx, imgIdx)}
                      className="pointer-events-auto absolute -top-0.5 -right-0.5 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                      aria-label={`删除${comp.label} 第${activeBatchIdx + 1}${batchUnit} ${imgIdx + 1}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </EcomIconAction>
                    {!isItemUploading && !isItemError && (
                      <EcomIconAction
                        data-orign-upload-edit
                        onClick={(e) => { e.stopPropagation(); setCropImageIndex(imgIdx); }}
                        className="pointer-events-auto absolute -bottom-0 -right-0 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                        aria-label={`裁剪${comp.label} 第${activeBatchIdx + 1}${batchUnit} ${imgIdx + 1}`}
                        title="裁剪图片"
                      >
                        <CropIcon data-dynamic-batch-image-crop-icon />
                      </EcomIconAction>
                    )}
                  </div>
                </OrignUpload>
              </div>
            );
          })}
          {!currentBatchFull && (
            <ImageSourceMenu
              onLocalFile={handleLocalFile}
              onLocalFiles={handleLocalFiles}
              onInspirationSelect={handleInspirationSelect}
              multiple
              disabled={uploading}
            >
              <div className="aspect-square flex flex-col items-center justify-center gap-1
                             border border-dashed border-white/15 rounded-[10px]
                             hover:border-white/30 hover:bg-white/6 transition-all cursor-pointer">
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14m-7-7h14" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </ImageSourceMenu>
          )}
        </div>
      )}

      {ratioBinding && batchCount > 1 && (
        <div className="mt-2">
          <PerBatchRatioInlinePicker
            index={activeBatchIdx}
            ratioComp={ratioBinding.ratioComp}
            globalRatio={ratioBinding.globalRatio}
            globalCropSize={ratioBinding.globalCropSize}
            value={ratioBinding.values[activeBatchIdx]}
            onChange={(next) => ratioBinding.onChange(activeBatchIdx, next)}
            batchUnit={ratioBinding.batchUnit}
          />
        </div>
      )}

      {shouldRenderLocalUploadHoverPreview(!!hoverPreview, !!onHoverPreviewChange) && hoverPreview && (
        <AdaptiveImagePreview
          url={hoverPreview.url}
          alt="预览"
          anchorX={hoverPreview.x}
          anchorTop={hoverPreview.y}
        />
      )}

      {cropImageIndex !== null && currentBatchImages[cropImageIndex] && (
        <ImageCropDialog
          imageUrl={editUrls[currentBatchImages[cropImageIndex]] || localFallbackUrls[currentBatchImages[cropImageIndex]] || previewUrls[currentBatchImages[cropImageIndex]] || currentBatchImages[cropImageIndex]}
          onCancel={() => setCropImageIndex(null)}
          onSave={async (croppedDataUrl) => {
            const idx = cropImageIndex;
            setCropImageIndex(null);
            try {
              const blob = await fetch(croppedDataUrl).then(r => r.blob());
              const file = new File([blob], `cropped-${Date.now()}.png`, { type: "image/png" });
              const res = await ecomImageApi.uploadProductImage(file);
              const uploadedUrl = getUploadedImageUrl(res.data);
              if (uploadedUrl) {
                const curBatch = ensuredRef.current[activeBatchIdx] || [];
                const updated = [...curBatch];
                updated[idx] = uploadedUrl;
                updateBatchImages(activeBatchIdx, updated);
                const previewUrl = getUploadedPreviewUrl(res.data);
                setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, previewUrl));
                setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
              } else { feedback.error({ code: "E5003", fallback: "upload" }); }
            } catch { feedback.error({ code: "E5003", fallback: "upload" }); }
          }}
        />
      )}
    </div>
  );
}

function BatchMultiImageField({
  comp,
  value,
  batchCount,
  batchUnit = "批",
  onChange,
  notifyUploading,
  ratioBinding,
  headerAction,
  onHoverPreviewChange,
}: {
  comp: ComponentConfig;
  value: string[];
  batchCount: number;
  batchUnit?: string;
  onChange: (v: string[]) => void;
  notifyUploading?: (delta: number) => void;
  ratioBinding?: PerBatchRatioBinding;
  headerAction?: ReactNode;
  onHoverPreviewChange?: (preview: ImageHoverPreview | null) => void;
}) {
  const feedback = useEcomFeedback();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [hoverPreview, setHoverPreview] = useState<{ url: string; x: number; y: number } | null>(null);
  const [uploadStates, setUploadStates] = useState<Record<string, "uploading" | "error">>({});
  const [replacingSlot, setReplacingSlot] = useState<number | null>(null);
  const [reorderDragIdx, setReorderDragIdx] = useState<number | null>(null);
  const [reorderOverIdx, setReorderOverIdx] = useState<number | null>(null);
  const [cropImageIndex, setCropImageIndex] = useState<number | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [editUrls, setEditUrls] = useState<Record<string, string>>({});
  const [localFallbackUrls, setLocalFallbackUrls] = useState<Record<string, string>>({});

  const ensuredValue = Array.from({ length: batchCount }, (_, i) => value[i] || "");
  const filledCount = ensuredValue.filter(Boolean).length;
  const isEmpty = filledCount === 0;
  const isFull = filledCount >= batchCount;
  const isSingleBatch = batchCount <= 1;

  const ensuredRef = useRef(ensuredValue);
  ensuredRef.current = ensuredValue;

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter((f) => isImageFile(f));
    if (imageFiles.length === 0) return;

    const currentEnsured = ensuredRef.current;
    const emptySlots = currentEnsured
      .map((url, idx) => (!url ? idx : -1))
      .filter((idx) => idx >= 0);
    const toUpload = imageFiles.slice(0, emptySlots.length);
    if (toUpload.length === 0) return;

    const blobUrls = toUpload.map(f => URL.createObjectURL(f));
    const next = [...currentEnsured];
    for (let i = 0; i < blobUrls.length; i++) {
      next[emptySlots[i]] = blobUrls[i];
    }
    onChange(next);

    const newStates: Record<string, "uploading" | "error"> = {};
    blobUrls.forEach(url => { newStates[url] = "uploading"; });
    setUploadStates(prev => ({ ...prev, ...newStates }));

    setUploading(true);
    notifyUploading?.(1);
    try {
      const currentFilled = currentEnsured.filter(Boolean).length;
      const compressed = await compressImagesForBatchUpload(toUpload, currentFilled);
      await mapWithConcurrency(compressed, async (file, i) => {
        try {
          const res = await ecomImageApi.uploadProductImage(file, { skipCompression: true });
          const uploadedUrl = getUploadedImageUrl(res.data);
          if (uploadedUrl) {
            const previewUrl = getUploadedPreviewUrl(res.data);
            const updated = ensuredRef.current.map(url => url === blobUrls[i] ? uploadedUrl : url);
            onChange(updated);
            setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, previewUrl));
            setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
            setLocalFallbackUrls(prev => ({ ...prev, [uploadedUrl]: blobUrls[i] }));
            setUploadStates(prev => {
              const next2 = { ...prev };
              delete next2[blobUrls[i]];
              return next2;
            });
          } else {
            setUploadStates(prev => ({ ...prev, [blobUrls[i]]: "error" }));
          }
        } catch {
          setUploadStates(prev => ({ ...prev, [blobUrls[i]]: "error" }));
        }
      });
    } catch {
      blobUrls.forEach(url => {
        setUploadStates(prev => ({ ...prev, [url]: "error" }));
      });
    } finally {
      setUploading(false);
      notifyUploading?.(-1);
    }
  }, [onChange, notifyUploading]);

  const handleInspirationSelect = useCallback((url: string) => {
    const firstEmpty = ensuredRef.current.findIndex((v) => !v);
    if (firstEmpty < 0) return;
    const next = [...ensuredRef.current];
    next[firstEmpty] = url;
    onChange(next);
  }, [onChange]);

  const handleSlotFile = (i: number, file: File) => {
    if (!isImageFile(file)) return;
    const previousUrl = ensuredRef.current[i] || "";
    const blobUrl = URL.createObjectURL(file);
    const pendingUrl = previousUrl || blobUrl;
    if (!previousUrl) {
      const next = [...ensuredRef.current];
      next[i] = blobUrl;
      onChange(next);
    }
    if (previousUrl) setReplacingSlot(i);
    else setUploadStates(prev => ({ ...prev, [blobUrl]: "uploading" }));
    setUploading(true);
    notifyUploading?.(1);
    const existingImageCount = ensuredRef.current.filter(Boolean).length - Number(Boolean(previousUrl));
    void (async () => {
      try {
        const [compressed] = await compressImagesForBatchUpload([file], existingImageCount);
        const res = await ecomImageApi.uploadProductImage(compressed);
        const uploadedUrl = getUploadedImageUrl(res.data);
        if (!uploadedUrl) throw new Error("upload_failed");
        if (ensuredRef.current[i] === pendingUrl) {
          const updated = [...ensuredRef.current];
          updated[i] = uploadedUrl;
          onChange(updated);
          const previewUrl = getUploadedPreviewUrl(res.data);
          setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, previewUrl));
          setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
          setLocalFallbackUrls(prev => ({ ...prev, [uploadedUrl]: blobUrl }));
        } else {
          URL.revokeObjectURL(blobUrl);
        }
        if (!previousUrl) {
          setUploadStates(prev => { const next = { ...prev }; delete next[blobUrl]; return next; });
        }
      } catch {
        if (previousUrl || ensuredRef.current[i] !== blobUrl) {
          URL.revokeObjectURL(blobUrl);
          if (!previousUrl) {
            setUploadStates(prev => { const next = { ...prev }; delete next[blobUrl]; return next; });
          }
          if (previousUrl) feedback.warning("图片替换失败，请重试");
        } else {
          setUploadStates(prev => ({ ...prev, [blobUrl]: "error" }));
        }
      } finally {
        if (previousUrl) setReplacingSlot(null);
        setUploading(false);
        notifyUploading?.(-1);
      }
    })();
  };

  const handleLocalFile = useCallback((file: File) => {
    if (!isImageFile(file)) return;
    handleFiles([file]);
  }, [handleFiles]);

  const handleLocalFiles = useCallback((files: FileList) => {
    handleFiles(files);
  }, [handleFiles]);

  const removeImage = useCallback((index: number) => {
    setHoverPreview(null);
    onHoverPreviewChange?.(null);
    const url = ensuredRef.current[index];
    revokeBlobUrl(url);
    revokeBlobUrl(localFallbackUrls[url]);
    setUploadStates(prev => {
      const next2 = { ...prev };
      delete next2[url];
      return next2;
    });
    setPreviewUrls(prev => {
      const next2 = { ...prev };
      delete next2[url];
      return next2;
    });
    setEditUrls(prev => {
      const next2 = { ...prev };
      delete next2[url];
      return next2;
    });
    setLocalFallbackUrls(prev => {
      const next2 = { ...prev };
      delete next2[url];
      return next2;
    });
    const next = [...ensuredRef.current];
    next[index] = "";
    onChange(next);
  }, [localFallbackUrls, onChange, onHoverPreviewChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const draggedImage = getInternalImageDragData(e.dataTransfer);
    if (draggedImage) {
      const firstEmpty = ensuredRef.current.findIndex((v) => !v);
      if (firstEmpty >= 0) {
        const next = [...ensuredRef.current];
        next[firstEmpty] = draggedImage.url;
        onChange(next);
        setPreviewUrls(prev => withMappedUrl(prev, draggedImage.url, draggedImage.previewUrl));
        setEditUrls(prev => withMappedUrl(prev, draggedImage.url, draggedImage.editUrl));
        if (draggedImage.localFallbackUrl) {
          setLocalFallbackUrls(prev => ({ ...prev, [draggedImage.url]: draggedImage.localFallbackUrl || "" }));
        }
      }
      return;
    }
    if (e.dataTransfer.getData(REORDER_INDEX_MIME)) return;
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  }, [handleFiles, onChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!hasReorderDragData(e.dataTransfer)) setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleReorderDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(REORDER_INDEX_MIME, String(index));
    const url = ensuredRef.current[index] || "";
    setInternalImageDragData(e, {
      url,
      previewUrl: previewUrls[url],
      editUrl: editUrls[url],
      localFallbackUrl: localFallbackUrls[url],
    });
    setReorderDragIdx(index);
    setHoverPreview(null);
    onHoverPreviewChange?.(null);
  }, [editUrls, localFallbackUrls, onHoverPreviewChange, previewUrls]);

  const handleReorderDragOver = useCallback((e: React.DragEvent, index: number) => {
    if (reorderDragIdx === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setReorderOverIdx(index);
  }, [reorderDragIdx]);

  const handleReorderDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const srcStr = e.dataTransfer.getData(REORDER_INDEX_MIME);
    if (!srcStr) return;
    const srcIndex = parseInt(srcStr, 10);
    if (isNaN(srcIndex) || srcIndex === targetIndex) {
      setReorderDragIdx(null);
      setReorderOverIdx(null);
      return;
    }
    const next = [...ensuredRef.current];
    const temp = next[srcIndex];
    next[srcIndex] = next[targetIndex];
    next[targetIndex] = temp;
    onChange(next);
    setReorderDragIdx(null);
    setReorderOverIdx(null);
  }, [onChange]);

  const handleReorderDragEnd = useCallback(() => {
    setReorderDragIdx(null);
    setReorderOverIdx(null);
  }, []);

  const handleThumbMouseEnter = useCallback((e: React.MouseEvent, url: string) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const previewUrl = resolveHoverPreviewUrl({
      url,
      previewUrl: previewUrls[url],
      editUrl: editUrls[url],
      localFallbackUrl: localFallbackUrls[url],
    });
    setHoverPreview({ url: previewUrl || url, x: rect.left + rect.width / 2, y: rect.top });
    if (previewUrl) {
      onHoverPreviewChange?.({ url: previewUrl, alt: `${comp.label} ${ensuredRef.current.indexOf(url) + 1}` });
    }
  }, [comp.label, editUrls, localFallbackUrls, onHoverPreviewChange, previewUrls]);

  const handleThumbMouseLeave = useCallback(() => {
    setHoverPreview(null);
    onHoverPreviewChange?.(null);
  }, [onHoverPreviewChange]);

  useEffect(() => {
    if (ensuredValue.length !== value.length || ensuredValue.some((v, i) => v !== (value[i] || ""))) {
      onChange(ensuredValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchCount]);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <FieldHeader
        label={comp.label}
        required={comp.is_required}
        helpTip={getComponentHelpTip(comp)}
        meta={!isSingleBatch ? <span className="ml-1 text-white/30">({filledCount}/{batchCount}{batchUnit})</span> : undefined}
        action={headerAction}
      />
      {!isSingleBatch && <div className="text-[10px] text-white/30 mb-2">
        上传图片后按{batchUnit}次绑定，{batchUnit}次数超过图片数时自动从第一张循环
      </div>}

      {isEmpty ? (
        <ImageSourceMenu
          onLocalFile={handleLocalFile}
          onLocalFiles={handleLocalFiles}
          onInspirationSelect={handleInspirationSelect}
          multiple
          disabled={uploading}
        >
          <EcomUploadEmptyState
            uploading={uploading}
            dragOver={dragOver}
            multiple
            className="w-full aspect-3/1"
          />
        </ImageSourceMenu>
      ) : (
        <div className={`grid grid-cols-3 gap-2 p-2 rounded-[10px] border border-dashed transition-all
                        ${dragOver ? "border-white/70 bg-white/10" : "border-transparent"}`}>
          {ensuredValue.map((url, i) => {
            if (!url) {
              return (
                <ImageSourceMenu
                  key={`batch-empty-${i}`}
                  onLocalFile={(file) => handleSlotFile(i, file)}
                  onLocalFiles={(files) => {
                    const imageFiles = Array.from(files).filter(f => isImageFile(f));
                    if (imageFiles.length === 0) return;
                    handleFiles(imageFiles);
                  }}
                  onInspirationSelect={(selectedUrl) => {
                    const next = [...ensuredRef.current];
                    next[i] = selectedUrl;
                    onChange(next);
                  }}
                  disabled={uploading}
                >
                  <div
                    className="relative aspect-square flex flex-col items-center justify-center gap-1
                               border border-dashed border-white/15 rounded-[10px]
                               hover:border-white/30 hover:bg-white/6 transition-all cursor-pointer"
                  >
                    <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-md bg-transparent backdrop-blur-xs
                                   flex items-center justify-center pointer-events-none">
                      <span className="text-[10px] font-bold text-white">{i + 1}</span>
                    </div>
                    <svg className="w-5 h-5 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14m-7-7h14" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </ImageSourceMenu>
              );
            }
            const state = replacingSlot === i ? "uploading" : uploadStates[url];
            const isItemUploading = state === "uploading";
            const isItemError = state === "error";
            return (
              <div
                key={`batch-${i}-${url.slice(-12)}`}
                className="min-w-0 space-y-1.5"
              >
                <OrignUpload
                  status={isItemError ? "error" : isItemUploading ? "uploading" : "ready"}
                  draggable={!isItemUploading}
                  onDragStart={(e) => handleReorderDragStart(e, i)}
                  onPasteFiles={files => { if (files[0]) handleSlotFile(i, files[0]); }}
                  pasteDisabled={uploading}
                  onDragOver={(e) => handleReorderDragOver(e, i)}
                  onDrop={(e) => handleReorderDrop(e, i)}
                  onDragEnd={handleReorderDragEnd}
                  className={`relative aspect-square group cursor-grab active:cursor-grabbing
                             transition-all duration-150
                             ${reorderDragIdx === i ? "opacity-30 scale-95" : ""}
                             ${isItemError ? "border border-red-500 rounded-[4px]" : ""}
                             ${reorderOverIdx === i && reorderDragIdx !== null && reorderDragIdx !== i ? "ring-2 ring-white/80 scale-105" : ""}`}
                  onMouseEnter={(e) => handleThumbMouseEnter(e, url)}
                  onMouseLeave={handleThumbMouseLeave}
                >
                  <div data-orign-upload-media className="relative w-full h-full overflow-hidden bg-[#141414]" style={{ borderRadius: 2 }}>
                    <UploadedPreviewImage
                      src={url}
                      previewSrc={previewUrls[url]}
                      localFallbackSrc={localFallbackUrls[url]}
                      alt={`${comp.label} ${i + 1}`}
                      className="w-full h-full object-contain bg-[#141414] pointer-events-none select-none"
                    />
                    {isItemUploading && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
                      </div>
                    )}
                    {isItemError && (
                      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-10 gap-1">
                        <svg className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 8v4m0 4h.01" strokeLinecap="round" />
                        </svg>
                        <span className="text-[9px] text-red-300">上传失败</span>
                      </div>
                    )}
                    {!isSingleBatch && <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-md bg-transparent backdrop-blur-xs
                                   flex items-center justify-center pointer-events-none">
                      <span className="text-[10px] font-bold text-white">{i + 1}</span>
                    </div>}
                    {!isSingleBatch && <div className="absolute bottom-0 inset-x-0 bg-black/70 backdrop-blur-xs py-0.5 text-center pointer-events-none">
                      <span className="text-[9px] font-medium text-white/90">第{i + 1}{batchUnit}</span>
                    </div>}
                  </div>
                  <div
                    data-orign-upload-actions
                    className="pointer-events-none absolute inset-0 z-20"
                  >
                    <EcomIconAction
                      data-orign-upload-remove
                      onClick={() => removeImage(i)}
                      className="pointer-events-auto absolute -top-0.5 -right-0.5 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                      aria-label={`删除${comp.label} ${i + 1}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </EcomIconAction>
                    {!isItemUploading && !isItemError && (
                      <EcomIconAction
                        data-orign-upload-edit
                        onClick={(e) => { e.stopPropagation(); setCropImageIndex(i); }}
                        className="pointer-events-auto absolute -bottom-0 -right-0 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                        aria-label={`裁剪${comp.label} ${i + 1}`}
                        title="裁剪图片"
                      >
                        <CropIcon data-dynamic-batch-image-crop-icon />
                      </EcomIconAction>
                    )}
                  </div>
                </OrignUpload>
                {ratioBinding && batchCount > 1 && (
                  <PerBatchRatioInlinePicker
                    index={i}
                    ratioComp={ratioBinding.ratioComp}
                    globalRatio={ratioBinding.globalRatio}
                    globalCropSize={ratioBinding.globalCropSize}
                    value={ratioBinding.values[i]}
                    onChange={(next) => ratioBinding.onChange(i, next)}
                    batchUnit={ratioBinding.batchUnit}
                  />
                )}
              </div>
            );
          })}
          {!isFull && (
            <ImageSourceMenu
              onLocalFile={handleLocalFile}
              onLocalFiles={handleLocalFiles}
              onInspirationSelect={handleInspirationSelect}
              multiple
              disabled={uploading}
            >
              <div
                className="aspect-square flex flex-col items-center justify-center gap-1
                           border border-dashed border-white/15 rounded-[10px]
                           hover:border-white/30 hover:bg-white/6 transition-all cursor-pointer"
              >
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14m-7-7h14" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </ImageSourceMenu>
          )}
        </div>
      )}

      {shouldRenderLocalUploadHoverPreview(!!hoverPreview, !!onHoverPreviewChange) && hoverPreview && (
        <AdaptiveImagePreview
          url={hoverPreview.url}
          alt="预览"
          anchorX={hoverPreview.x}
          anchorTop={hoverPreview.y}
        />
      )}

      {cropImageIndex !== null && ensuredValue[cropImageIndex] && (
        <ImageCropDialog
          imageUrl={editUrls[ensuredValue[cropImageIndex]] || localFallbackUrls[ensuredValue[cropImageIndex]] || previewUrls[ensuredValue[cropImageIndex]] || ensuredValue[cropImageIndex]}
          onCancel={() => setCropImageIndex(null)}
          onSave={async (croppedDataUrl) => {
            const idx = cropImageIndex;
            setCropImageIndex(null);
            try {
              const blob = await fetch(croppedDataUrl).then(r => r.blob());
              const file = new File([blob], `cropped-${Date.now()}.png`, { type: "image/png" });
              const res = await ecomImageApi.uploadProductImage(file);
              const uploadedUrl = getUploadedImageUrl(res.data);
              if (uploadedUrl) {
                const updated = [...ensuredRef.current];
                updated[idx] = uploadedUrl;
                onChange(updated);
                const previewUrl = getUploadedPreviewUrl(res.data);
                setPreviewUrls(prev => withMappedUrl(prev, uploadedUrl, previewUrl));
                setEditUrls(prev => withMappedUrl(prev, uploadedUrl, getUploadedEditUrl(res.data)));
              } else { feedback.error({ code: "E5003", fallback: "upload" }); }
            } catch { feedback.error({ code: "E5003", fallback: "upload" }); }
          }}
        />
      )}
    </div>
  );
}

export function ImageReversePromptButton({
  comp,
  onChange,
  workflowKey,
  callableKey,
  expandConfig,
  sourceKeys,
  params,
  uiComponents,
  prefillImageUrl,
  draftImage,
  onDraftImageChange,
  billingCosts,
  myCosts,
  isGenerating,
  onGeneratingChange,
  onFailure,
  onRegenerateActionChange,
  onCompleted,
  onSourceOrderChange,
  helpTipStorageKey,
  helpTipRedDotStorageKey,
  reversePromptOwnerScope,
}: {
  comp: ComponentConfig;
  onChange: (v: string) => void;
  workflowKey: string;
  callableKey?: string;
  expandConfig?: ExpandConfigItem;
  sourceKeys: string[];
  params?: Record<string, unknown>;
  uiComponents?: ComponentConfig[];
  prefillImageUrl?: string;
  draftImage?: EcomImageReversePromptDraftImage;
  onDraftImageChange?: (image: EcomImageReversePromptDraftImage | null) => void;
  billingCosts?: WorkflowCostItem[];
  myCosts?: MyCostsResult | null;
  isGenerating?: boolean;
  onGeneratingChange?: (generating: boolean) => void;
  onFailure?: (failure: ReversePromptFailureAction | null) => void;
  onRegenerateActionChange?: (action?: () => void) => void;
  onCompleted?: () => void;
  onSourceOrderChange?: (orderedImages: ReversePromptImageItem[]) => void;
  helpTipStorageKey?: string;
  helpTipRedDotStorageKey?: string;
  reversePromptOwnerScope?: string;
}) {
  const { run: runEcomPollingRequest } = useEcomPollingFeedback();
  const getFunctionalResult = useCallback(
    (taskId: string) => runEcomPollingRequest(
      `ecom-image:functional-result:${taskId}`,
      () => requestFunctionalResult(taskId),
    ),
    [runEcomPollingRequest],
  );
  const [modalOpen, setModalOpen] = useState(false);
  const appliedPrefillImageRef = useRef("");
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);
  const uiComponentsRef = useRef(uiComponents);
  useEffect(() => {
    uiComponentsRef.current = uiComponents;
  }, [uiComponents]);
  const pollingRef = useRef(false);
  const runReversePromptRef = useRef<(request: ReversePromptSubmitRequest) => void>(() => undefined);
  // 订阅同步会在每次 effect 重跑时重复注册回调；用 ref 复用同一函数实例，
  // 让消费方的 prev === action 守卫生效，避免 setState 渲染死循环。
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const regenerateActionRef = useRef<{ requestKey: string; action: () => void } | null>(null);
  const retryActionRef = useRef<{ requestKey: string; action: () => void } | null>(null);
  const reversePromptChannel = `ordinary:${workflowKey}:${comp.coze_param_key}`;

  const customTitle = (comp.config as Record<string, unknown>)?.title as string || comp.label || "反推";

  useEffect(() => {
    const normalized = normalizeImageUrlValue(prefillImageUrl);
    if (!normalized || normalized === appliedPrefillImageRef.current) return;
    appliedPrefillImageRef.current = normalized;
    onDraftImageChange?.({ url: normalized });
  }, [prefillImageUrl, onDraftImageChange]);

  const modalImages = useMemo(() => {
    const sourceImages = collectReversePromptImagesFromParams(params, sourceKeys, uiComponents);
    const draftUrl = normalizeImageUrlValue(draftImage?.url);
    if (
      draftUrl &&
      !draftUrl.startsWith("blob:") &&
      !sourceImages.some((image) => image.url === draftUrl)
    ) {
      sourceImages.push({
        id: `${comp.coze_param_key}-draft-${draftUrl}`,
        componentKey: comp.coze_param_key,
        url: draftUrl,
        previewUrl: normalizeImageUrlValue(draftImage?.previewUrl),
        editUrl: normalizeImageUrlValue(draftImage?.editUrl),
        label: customTitle,
      });
    }
    return sourceImages;
  }, [comp.coze_param_key, customTitle, draftImage?.editUrl, draftImage?.previewUrl, draftImage?.url, params, sourceKeys, uiComponents]);
  const hasAnyImage = modalImages.length > 0;
  const helpTip = getComponentHelpTip(comp);
  const helpTipFallbackTitle = cleanString(comp.label) || "反推提示词";
  const componentRedDot = useHelpTipRedDot("component", helpTip, helpTipRedDotStorageKey);

  const runReversePrompt = useCallback(async (request: ReversePromptSubmitRequest) => {
    const operation = claimReversePromptOperation(
      reversePromptOwnerScope,
      reversePromptChannel,
      createClientUUID(),
      { request },
    );
    if (!operation) return;
    pollingRef.current = true;
    onFailure?.(null);
    onGeneratingChange?.(true);
    if (request.mode === "multi") {
      onSourceOrderChange?.(request.sourceImages || []);
    }
    setModalOpen(false);

    try {
      const res = await executeFunctional({
        callable_key: callableKey || workflowKey,
        params: {
          __reverse_prompt_mode: request.mode,
          __reverse_prompt_images: request.images,
          ...(request.templateID ? { template_id: request.templateID } : {}),
        },
      });
      const taskId = res.data?.task_id;
      if (!taskId) throw new Error("E5302");
      if (!updateReversePromptOperation(operation, "polling", { request, taskId })) return;

      const startTime = Date.now();
      let consecutiveErrors = 0;
      while (pollingRef.current && isReversePromptOperationCurrent(operation)) {
        if (Date.now() - startTime > 240_000) {
          throw new Error("E5302");
        }
        try {
          const result = await getFunctionalResult(taskId);
          const data = result.data;
          if (data?.status === "completed") {
            const nextPrompt = data.inspire_prompt || "";
            if (!nextPrompt) throw new Error("E5302");
            if (!isReversePromptOperationCurrent(operation)) return;
            const sourceUrls = collectReversePromptImagesFromParams(
              paramsRef.current,
              sourceKeys,
              uiComponentsRef.current,
            ).map((image) => image.url);
            const resolvedPrompt = rewriteReversePromptImageReferences(
              normalizeReversePromptToPlainText(nextPrompt),
              request.sourceImages || [],
              sourceUrls,
            );
            if (!updateReversePromptOperation(operation, "completed", { request, taskId, prompt: resolvedPrompt })) return;
            onChange(resolvedPrompt);
            const regenerateRequest: ReversePromptSubmitRequest = {
              mode: request.mode,
              images: request.images.map((image) => ({ ...image })),
              sourceImages: request.sourceImages?.map((image) => ({ ...image })),
              ...(request.templateID ? { templateID: request.templateID } : {}),
            };
            const regenerateRequestKey = JSON.stringify(regenerateRequest);
            if (regenerateActionRef.current?.requestKey !== regenerateRequestKey) {
              regenerateActionRef.current = {
                requestKey: regenerateRequestKey,
                action: () => {
                  if (!pollingRef.current) {
                    onChangeRef.current("");
                    void runReversePromptRef.current(regenerateRequest);
                  }
                },
              };
            }
            onRegenerateActionChange?.(regenerateActionRef.current.action);
            onCompleted?.();
            onFailure?.(null);
            return;
          }
          if (data?.status === "failed" || data?.status === "cancelled") {
            throw data.failure || createEcomApiFailure(null, "functional_task_failed", "E5302");
          }
          // 只有仍在排队/运行的成功响应才算“本轮无异常”，清零瞬时错误计数；
          // 放在 failed 检查之前会把确定性失败也当成瞬时错误无限重抛。
          consecutiveErrors = 0;
        } catch (pollErr) {
          consecutiveErrors++;
          if (consecutiveErrors > 5) throw pollErr;
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    } catch {
      if (!isReversePromptOperationCurrent(operation)) return;
      const retryRequest: ReversePromptSubmitRequest = {
        mode: request.mode,
        images: request.images.map((image) => ({ ...image })),
        sourceImages: request.sourceImages?.map((image) => ({ ...image })),
        ...(request.templateID ? { templateID: request.templateID } : {}),
      };
      updateReversePromptOperation(operation, "failed", { request: retryRequest, errorCode: "E5302" });
      const retryRequestKey = JSON.stringify(retryRequest);
      if (retryActionRef.current?.requestKey !== retryRequestKey) {
        retryActionRef.current = {
          requestKey: retryRequestKey,
          action: () => runReversePromptRef.current(retryRequest),
        };
      }
      onFailure?.({ onRetry: retryActionRef.current.action });
    } finally {
      pollingRef.current = false;
      onGeneratingChange?.(false);
    }
  }, [callableKey, onChange, onCompleted, onFailure, onGeneratingChange, onRegenerateActionChange, onSourceOrderChange, reversePromptChannel, reversePromptOwnerScope, sourceKeys, workflowKey]);

  useEffect(() => {
    runReversePromptRef.current = runReversePrompt;
  }, [runReversePrompt]);

  useEffect(() => {
    type OrdinaryReverseSnapshotData = {
      request?: ReversePromptSubmitRequest;
      taskId?: string;
      prompt?: string;
      errorCode?: string;
    };
    const syncOperation = (incoming?: ReversePromptOperationSnapshot | null) => {
      const snapshot = incoming?.channel === reversePromptChannel
        ? incoming as ReversePromptOperationSnapshot<OrdinaryReverseSnapshotData>
        : getLatestReversePromptOperation<OrdinaryReverseSnapshotData>(reversePromptOwnerScope, reversePromptChannel);
      const request = snapshot?.data?.request;
      const generating = snapshot?.phase === "submitting" || snapshot?.phase === "polling";
      pollingRef.current = generating;
      onGeneratingChange?.(generating);
      if (snapshot?.phase === "failed" && request) {
        const requestKey = JSON.stringify(request);
        if (retryActionRef.current?.requestKey !== requestKey) {
          retryActionRef.current = {
            requestKey,
            action: () => runReversePromptRef.current(request),
          };
        }
        onFailure?.({ onRetry: retryActionRef.current.action });
      } else {
        onFailure?.(null);
      }
      if (snapshot?.phase === "completed" && request) {
        const requestKey = JSON.stringify(request);
        if (regenerateActionRef.current?.requestKey !== requestKey) {
          regenerateActionRef.current = {
            requestKey,
            action: () => {
              if (!pollingRef.current) {
                onChangeRef.current("");
                void runReversePromptRef.current(request);
              }
            },
          };
        }
        onRegenerateActionChange?.(regenerateActionRef.current.action);
      }
    };
    syncOperation();
    return subscribeReversePromptOperations(reversePromptOwnerScope, syncOperation);
  }, [onChange, onFailure, onGeneratingChange, onRegenerateActionChange, reversePromptChannel, reversePromptOwnerScope]);

  const reverseButton = (
    <span
      className="relative inline-flex max-w-full items-center gap-1"
      onClickCapture={componentRedDot.visible && componentRedDot.dismissTrigger === "click" ? componentRedDot.dismiss : undefined}
      onPointerEnter={componentRedDot.visible && componentRedDot.dismissTrigger === "hover" ? componentRedDot.dismiss : undefined}
    >
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        disabled={!hasAnyImage || isGenerating}
        aria-label={isGenerating ? "提示词生成中" : "反推提示词"}
        className={`group relative inline-flex h-[26px] w-[118px] max-w-full cursor-pointer items-center justify-center gap-1 overflow-hidden rounded-[34px] border px-2 text-[13px] font-medium transition-all disabled:cursor-not-allowed disabled:hover:brightness-100 ${
          hasAnyImage
            ? isGenerating
              ? "cursor-not-allowed border-white/15 bg-[url('/button_bg8.png')] bg-cover bg-center text-white/50"
              : "border-white/15 bg-[url('/button_bg8.png')] bg-cover bg-center text-white hover:border-white/25 hover:brightness-125"
            : "cursor-not-allowed border-white/8 bg-[url('/button_bg7.png')] bg-cover bg-center text-white/30"
        }`}
      >
        {hasAnyImage && !isGenerating && (
          <span className="absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 ease-in-out group-hover:translate-x-full" />
        )}
        {isGenerating ? (
          <Loader2 className="relative z-10 h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
        ) : (
          <ReversePromptAiIcon className="relative z-10 h-3.5 w-3.5 shrink-0" />
        )}
        <span className="relative z-10 whitespace-nowrap text-[12px]">{isGenerating ? "提示词生成中..." : "反推提示词"}</span>
      </button>
      <HelpTipTrigger
        helpTip={helpTip?.display_mode === "icon" ? helpTip : null}
        fallbackTitle={helpTipFallbackTitle}
        redDotStorageKey={helpTipRedDotStorageKey}
      />
      {componentRedDot.visible && <HelpTipRedDot />}
    </span>
  );

  return (
    <>
      {helpTip?.display_mode === "component_hover" ? (
        <HelpTipHoverArea helpTip={helpTip} fallbackTitle={helpTipFallbackTitle} storageKey={helpTipStorageKey}>{reverseButton}</HelpTipHoverArea>
      ) : reverseButton}
      <ImageReversePromptModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={customTitle}
        images={modalImages}
        workflowKey={workflowKey}
        config={comp.config}
        expandTemplateId={expandConfig?.template_id}
        billingCosts={billingCosts}
        myCosts={myCosts}
        onSubmit={runReversePrompt}
      />
    </>
  );
}

function collectReversePromptImagesFromParams(
  params: Record<string, unknown> | undefined,
  sourceKeys: string[],
  uiComponents?: ComponentConfig[],
): ReversePromptImageItem[] {
  if (!params || sourceKeys.length === 0) return [];
  const images: ReversePromptImageItem[] = [];
  const seen = new Set<string>();

  const pushImage = (componentKey: string, raw: unknown, label: string, path: string) => {
    const url = normalizeImageUrlValue(raw);
    if (!url || url.startsWith("blob:") || seen.has(url)) return;
    seen.add(url);
    images.push({
      id: `${componentKey}-${path}-${url}`,
      componentKey,
      url,
      label,
    });
  };

  sourceKeys.forEach((key) => {
    const val = params[key];
    const foundComp = uiComponents?.find((c) => c.coze_param_key === key);
    const label = foundComp?.label || key;

    if (Array.isArray(val)) {
      val.forEach((item, index) => {
        if (Array.isArray(item)) {
          item.forEach((nested, nestedIndex) => pushImage(key, nested, label, `${index}-${nestedIndex}`));
        } else {
          pushImage(key, item, label, String(index));
        }
      });
      return;
    }

    pushImage(key, val, label, "0");
  });

  return images;
}

function ImageReversePromptModal({
  open,
  onClose,
  title,
  images,
  workflowKey,
  config,
  expandTemplateId,
  billingCosts,
  myCosts,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  images: Array<{ id: string; componentKey: string; url: string; previewUrl?: string; editUrl?: string; label?: string }>;
  workflowKey: string;
  config?: Record<string, unknown>;
  expandTemplateId?: number;
  billingCosts?: WorkflowCostItem[];
  myCosts?: MyCostsResult | null;
  onSubmit: (request: ReversePromptSubmitRequest) => void;
}) {
  const [roles, setRoles] = useState<Record<string, ReverseImageRole>>({});
  const [selectionError, setSelectionError] = useState("");
  const [isMultiMode, setIsMultiMode] = useState(false);
  const [selectedSingleImageId, setSelectedSingleImageId] = useState("");
  const [bulkSelectedImageIds, setBulkSelectedImageIds] = useState<string[]>([]);
  const modalStateRef = useRef({ open: false, imageSignature: "" });
  const imageSignature = useMemo(
    () => images.map((image) => `${image.id}:${image.url}`).join("|"),
    [images],
  );
  const configuredMode = cleanString(config?.mode);

  useEffect(() => {
    const previous = modalStateRef.current;
    const shouldReset = open && (!previous.open || previous.imageSignature !== imageSignature);
    modalStateRef.current = { open, imageSignature: open ? imageSignature : "" };
    if (!shouldReset) return;
    setSelectionError("");
    setBulkSelectedImageIds([]);
    setIsMultiMode(configuredMode === "multi");
    setSelectedSingleImageId((prev) => images.some((image) => image.id === prev) ? prev : images[0]?.id || "");
    setRoles((prev) => {
      const next: Record<string, ReverseImageRole> = {};
      images.forEach((image, index) => {
        next[image.id] = prev[image.id] || (images.length === 1 && index === 0 ? "product" : "undefined");
      });
      return next;
    });
  }, [configuredMode, open, images, imageSignature]);

  useEffect(() => {
    if (!open) return;
    if (images.length < 2 && isMultiMode) {
      setIsMultiMode(false);
    }
    if (!selectedSingleImageId || !images.some((image) => image.id === selectedSingleImageId)) {
      setSelectedSingleImageId(images[0]?.id || "");
    }
  }, [images, isMultiMode, open, selectedSingleImageId]);

  useEffect(() => {
    if (!open || !isMultiMode) {
      setBulkSelectedImageIds([]);
      return;
    }
    setBulkSelectedImageIds((prev) => prev.filter((id) => images.some((image) => image.id === id)));
  }, [images, isMultiMode, open]);

  const effectiveMode = isMultiMode ? "multi" : "single";
  const templateID = resolveReversePromptTemplateID(config, effectiveMode, expandTemplateId);
  const costInfo = resolveReversePromptCost(workflowKey, config, effectiveMode, templateID, billingCosts, myCosts);
  const sortedImages = useMemo(() => sortReversePromptImagesByRole(images, roles), [images, roles]);
  // UI 弹窗显示顺序与提交给后端 LLM 的顺序保持一致：参考图在前，产品图次之，未定义最后。
  const displayedImages = sortedImages;
  const selectedImages = isMultiMode
    ? sortedImages.filter((image) => roles[image.id] === "product" || roles[image.id] === "reference")
    : images.filter((image) => image.id === selectedSingleImageId).slice(0, 1);
  const canGenerate = selectedImages.length > 0;
  const clearSelectionError = useCallback(() => {
    setSelectionError("");
  }, []);
  const selectedBulkCount = bulkSelectedImageIds.length;
  const hasBulkSelection = selectedBulkCount > 0;

  const toggleBulkImageSelection = useCallback((imageId: string) => {
    setBulkSelectedImageIds((prev) => (
      prev.includes(imageId)
        ? prev.filter((id) => id !== imageId)
        : [...prev, imageId]
    ));
    clearSelectionError();
  }, [clearSelectionError]);

  const applyBulkRole = useCallback((role: ReverseImageRole) => {
    setRoles((prev) => applyReversePromptRoleToSelection(prev, bulkSelectedImageIds, role));
    clearSelectionError();
  }, [bulkSelectedImageIds, clearSelectionError]);

  const submitReverse = useCallback(() => {
    if (!canGenerate) {
      setSelectionError(isMultiMode ? "请先定义图片类型" : "请选择一张图片");
      return;
    }
    setSelectionError("");

    const payloadImages = selectedImages.map((image) => ({
      url: image.url,
      role: (isMultiMode ? roles[image.id] : "product") as "product" | "reference",
      label: image.label || image.componentKey,
    }));
    onSubmit({
      mode: effectiveMode,
      images: payloadImages,
      sourceImages: selectedImages,
      ...(templateID ? { templateID } : {}),
    });
  }, [canGenerate, effectiveMode, isMultiMode, onSubmit, roles, selectedImages, templateID]);

  const modalTitle = title.trim() === "反推" ? "反推提示词" : title;
  const multiToggleAvailable = images.length > 1;

  return (
    <EcomModal
      open={open}
      onClose={onClose}
      layer="modal"
      closeLabel="关闭反推提示词弹窗"
      width={REVERSE_PROMPT_MODAL_WIDTH}
      title={(
        <div className="flex items-start justify-between pr-11">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-[#F6F6F6]">{modalTitle}</h3>
            {multiToggleAvailable && (
              <div className="ml-1 flex items-center gap-1.5">
                <span className="text-[12px] text-[#8C8C8C]">多图反推</span>
                <span className="flex h-2.5 w-2.5 items-center justify-center rounded-full border border-[#8C8C8C] text-[8px] leading-none text-[#8C8C8C]">
                  ?
                </span>
                <EcomSwitch
                  checked={isMultiMode}
                  onCheckedChange={(checked) => {
                    setSelectionError("");
                    setIsMultiMode(checked);
                  }}
                  aria-label="切换多图反推"
                  variant="flowx"
                  size="flowx"
                />
              </div>
            )}
          </div>
        </div>
      )}
      classNames={{ panel: "border border-[#353537]" }}
      styles={{
        mask: { background: REVERSE_PROMPT_MODAL_OVERLAY_STYLE.background },
        body: { maxHeight: "58vh", overflowY: "auto" },
      }}
      footer={(
        <div className="flex items-end gap-7">
          <EcomButton onClick={onClose} variant="transparent" size="medium">
            取消
          </EcomButton>
          <div className="flex flex-col items-stretch gap-2">
            <div className="flex items-center justify-between gap-6 text-xs text-[#A0A0A0]">
              <span>预估消耗</span>
              <span className="flex items-baseline">
                {costInfo.hasDiscount && (
                  <span className="text-xs text-[#A0A0A0] line-through">{costInfo.originalCost}</span>
                )}
                <span className="ml-2 text-lg font-medium text-[#FCB453]">{costInfo.cost}</span>
                <span className="ml-1">豌豆</span>
              </span>
            </div>
            <EcomButton
              onClick={submitReverse}
              disabled={!canGenerate}
              variant="default"
              size="medium"
            >
              生成提示词
            </EcomButton>
          </div>
        </div>
      )}
    >
      <p className="text-[13px] text-[#A0A0A0]">
        {isMultiMode ? "请定义每张图片的属性" : "请选择一张图片进行反推"}
      </p>
      <div className="[scrollbar-width:thin]">
          {isMultiMode && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-[10px] border border-[#2A2A2C] bg-[#202020] px-3 py-2">
              <span className="mr-1 text-xs text-[#A0A0A0]">已选 {selectedBulkCount} 张</span>
              {REVERSE_IMAGE_BULK_ROLE_OPTIONS.map((option) => (
                <EcomButton
                  key={option.value}
                  type="button"
                  onClick={() => applyBulkRole(option.value)}
                  disabled={!hasBulkSelection}
                  variant="outline"
                  size="small"
                >
                  设为{option.label}
                </EcomButton>
              ))}
              <EcomButton
                type="button"
                onClick={() => setBulkSelectedImageIds([])}
                disabled={!hasBulkSelection}
                variant="ghost"
                size="small"
                className="ml-auto"
              >
                清空选择
              </EcomButton>
            </div>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-5">
            {displayedImages.map((image) => {
              const role = roles[image.id] || "undefined";
              const bulkSelected = isMultiMode && bulkSelectedImageIds.includes(image.id);
              const roleDefined = role !== "undefined";
              const selected = isMultiMode ? roleDefined : image.id === selectedSingleImageId;
              return (
                <div
                  key={image.id}
                  className="transition-all"
                  style={{ width: REVERSE_PROMPT_MODAL_IMAGE_SIZE }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    aria-pressed={isMultiMode ? bulkSelected : image.id === selectedSingleImageId}
                    aria-label={`${isMultiMode ? "选择" : "使用"}${image.label || "反推图片"}`}
                    onClick={() => {
                      if (isMultiMode) {
                        toggleBulkImageSelection(image.id);
                        return;
                      }
                      setSelectedSingleImageId(image.id);
                      clearSelectionError();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (isMultiMode) {
                          toggleBulkImageSelection(image.id);
                          return;
                        }
                        setSelectedSingleImageId(image.id);
                        clearSelectionError();
                      }
                    }}
                    className="relative cursor-pointer rounded-[4px] p-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/35"
                    style={{
                      width: REVERSE_PROMPT_MODAL_IMAGE_SIZE,
                      height: REVERSE_PROMPT_MODAL_IMAGE_SIZE,
                      background: "#353537",
                      borderRadius: 4,
                      padding: selected ? 2 : 1,
                      boxShadow: bulkSelected ? "0 0 0 2px rgba(252, 180, 83, 0.9)" : undefined,
                    }}
                  >
                    <div className="h-full w-full overflow-hidden rounded-[2px] bg-black/45">
                      <UploadedPreviewImage
                        src={image.url}
                        previewSrc={image.previewUrl}
                        localFallbackSrc={image.editUrl}
                        alt={image.label || "反推图片"}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    {!selected && (
                      <div
                        className="pointer-events-none absolute inset-0 rounded-[4px]"
                        style={REVERSE_PROMPT_MODAL_OVERLAY_STYLE}
                      />
                    )}
                    {isMultiMode && (
                      <div
                        className={`absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors ${
                          bulkSelected
                            ? "border-[#FCB453] bg-[#FCB453] text-[#181818]"
                            : "border-white/45 bg-black/45 text-transparent"
                        }`}
                      >
                        ✓
                      </div>
                    )}
                  </div>
                  {isMultiMode && (
                    <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                      <ReverseImageRoleSelect
                        value={role}
                        onChange={(nextRole) => {
                          setRoles((prev) => ({ ...prev, [image.id]: nextRole }));
                          clearSelectionError();
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {selectionError && (
            <div className="mt-4 rounded-[8px] bg-[#D63D43] px-3 py-2 text-sm text-white">
              {selectionError}
            </div>
          )}
        </div>
    </EcomModal>
  );
}

function ReverseImageRoleSelect({
  value,
  onChange,
}: {
  value: ReverseImageRole;
  onChange: (value: ReverseImageRole) => void;
}) {
  return (
    <EcomSelect
      aria-label="选择图片类型"
      value={value}
      onChange={(nextValue) => onChange(nextValue as ReverseImageRole)}
      options={REVERSE_IMAGE_ROLE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
      overlayLayer="modalDropdown"
      size="large"
      className="mt-[7px]"
      style={{ width: REVERSE_PROMPT_MODAL_ROLE_WIDTH }}
    />
  );
}

function resolveReversePromptTemplateID(config: Record<string, unknown> | undefined, mode: string, fallback?: number): number | undefined {
  const raw = mode === "multi" ? config?.multi_template_id : config?.single_template_id;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return fallback && fallback > 0 ? fallback : undefined;
}

function resolveReversePromptCost(
  workflowKey: string,
  config: Record<string, unknown> | undefined,
  mode: string,
  templateID: number | undefined,
  billingCosts?: WorkflowCostItem[],
  myCosts?: MyCostsResult | null,
): { cost: number; originalCost: number; hasDiscount: boolean } {
  const configCost = Number(mode === "multi" ? config?.multi_cost : config?.single_cost);
  const standardCost = Number.isFinite(configCost) && configCost > 0
    ? configCost
    : resolveTemplateConfiguredCost(config, templateID) || billingCosts?.find((item) => item.workflow_key === workflowKey && item.enabled)?.fixed_cost || 0;
  const discountRate = myCosts?.tier?.discount_rate;
  const cost = typeof discountRate === "number" && standardCost > 0
    ? Math.ceil(standardCost * discountRate)
    : standardCost;
  return { cost, originalCost: standardCost, hasDiscount: cost < standardCost };
}

function resolveTemplateConfiguredCost(config: Record<string, unknown> | undefined, templateID?: number): number {
  if (!config || !templateID) return 0;
  const options = Array.isArray(config.templates) ? config.templates : [];
  for (const item of options) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    if (Number(record.id) === templateID || Number(record.inspire_template_id) === templateID) {
      const cost = Number(record.cost);
      return Number.isFinite(cost) && cost > 0 ? cost : 0;
    }
  }
  return 0;
}

function HiddenField({ comp, onChange }: { comp: ComponentConfig; onChange: (v: unknown) => void }) {
  const defaultValue = comp.config?.default_value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      onChangeRef.current(defaultValue);
    }
  }, [defaultValue]);

  return null;
}

type LogoPresetFieldValue = LogoPresetPayload;

function LogoPresetField({
  value,
  onChange,
  sourceImageUrl,
  outputCropSize,
  aspectRatioHint,
}: {
  value: LogoPresetFieldValue;
  onChange: (v: LogoPresetFieldValue) => void;
  sourceImageUrl?: string | null;
  outputCropSize?: string | null;
  aspectRatioHint?: string | null;
}) {
  return (
    <LogoPresetPanel
      moduleCode="ecom_image"
      value={value}
      onChange={onChange}
      sourceImageUrl={sourceImageUrl}
      outputCropSize={outputCropSize}
      aspectRatioHint={aspectRatioHint}
    />
  );
}

export function hasBatchSelectorInConfig(uiConfig: WorkflowUIConfig | null): boolean {
  if (!uiConfig) return false;
  return uiConfig.components.some(c => c.component_type === "batch_selector" && c.is_visible);
}

export function checkRequiredFieldsFilled(
  uiConfig: WorkflowUIConfig | null,
  params: Record<string, unknown>,
): boolean {
  if (!uiConfig) return true;

  const requiredVisible = uiConfig.components.filter(
    (c) => c.is_visible && (c.is_required || c.component_type === "logo_preset"),
  );

  for (const comp of requiredVisible) {
    const val = params[comp.coze_param_key];
    switch (comp.component_type) {
      case "prompt":
        if (typeof val !== "string" || !val.trim()) return false;
        break;
      case "model_gallery":
      case "background_gallery":
        if (val == null) return false;
        break;
      case "reference_image":
      case "image_upload":
        if (typeof val !== "string" || !val) return false;
        break;
      case "image_reverse_prompt": {
        const targetKey = typeof comp.config?.target_prompt_key === "string" && comp.config.target_prompt_key.trim()
          ? comp.config.target_prompt_key.trim()
          : comp.coze_param_key;
        const targetValue = params[targetKey];
        if (typeof targetValue !== "string" || !targetValue.trim()) return false;
        break;
      }
      case "multi_image":
        if (!Array.isArray(val) || val.length === 0) return false;
        if (!(val as string[]).some(v => typeof v === "string" && v && !v.startsWith("blob:"))) return false;
        break;
      case "batch_prompt":
        if (!Array.isArray(val) || val.length === 0 || val.some((v: unknown) => typeof v !== "string" || !(v as string).trim())) return false;
        break;
      case "select":
      case "tag_select":
      case "style_preset_selector":
      case "preset_prompt":
        if (typeof val !== "string" || !val) return false;
        break;
      case "logo_preset": {
        const lp = val as LogoPresetFieldValue | null | undefined;
        if (!lp || !Array.isArray(lp.items) || lp.items.length === 0) return false;
        break;
      }
      case "template_multi_select": {
        const minSelect = (comp.config?.min_select as number) || 1;
        if (!Array.isArray(val) || val.length < minSelect) return false;
        break;
      }
      case "coze_template_set": {
        const parsed = normalizeCozeTemplateSetValue(val);
        const validation = validateCozeTemplateSetValue(parsed);
        if (!validation.ok) return false;
        break;
      }
      // ratio_selector / resolution_selector / hidden always have defaults
    }
  }
  return true;
}

/** Returns true if any value in the params map contains a blob: URL (upload still in progress). */
export function hasPendingBlobUrls(params: Record<string, unknown>): boolean {
  const isBlob = (v: unknown): boolean => typeof v === "string" && v.startsWith("blob:");
  const visit = (val: unknown): boolean => {
    if (isBlob(val)) return true;
    if (Array.isArray(val)) return val.some(visit);
    if (val && typeof val === "object") return Object.values(val as Record<string, unknown>).some(visit);
    return false;
  };
  return Object.values(params).some(visit);
}

/** Strips blob: URLs from params, returning a cleaned copy. */
export function stripBlobUrls(params: Record<string, unknown>): Record<string, unknown> {
  const isBlob = (v: unknown): boolean => typeof v === "string" && v.startsWith("blob:");
  const stripValue = (val: unknown): unknown => {
    if (isBlob(val)) return undefined;
    if (Array.isArray(val)) {
      return val
        .map(stripValue)
        .filter((item) => item !== undefined);
    }
    if (val && typeof val === "object") {
      const next: Record<string, unknown> = {};
      for (const [childKey, childValue] of Object.entries(val as Record<string, unknown>)) {
        const stripped = stripValue(childValue);
        if (stripped !== undefined) next[childKey] = stripped;
      }
      return next;
    }
    return val;
  };
  const clean: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(params)) {
    const stripped = stripValue(val);
    if (stripped !== undefined) clean[key] = stripped;
  }
  return clean;
}
